import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, CheckCircle2, Sparkles, MessageSquare, X, AlertCircle, Loader2, Settings, Zap, User, ArrowRight, Trash2, BookText, Scroll, Info, GraduationCap, BookOpen as BookOpenIcon, Home, Lock, RefreshCw, Pencil, Paperclip } from 'lucide-react';
import { BookOpen, Skull, Heart, HelpCircle, Eye as EyeIcon, Lightbulb, Drama, Flame, Target, Brain, Tv } from 'lucide-react';
import { Coffee, Shirt, Dumbbell, Briefcase, MoreHorizontal, TrendingUp, Award, Users, Rocket, ShoppingBag, FileDown, PieChart, CalendarDays, Clapperboard, Store } from 'lucide-react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { generateStoryDiscoveryPrompt } from '../utils/storyDiscoveryPrompt';
import { generateAuthorityPromptShort, generateStoryPromptShort, generateTopicsPrompt } from '../utils/coursedeePromptsShort';
import { generateWithAI, generateImageAI } from '../services/openaiService';
import { useAuthContext } from '../contexts/AuthContext';

// true  = ใช้ prompt "ย่อ" ส่งเข้า ChatGPT ผ่าน ?q= → กดปุ่มเดียว ChatGPT เจนให้เลย
// false = กลับไปใช้ prompt "เต็ม" แบบเดิม (เปิด ChatGPT + คัดลอกให้ ผู้ใช้กด Ctrl+V เอง)
const USE_SHORT_CHATGPT = true;

// หมวดธุรกิจ (แตะเพื่อเติมช่อง "ขายอะไร/ทำอะไร" เร็ว ๆ) — Lucide icons เท่านั้น
const BUSINESS_CATEGORIES = [
  { label: 'ร้านอาหาร/คาเฟ่', icon: Coffee },
  { label: 'เสื้อผ้า/แฟชั่น', icon: Shirt },
  { label: 'ความงาม/สกินแคร์', icon: Sparkles },
  { label: 'สุขภาพ/ออกกำลังกาย', icon: Dumbbell },
  { label: 'คอร์ส/สอน', icon: GraduationCap },
  { label: 'ขายแฟรนไชส์', icon: Store },
  { label: 'บริการ', icon: Briefcase },
  { label: 'อื่นๆ', icon: MoreHorizontal },
];

// ประเภทความท้าทาย 60 วัน (แตะเลือกเพื่อเติมเทมเพลต แล้วผู้ใช้กรอกจำนวนเอง)
const CHALLENGE_PRESETS: { label: string; icon: any; tmpl: string }[] = [
  { label: 'เพิ่มผู้ติดตาม', icon: Users, tmpl: 'เพิ่มผู้ติดตามให้ได้ [ใส่จำนวน] คน ใน 60 วัน' },
  { label: 'สร้างยอดขาย', icon: TrendingUp, tmpl: 'ทำยอดขายให้ถึง [ใส่จำนวน] บาท ใน 60 วัน' },
  { label: 'หาลูกค้าใหม่', icon: Users, tmpl: 'หาลูกค้าใหม่ให้ได้ [ใส่จำนวน] คน ใน 60 วัน' },
  { label: 'ขายแฟรนไชส์', icon: Store, tmpl: 'ขายแฟรนไชส์ให้ได้ [ใส่จำนวน] สาขา ใน 60 วัน' },
  { label: 'ขยายสาขา', icon: Store, tmpl: 'ขยายสาขาให้ได้ [ใส่จำนวน] สาขา ใน 60 วัน' },
  { label: 'ปั้นแบรนด์ให้คนรู้จัก', icon: Sparkles, tmpl: 'ทำให้คนรู้จักแบรนด์ [ชื่อแบรนด์] ให้ได้มากที่สุด ใน 60 วัน' },
  { label: 'ยอดวิว/ไวรัล', icon: Flame, tmpl: 'ทำคลิปให้มียอดวิวรวม [ใส่จำนวน] วิว ใน 60 วัน' },
  { label: 'ลดน้ำหนัก', icon: Dumbbell, tmpl: 'ลดน้ำหนักให้ได้ [ใส่จำนวน] กิโล ใน 60 วัน' },
  { label: 'พัฒนาตัวเอง/อัปสกิล', icon: Brain, tmpl: 'พัฒนา [ทักษะที่อยากเก่ง เช่น พูดในที่สาธารณะ] ให้เก่งขึ้นใน 60 วัน' },
  { label: 'สร้างนิสัยใหม่', icon: Target, tmpl: 'สร้างนิสัย [เช่น ตื่นเช้า/ออกกำลังกาย/อ่านหนังสือ] ให้ได้ทุกวัน 60 วัน' },
];

// ตรวจเพศผู้พูดจาก "คำลงท้าย" ที่ผู้ใช้พิมพ์ในข้อมูลตัวตน (ครับ=ชาย · ค่ะ=หญิง) → ให้สคริปต์ลงท้ายตรงเพศ
// ✂️ สรุปภารกิจให้เหลือ "แก่น" (เป้าหมาย+กรอบเวลา) ก่อนส่งเข้า AI — ตัดท่อน "โดยบันทึก.../ตั้งแต่การ..."
// (รายการขั้นตอน) ทิ้ง เพราะ AI ชอบเอาไปท่องเป็นสารบัญในสคริปต์ไม่ว่าจะห้ามกี่กฎ — สิ่งที่ AI ไม่เห็น = ท่องไม่ได้
function challengeEssence(t?: string): string {
  return String(t || '').split(/\sโดย|ตั้งแต่การ/)[0].trim();
}

function detectThaiGender(...texts: (string | undefined | null)[]): 'male' | 'female' | null {
  const t = texts.filter(Boolean).join(' ');
  const male = (t.match(/ครับ|คร้าบ|ครัช|คับ(?=$|[\s.!?…,)】])/g) || []).length;
  const female = (t.match(/ค่ะ|นะคะ|จ้ะ|คะ(?=$|[\s.!?…,)】])/g) || []).length;
  if (male > female) return 'male';
  if (female > male) return 'female';
  return null;
}

// ===== แดชบอร์ดผลลัพธ์ (Progress Tracker) =====
type MetricKey = 'followers' | 'sales' | 'customers' | 'views' | 'clips';
// agg: วิธีสรุปค่าล่าสุด — 'last' = ตัวเลขสะสมล่าสุด · 'max' = ค่าสูงสุดในช่วง (ยอดวิว = เอาคลิปที่ปังสุด)
const PROGRESS_METRICS: { key: MetricKey; label: string; unit: string; color: string; agg: 'last' | 'max' }[] = [
  { key: 'followers', label: 'ผู้ติดตาม', unit: 'คน', color: '#8b6dff', agg: 'last' },
  { key: 'sales', label: 'ยอดขาย', unit: 'บาท', color: '#34d399', agg: 'last' },
  { key: 'customers', label: 'ลูกค้าใหม่/ทัก', unit: 'คน', color: '#f59e0b', agg: 'last' },
  { key: 'views', label: 'ยอดวิวสูงสุด', unit: 'วิว', color: '#ec4899', agg: 'max' },
  { key: 'clips', label: 'คลิปที่ลง', unit: 'คลิป', color: '#60a5fa', agg: 'last' },
];
type ProgressCheckin = { date: string; note?: string } & Partial<Record<MetricKey, number>>;
// ค่าปัจจุบันของตัวชี้วัด (max = สูงสุดในช่วง · last = ล่าสุด) และค่าเริ่มต้น
const metricCurrent = (m: { key: MetricKey; agg: 'last' | 'max' }, cks: ProgressCheckin[]): number | undefined => {
  const vals = cks.map((c) => c[m.key]).filter((v) => v !== undefined) as number[];
  if (!vals.length) return undefined;
  return m.agg === 'max' ? Math.max(...vals) : vals[vals.length - 1];
};
const metricStart = (m: { key: MetricKey }, cks: ProgressCheckin[]): number | undefined => {
  const vals = cks.map((c) => c[m.key]).filter((v) => v !== undefined) as number[];
  return vals.length ? vals[0] : undefined;
};
type ProgressTracker = { startDate: string; target: Partial<Record<MetricKey, number>>; checkins: ProgressCheckin[] };
const fmtNum = (n: number | undefined) => (n === undefined || n === null || isNaN(n) ? '—' : Math.round(n).toLocaleString('en-US'));
const quarterKey = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`; };
// ดึงเลขเป้าหมายจากข้อความความท้าทาย (เช่น "เพิ่มผู้ติดตาม 10,000 คน" → 10000)
const parseTargetNum = (text: string): number | undefined => {
  const m = (text || '').replace(/,/g, '').match(/(\d[\d.]*)\s*(ล้าน|แสน|หมื่น|พัน|k|K|m|M)?/);
  if (!m) return undefined;
  let n = parseFloat(m[1]);
  const u = m[2];
  if (u === 'ล้าน' || u === 'm' || u === 'M') n *= 1_000_000;
  else if (u === 'แสน') n *= 100_000;
  else if (u === 'หมื่น') n *= 10_000;
  else if (u === 'พัน' || u === 'k' || u === 'K') n *= 1_000;
  return isNaN(n) ? undefined : n;
};

// ตรวจว่ามีเวอร์ชันใหม่ถูก deploy หรือยัง (เทียบ signature ของ /index.html) → คืน true เมื่อมีอัปเดต (ปุ่มจะโผล่เฉพาะตอนนี้)
function useAppUpdate(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineRef = useRef<string | null>(null);
  useEffect(() => {
    let stopped = false;
    const sigOf = (html: string) => {
      const m = html.match(/\/assets\/[A-Za-z0-9_.-]+\.(?:js|css)/g);
      return m ? Array.from(new Set(m)).sort().join('|') : String(html.length);
    };
    const check = async () => {
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const sig = sigOf(await res.text());
        if (baselineRef.current === null) baselineRef.current = sig;          // เวอร์ชันที่รันอยู่ตอนนี้
        else if (sig !== baselineRef.current && !stopped) setUpdateAvailable(true); // เจอเวอร์ชันใหม่
      } catch { /* offline — ข้าม */ }
    };
    check();
    const iv = setInterval(check, 3 * 60 * 1000); // เช็คทุก 3 นาที
    const onFocus = () => check();                // เช็คตอนสลับกลับมาที่แท็บ
    window.addEventListener('focus', onFocus);
    return () => { stopped = true; clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, []);
  return updateAvailable;
}

// เป้าหมายบน TikTok (แตะเลือก 1-2 อัน) → ระบบแปลงเป็น "Main Story Arc" ให้เอง ลูกค้าไม่ต้องคิดคำว่า "พิสูจน์อะไร"
const CONTENT_GOALS: { key: string; label: string; icon: any; proof: string }[] = [
  { key: 'sell', label: 'ขายของให้ได้มากขึ้น', icon: TrendingUp, proof: 'อยากพิสูจน์ว่าธุรกิจนี้ขายดีขึ้นได้จริง และทำให้คนซื้อซ้ำ' },
  { key: 'brand', label: 'ทำให้คนจำแบรนด์ได้', icon: Sparkles, proof: 'อยากสร้างแบรนด์ให้เป็นที่จดจำและแตกต่างจากคู่แข่ง' },
  { key: 'expert', label: 'เป็นผู้เชี่ยวชาญที่คนเชื่อถือ', icon: Award, proof: 'อยากพิสูจน์ว่าเป็นตัวจริงที่รู้ลึกเรื่องนี้ จนคนเชื่อถือและอยากมาปรึกษา' },
  { key: 'viral', label: 'คอนเทนต์สนุก/ไวรัล', icon: Flame, proof: 'อยากทำคอนเทนต์สนุกที่คนดูจนจบและอยากแชร์ต่อ' },
  { key: 'community', label: 'หาลูกค้า/สร้างคอมมูนิตี้', icon: Users, proof: 'อยากหาลูกค้าใหม่และสร้างกลุ่มคนที่ติดตามเหนียวแน่น' },
];

// รวมเป้าหมายที่เลือก → ประโยค "ฉันกำลังพิสูจน์" สำหรับ prompt
const deriveProofFromGoals = (goals: string[]): string =>
  CONTENT_GOALS.filter((g) => goals.includes(g.key)).map((g) => g.proof).join(' และ ');

// เป้าหมายที่เลือก → "สไตล์การเขียนสคริปต์" ที่แทรกลงใน prompt (ให้เป้าหมายส่งผลจริง ไม่ใช่แค่ข้อความ)
const GOAL_STYLE: Record<string, string> = {
  viral: '🔥 สนุก/ไวรัล → ทำให้ "สนุก บันเทิง เกินคาด อยากแชร์ต่อ": เปิดด้วย hook แรงมากใน 2-3 วิแรก (คำถามค้างใจ/ภาพสะดุด/ประโยคช็อก) · ใส่มุก/พลิกความคาดหมาย/เล่นกับเทรนด์-มีม/ทดลองทำอะไรแปลกใหม่ที่คนอยากดูจนจบ · จังหวะเร็ว ตัดสั้น กระชับ · ปิดด้วยของที่คน "ต้องส่งให้เพื่อนดู/อยากคอมเมนต์" — ❌ ห้ามจืด/ทางการ/สอนแบบแห้ง ๆ น่าเบื่อ',
  brand: '✨ จำแบรนด์ → มีเอกลักษณ์/บุคลิกชัดเจน เล่าด้วยสไตล์เฉพาะตัวที่คนจำได้ ใช้ประโยค/มุกซิกเนเจอร์ให้ติดหู',
  expert: '🎓 น่าเชื่อถือ → โชว์ความลึก/ดีเทลที่มืออาชีพเท่านั้นรู้ พูดมั่นใจ มีเหตุผล/ตัวเลข/เคสจริงหนุน',
  community: '🤝 คอมมูนิตี้ → พูดแบบเป็นพวกเดียวกัน ชวนคุย/ชวนคอมเมนต์/ถามความเห็น ให้คนรู้สึกเป็นส่วนหนึ่ง',
  sell: '🛒 ขายได้ขึ้น → เน้นให้เห็นคุณค่า/ผลลัพธ์/ปัญหาที่แก้ได้ จนคนอยากได้เอง (เนียน ไม่ยัดเยียด)',
};

// 3 สายคอนเทนต์: Authority (สอน) / Story (เล่าเรื่องจริง) / Soft-sell (แนะนำสินค้าแบบไม่ขาย)
type ContentMode = 'authority' | 'story' | 'softsell';
const MODE_LABEL: Record<ContentMode, string> = { authority: 'Authority', story: 'Story', softsell: 'Soft-sell' };
const MODE_SUB: Record<ContentMode, string> = { authority: 'สายรู้จริง', story: 'เล่าเรื่อง', softsell: 'ขายแบบไม่ขาย' };

// แยกสคริปต์ AI เป็นเซกชัน (การ์ดสรุป / HOOK / สคริปต์เต็ม / แคปชั่น / แฮชแท็ก) → แสดงเป็นการ์ด
const CD_SECTION_ICONS = ['🃏', '🎬', '📝', '📱', '#️⃣'];
function cdParseScript(text: string): { title: string; body: string }[] {
  const out: { title: string; body: string[] }[] = [];
  let cur: { title: string; body: string[] } | null = null;
  for (const line of text.split('\n')) {
    const t = line.trim();
    if (CD_SECTION_ICONS.some((ic) => t.startsWith(ic))) {
      cur = { title: t, body: [] };
      out.push(cur);
    } else {
      if (!cur) { cur = { title: '', body: [] }; out.push(cur); }
      cur.body.push(line);
    }
  }
  return out.map((s) => ({ title: s.title, body: s.body.join('\n').replace(/^\n+|\n+$/g, '') })).filter((s) => s.title || s.body);
}

// ธีม (ดีไซน์ใหม่) — g1/g2 = สีแบรนด์ (ม่วง→ชมพู)
const CD_G1 = '#7c5cff';
const CD_G2 = '#c77dff';
const CD_THEME = {
  dark: {
    bg: 'radial-gradient(ellipse 900px 520px at 50% -6%, rgba(124,92,255,.16), transparent 68%), #0a0813',
    vars: { '--ink': '#f2f0fa', '--ink2': '#e6e2f2', '--sub': '#9a92b4', '--sub2': '#8a83a6', '--soft': '#c8c2dd', '--cv': '#c3b0ff', '--panel': 'rgba(255,255,255,.035)', '--panel2': 'rgba(255,255,255,.02)', '--field': 'rgba(255,255,255,.03)', '--sbg': 'rgba(255,255,255,.04)', '--bd': 'rgba(255,255,255,.09)', '--bd2': 'rgba(255,255,255,.12)', '--track2': 'rgba(255,255,255,.2)', '--modal': '#12101f', '--overlay': 'rgba(8,6,16,.74)', '--topbar': 'rgba(10,8,19,.72)' },
  },
  light: {
    bg: 'radial-gradient(ellipse 900px 520px at 50% -6%, rgba(124,92,255,.1), transparent 66%), #f4f2fa',
    vars: { '--ink': '#191428', '--ink2': '#2c2740', '--sub': '#6b6685', '--sub2': '#8a84a0', '--soft': '#4a4560', '--cv': '#6d4ee0', '--panel': '#ffffff', '--panel2': '#f7f5fb', '--field': '#f6f4fb', '--sbg': '#f0edf8', '--bd': 'rgba(20,16,40,.1)', '--bd2': 'rgba(20,16,40,.14)', '--track2': 'rgba(20,16,40,.22)', '--modal': '#ffffff', '--overlay': 'rgba(30,24,54,.34)', '--topbar': 'rgba(250,249,253,.82)' },
  },
} as const;

const ContentGenerator: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  
  useEffect(() => {
    document.title = 'สร้างคอนเท้นต์ - AIForYou V1';
  }, []);

  // User display name from Firebase Auth
  const currentUsername = user?.email?.split('@')[0] || user?.displayName || 'User';
  const isAdmin = user?.email === 'viitamint@gmail.com'; // ปุ่มลบชุดหัวข้อ เฉพาะแอดมิน

  // Creator Identity (loaded from localStorage)
  const [creatorIdentity, setCreatorIdentity] = useState<any>(null);
  const [identityText, setIdentityText] = useState('');

  // Identity Setup states
  const [currentStep, setCurrentStep] = useState(1); // Multi-step form: 1, 2, 3, 4
  const [whatIDo, setWhatIDo] = useState('');
  const [myAudience, setMyAudience] = useState(''); // กลุ่มเป้าหมาย — ให้คอนเทนต์ดึงคนกลุ่มนี้
  const [myExpertise, setMyExpertise] = useState(''); // ใช้เก็บ "จุดแข็งที่ไม่เหมือนใคร"
  const [myProof, setMyProof] = useState(''); // ฉันกำลังพิสูจน์อะไร (derive จากเป้าหมายที่เลือก)
  const [myGoals, setMyGoals] = useState<string[]>([]); // เป้าหมายที่ลูกค้าแตะเลือก (ตอบง่ายกว่าพิมพ์เอง)
  const [myChallenge, setMyChallenge] = useState(''); // ความท้าทาย 60 วัน (ไม่บังคับ) — ใช้เป็นแกนเรื่องซีรีส์
  const [challengeType, setChallengeType] = useState(''); // ประเภทความท้าทายที่แตะเลือก (แยกจากข้อความ — ลบ/แก้ข้อความแล้วตัวเลือกไม่หลุด)
  const [challengeMode, setChallengeMode] = useState(false); // โหมดลุยชาเลนจ์จริง — สคริปต์เป็นการลุยภารกิจให้คนติดตาม
  const [myProduct, setMyProduct] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Content Generator states
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // เจนคอนเทนต์ในเว็บ (Gemini)
  const [aiResult, setAiResult] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  // 2 แท็บ Authority/Story แยกกัน — แต่ละแท็บเก็บ "หลายชุด" หัวข้อ (ประวัติ ย้อนดูได้)
  type TopicScript = { topic: string; script: string };
  type TopicSet = { id: string; createdAt: string; topics: string[]; scripts: TopicScript[] };
  const [activeTab, setActiveTab] = useState<ContentMode>('authority');
  const [mainView, setMainView] = useState<'topics' | 'plan' | 'results' | 'chat'>('topics'); // มุมมองหลัก: เจนหัวข้อ / แผน 60 วัน / ผลลัพธ์ / แชท AI (แอดมิน)
  // 💬 แชท AI (แอดมิน) — คุยเล่นได้ + สั่งเจนภาพในแชทได้ (ภาพเด้งในบทสนทนา)
  type ChatMsg = { role: 'user' | 'ai'; text?: string; imageB64?: string; mime?: string; ts: number };
  const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatSending, setIsChatSending] = useState(false);
  const [chatAttach, setChatAttach] = useState<{ b64: string; mime: string } | null>(null); // 📎 รูปแนบ = ต้นแบบให้ AI เจนภาพ
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [progressTracker, setProgressTracker] = useState<ProgressTracker | null>(null); // แดชบอร์ดผลลัพธ์
  const [checkinDraft, setCheckinDraft] = useState<Record<string, string> | null>(null); // ฟอร์มเช็คอิน/ตั้งค่าเริ่มต้น (null = ปิด)
  const [chartMetric, setChartMetric] = useState<MetricKey>('followers'); // ตัวชี้วัดที่โชว์ในกราฟ
  const [showScriptModal, setShowScriptModal] = useState(false); // สคริปต์ที่เจน → เด้งเป็น popup
  const [openWeeks, setOpenWeeks] = useState<Record<number, boolean>>({ 1: true }); // แผน 60 วัน: สัปดาห์ที่เปิดอยู่ (พับได้)
  // 📁 Projects (แอดมินเท่านั้น) — แต่ละโปรเจกต์เก็บ ตัวตน+หัวข้อ+สคริปต์+ผลลัพธ์ แยกกัน (subcollection projects/{id})
  const [projects, setProjects] = useState<{ id: string; name: string; updatedAt?: string }[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  // 🖊️ คลิปเล่าเรื่องเอง — ผู้ใช้เล่าเหตุการณ์จริง → AI เจนสคริปต์ แทรกในแผนต่อจากวันล่าสุดที่เจนไว้
  type CustomClip = { id: string; title: string; story: string; script: string; afterDay: number; createdAt: string };
  const [customClips, setCustomClips] = useState<CustomClip[]>([]);
  const [showCustomClipBox, setShowCustomClipBox] = useState(false);
  const [customStoryDraft, setCustomStoryDraft] = useState('');
  const [isCustomGenerating, setIsCustomGenerating] = useState(false);
  const [customAttach, setCustomAttach] = useState<{ b64: string; mime: string }[]>([]); // รูปแนบ (สูงสุด 3) — ให้ AI ดูประกอบ ไม่เก็บถาวร
  const customFileRef = useRef<HTMLInputElement | null>(null);
  // 📁 เคสลูกค้า — เล่าโจทย์เคสจริง → AI แตกเป็นมินิซีรีส์ 4-6 คลิป แทรกในแผน (แบบบ้าน 1 หลัง = 5 คลิป)
  type CustomCase = { id: string; name: string; brief: string; afterDay: number; createdAt: string; clips: { title: string; outline: string; script: string }[] };
  const [customCases, setCustomCases] = useState<CustomCase[]>([]);
  const [showCaseBox, setShowCaseBox] = useState(false);
  const [caseBriefDraft, setCaseBriefDraft] = useState('');
  const [isCaseGenerating, setIsCaseGenerating] = useState(false);
  const [caseClipBusy, setCaseClipBusy] = useState(''); // `${caseId}_${idx}` ที่กำลังเจนสคริปต์
  // 🧑‍🎨 ตัวละครประจำโปรเจกต์ (แอดมิน) — รูปอ้างอิงให้ AI เจนภาพฉากด้วยหน้าเดิมทุกภาพ
  const [character, setCharacter] = useState<{ desc?: string; imageB64?: string; mime?: string } | null>(null);
  const [charDescDraft, setCharDescDraft] = useState('');
  const [isCharGenerating, setIsCharGenerating] = useState(false);
  const [showCharPanel, setShowCharPanel] = useState(false);
  // 🖼️ ภาพประกอบฉากของสคริปต์ (สำหรับเอาไปทำวิดีโอใน Google Flow)
  type SceneImage = { scene: string; imagePrompt: string; motionPrompt: string; imageB64?: string; mime?: string; status: 'pending' | 'generating' | 'done' | 'error' };
  const [sceneImages, setSceneImages] = useState<SceneImage[]>([]);
  const [isSceneGenRunning, setIsSceneGenRunning] = useState(false);
  const updateAvailable = useAppUpdate(); // true เมื่อมีเวอร์ชันใหม่ deploy → โชว์ปุ่มอัปเดต
  const [topicSetsByMode, setTopicSetsByMode] = useState<{ authority: TopicSet[]; story: TopicSet[]; softsell: TopicSet[] }>({ authority: [], story: [], softsell: [] });
  const [activeSetIdx, setActiveSetIdx] = useState<{ authority: number; story: number; softsell: number }>({ authority: 0, story: 0, softsell: 0 });
  // derived: ชุดที่กำลังเปิดอยู่ของแท็บนั้น
  const sets = topicSetsByMode[activeTab];
  const currentSet = sets[activeSetIdx[activeTab]] || null;
  const topicList = currentSet?.topics || [];
  const topicScripts = currentSet?.scripts || [];
  const [isTopicGenerating, setIsTopicGenerating] = useState(false);
  const [topicError, setTopicError] = useState('');

  // Identity Prompt Copy states
  const [copiedIdentityPrompt, setCopiedIdentityPrompt] = useState(false);
  const [isCopyingIdentityPrompt, setIsCopyingIdentityPrompt] = useState(false);
  
  // Story Discovery Prompt states
  const [storyPrompt, setStoryPrompt] = useState('');
  const [copiedStoryPrompt, setCopiedStoryPrompt] = useState(false);
  const [isCopyingStoryPrompt, setIsCopyingStoryPrompt] = useState(false);
  
  // Content Mode Selection
  const [selectedMode, setSelectedMode] = useState<ContentMode>('authority');

  // ธีมมืด/สว่าง (ดีไซน์ใหม่)
  const [theme, setTheme] = useState<'dark' | 'light'>(() => (localStorage.getItem('coursedee_v1_theme') as 'dark' | 'light') || 'dark');
  useEffect(() => { localStorage.setItem('coursedee_v1_theme', theme); }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  // ล็อกไม่ให้หน้าหลังเลื่อนตอนเปิด wizard modal (กัน scrollbar ซ้อน)
  useEffect(() => {
    if (currentStep >= 1) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [currentStep]);

  // Ref for auto-scroll to prompt result
  const promptResultRef = useRef<HTMLDivElement>(null);
  const scriptContentRef = useRef<HTMLDivElement>(null); // สำหรับ export PDF
  const [savingPdf, setSavingPdf] = useState(false);

  const contentFormats = [
    { id: 'shock', label: 'Shock', icon: Skull, color: 'from-red-500 to-orange-500', description: 'ทำให้ตกใจ + ให้ความรู้ที่ช็อก' },
    { id: 'pain', label: 'Pain', icon: Heart, color: 'from-pink-500 to-rose-500', description: 'แตะ pain point + ให้วิธีแก้' },
    { id: 'curiosity', label: 'Curiosity', icon: HelpCircle, color: 'from-purple-500 to-indigo-500', description: 'ทำให้อยากรู้ + เปิดเผยความจริง' },
    { id: 'reality', label: 'Reality', icon: EyeIcon, color: 'from-blue-500 to-cyan-500', description: 'เปิดความจริง + insight ที่ไม่เคยรู้' },
    { id: 'hope', label: 'Hope', icon: Lightbulb, color: 'from-yellow-500 to-amber-500', description: 'ให้ความหวัง + แผนที่ชัดเจน' },
    { id: 'pov', label: 'POV + Roleplay', icon: Drama, color: 'from-teal-500 to-cyan-500', description: 'สวมบทบาท + สอนผ่านสถานการณ์' },
    { id: 'trend', label: 'Trend', icon: Flame, color: 'from-orange-500 to-red-500', description: 'ขี่เทรนด์ + ใส่ความรู้' },
    { id: 'framework', label: 'Framework', icon: Target, color: 'from-green-500 to-emerald-500', description: 'สอนเฟรมเวิร์ก + checklist' },
    { id: 'insight', label: 'Insight', icon: Brain, color: 'from-violet-500 to-purple-500', description: 'แชร์ insight + วิธีคิดจากประสบการณ์' },
    { id: 'series', label: 'Series', icon: Tv, color: 'from-fuchsia-500 to-pink-500', description: 'ทำ mission + สอนระหว่างทาง' }
  ];

  // Load creator identity on mount
  useEffect(() => {
    if (currentUsername) {
      loadUserData(currentUsername);
    }
  }, [currentUsername]);

  // 📁 โหลดรายการโปรเจกต์ (แอดมิน) + migrate งานเดิมเป็น "โปรเจกต์ 1" ถ้ายังไม่มีโปรเจกต์
  useEffect(() => {
    if (!isAdmin || !currentUsername) return;
    let cancelled = false;
    (async () => {
      try {
        const colRef = collection(db, 'coursedee_v1_users', currentUsername, 'projects');
        const snap = await getDocs(colRef);
        let list = snap.docs.map((dd) => { const x = dd.data() as any; return { id: dd.id, name: x.name || 'โปรเจกต์', createdAt: x.createdAt || '', updatedAt: x.updatedAt || '' }; });
        const mainSnap = await getDoc(doc(db, 'coursedee_v1_users', currentUsername));
        const main: any = mainSnap.exists() ? mainSnap.data() : {};
        let activeId: string | null = main.activeProjectId || null;
        if (list.length === 0 && main.creatorIdentity) {
          const id = `p_${Date.now()}_1`;
          const now = new Date().toISOString();
          await setDoc(doc(db, 'coursedee_v1_users', currentUsername, 'projects', id), {
            name: 'โปรเจกต์ 1', createdAt: main.updatedAt || now, updatedAt: now,
            creatorIdentity: main.creatorIdentity, topicSetsByMode: main.topicSetsByMode || { authority: [], story: [], softsell: [] }, progressTracker: main.progressTracker || null,
          });
          await setDoc(doc(db, 'coursedee_v1_users', currentUsername), { activeProjectId: id }, { merge: true });
          list = [{ id, name: 'โปรเจกต์ 1', createdAt: now, updatedAt: now }];
          activeId = id;
        }
        if (cancelled) return;
        list.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
        setProjects(list.map((p) => ({ id: p.id, name: p.name, updatedAt: p.updatedAt })));
        setActiveProjectId(activeId);
      } catch (e) { console.error('[projects] load error', e); }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, currentUsername]);

  // mirror ข้อมูลไปยังโปรเจกต์ที่ active (แอดมิน) — เรียกหลังเซฟ main doc
  const mirrorToProject = (dataPart: any) => {
    if (!activeProjectId || !currentUsername) return;
    setDoc(doc(db, 'coursedee_v1_users', currentUsername, 'projects', activeProjectId), { ...dataPart, updatedAt: new Date().toISOString() }, { merge: true }).catch((e) => console.error('[project mirror]', e));
  };

  // ใส่ข้อมูลจาก project doc → working state
  const applyProjectData = (d: any) => {
    const parsed = d?.creatorIdentity || null;
    setCreatorIdentity(parsed);
    if (parsed) {
      setIdentityText(`ฉันทำ: ${parsed.whatIDo}\nความเชี่ยวชาญของฉัน: ${parsed.myExpertise || ''}\nฉันกำลังพิสูจน์: ${parsed.myProof}${parsed.myProduct ? `\nสินค้าของฉัน: ${parsed.myProduct}` : ''}`);
      setWhatIDo(parsed.whatIDo || ''); setMyExpertise(parsed.myExpertise || parsed.myCustomers || ''); setMyAudience(parsed.myAudience || ''); setMyProof(parsed.myProof || ''); setMyGoals(parsed.myGoals || []); setMyChallenge(parsed.myChallenge || ''); setChallengeType(parsed.challengeType || ''); setChallengeMode(!!parsed.challengeMode); setMyProduct(parsed.myProduct || '');
    } else {
      setWhatIDo(''); setMyAudience(''); setMyExpertise(''); setMyProof(''); setMyGoals([]); setMyChallenge(''); setChallengeType(''); setChallengeMode(false); setMyProduct(''); setIdentityText('');
    }
    const tsm = d?.topicSetsByMode || {};
    const a = Array.isArray(tsm.authority) ? tsm.authority : [];
    const s = Array.isArray(tsm.story) ? tsm.story : [];
    const ss = Array.isArray(tsm.softsell) ? tsm.softsell : [];
    setTopicSetsByMode({ authority: a, story: s, softsell: ss });
    setActiveSetIdx({ authority: Math.max(0, a.length - 1), story: Math.max(0, s.length - 1), softsell: Math.max(0, ss.length - 1) });
    setProgressTracker(d?.progressTracker && Array.isArray(d.progressTracker.checkins) ? d.progressTracker : null);
    setCustomClips(Array.isArray(d?.customClips) ? d.customClips : []);
    setCustomCases(Array.isArray(d?.customCases) ? d.customCases : []);
    setAiResult(''); setSelectedTopic('');
  };

  // ＋ โปรเจกต์ใหม่ (งานปัจจุบันถูก mirror เข้าโปรเจกต์เดิมไว้แล้ว จึงล้างได้เลย)
  const handleNewProject = async () => {
    if (!currentUsername) return;
    const now = new Date().toISOString();
    const id = `p_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const typed = window.prompt('ตั้งชื่อโปรเจกต์ใหม่', `โปรเจกต์ ${projects.length + 1}`);
    if (typed === null) return; // กดยกเลิก = ไม่สร้าง
    const name = (typed.trim() || `โปรเจกต์ ${projects.length + 1}`).slice(0, 60);
    resetWorkingStateForProject();
    setActiveProjectId(id);
    setProjects((prev) => [...prev, { id, name, updatedAt: now }]);
    setShowProjectMenu(false);
    try {
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername, 'projects', id), { name, createdAt: now, updatedAt: now, creatorIdentity: null, topicSetsByMode: { authority: [], story: [], softsell: [] }, progressTracker: null });
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername), { activeProjectId: id, creatorIdentity: null, topicSetsByMode: { authority: [], story: [], softsell: [] }, progressTracker: null, updatedAt: now }, { merge: true });
    } catch (e) { console.error('[newProject]', e); }
    setCurrentStep(1); // เปิด wizard ตั้งค่าตัวตนของโปรเจกต์ใหม่
  };

  const resetWorkingStateForProject = () => {
    setCreatorIdentity(null);
    setWhatIDo(''); setMyAudience(''); setMyExpertise(''); setMyProof(''); setMyGoals([]); setMyChallenge(''); setChallengeType(''); setChallengeMode(false); setMyProduct('');
    setIdentityText('');
    setTopicSetsByMode({ authority: [], story: [], softsell: [] });
    setActiveSetIdx({ authority: 0, story: 0, softsell: 0 });
    setProgressTracker(null);
    setCustomClips([]);
    setCustomCases([]);
    setAiResult(''); setSelectedTopic(''); setMainView('topics');
  };

  // ✏️ เปลี่ยนชื่อโปรเจกต์
  const handleRenameProject = async (id: string) => {
    if (!currentUsername) return;
    const cur = projects.find((p) => p.id === id);
    const typed = window.prompt('เปลี่ยนชื่อโปรเจกต์', cur?.name || '');
    if (typed === null) return;
    const name = typed.trim().slice(0, 60);
    if (!name || name === cur?.name) return;
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
    try {
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername, 'projects', id), { name, updatedAt: new Date().toISOString() }, { merge: true });
    } catch (e) { console.error('[renameProject]', e); alert('บันทึกชื่อไม่สำเร็จ ลองใหม่'); }
  };

  // เลือกโปรเจกต์ที่เคยเจนไว้
  const handleSelectProject = async (id: string) => {
    setShowProjectMenu(false);
    if (!currentUsername || id === activeProjectId) return;
    try {
      const snap = await getDoc(doc(db, 'coursedee_v1_users', currentUsername, 'projects', id));
      if (!snap.exists()) return;
      const d: any = snap.data();
      applyProjectData(d);
      setActiveProjectId(id);
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername), {
        activeProjectId: id, creatorIdentity: d.creatorIdentity || null,
        topicSetsByMode: d.topicSetsByMode || { authority: [], story: [], softsell: [] }, progressTracker: d.progressTracker || null,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setCurrentStep(0);
    } catch (e) { console.error('[selectProject]', e); }
  };

  // 🧑‍🎨 โหลดตัวละครของโปรเจกต์ที่ active (fallback เป็น 'default' ถ้ายังไม่มีของโปรเจกต์นี้) + เคลียร์ภาพฉากเก่าเมื่อสลับโปรเจกต์
  useEffect(() => {
    setSceneImages([]);
    if (!isAdmin || !currentUsername) { setCharacter(null); setCharDescDraft(''); return; }
    (async () => {
      try {
        const keys = activeProjectId ? [activeProjectId, 'default'] : ['default'];
        for (const k of keys) {
          const s = await getDoc(doc(db, 'coursedee_v1_users', currentUsername, 'characters', k));
          if (s.exists()) {
            const d = s.data() as any;
            setCharacter(d);
            setCharDescDraft(d?.desc || '');
            return;
          }
        }
        setCharacter(null);
      } catch (e) { console.error('[loadCharacter]', e); setCharacter(null); }
    })();
  }, [isAdmin, currentUsername, activeProjectId]);

  // 🔑 คีย์ชุดภาพฉาก = โปรเจกต์ + hash ของหัวข้อ (deterministic — เปิดสคริปต์เดิมได้ชุดเดิม)
  const sceneSetKey = (topic: string) => {
    let h = 5381;
    for (let i = 0; i < topic.length; i++) h = ((h << 5) + h + topic.charCodeAt(i)) >>> 0;
    return `${activeProjectId || 'default'}_h${h.toString(36)}`;
  };

  // เซฟภาพฉากลง Firestore (ย่อเป็น jpeg ก่อน · แยก doc ละภาพ กันชนลิมิต 1MB)
  const saveSceneImageDoc = async (setKey: string, i: number, imageB64: string, mime: string) => {
    if (!currentUsername) return;
    try {
      const small = await b64ToResizedJpeg(imageB64, mime);
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername, 'sceneSets', setKey, 'images', String(i)), { imageB64: small, mime: 'image/jpeg', updatedAt: new Date().toISOString() });
    } catch (e) { console.error('[saveSceneImage]', e); }
  };
  const saveSceneMeta = async (setKey: string, topic: string, scenes: { scene: string; imagePrompt: string; motionPrompt: string }[]) => {
    if (!currentUsername) return;
    try {
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername, 'sceneSets', setKey), {
        topic, projectId: activeProjectId || 'default',
        scenes: scenes.map((s) => ({ scene: s.scene, imagePrompt: s.imagePrompt, motionPrompt: s.motionPrompt })),
        updatedAt: new Date().toISOString(),
      });
    } catch (e) { console.error('[saveSceneMeta]', e); }
  };

  // เปลี่ยนหัวข้อ/โปรเจกต์ → โหลดชุดภาพฉากที่เคยเจนไว้ของสคริปต์นั้น (ถ้ามี) แทนการหายเงียบ
  useEffect(() => {
    setSceneImages([]);
    if (!isAdmin || !currentUsername || !selectedTopic) return;
    let cancelled = false;
    (async () => {
      try {
        const key = sceneSetKey(selectedTopic);
        const metaSnap = await getDoc(doc(db, 'coursedee_v1_users', currentUsername, 'sceneSets', key));
        if (!metaSnap.exists() || cancelled) return;
        const meta: any = metaSnap.data();
        const imgsSnap = await getDocs(collection(db, 'coursedee_v1_users', currentUsername, 'sceneSets', key, 'images'));
        const imgMap: Record<string, any> = {};
        imgsSnap.docs.forEach((d) => { imgMap[d.id] = d.data(); });
        if (cancelled) return;
        const scenes: SceneImage[] = (meta.scenes || []).map((s: any, i: number) => ({
          scene: s.scene || `ฉาก ${i + 1}`, imagePrompt: s.imagePrompt || '', motionPrompt: s.motionPrompt || '',
          imageB64: imgMap[String(i)]?.imageB64, mime: imgMap[String(i)]?.mime,
          status: imgMap[String(i)]?.imageB64 ? 'done' as const : 'error' as const,
        }));
        if (scenes.length) setSceneImages(scenes);
      } catch (e) { console.error('[loadSceneSet]', e); }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, currentUsername, selectedTopic, activeProjectId]);

  const saveCharacter = async (c: { desc?: string; imageB64?: string; mime?: string }) => {
    setCharacter(c);
    if (!currentUsername) return;
    // ถ้าโปรเจกต์ยังโหลดไม่เสร็จ เซฟลง 'default' ไว้ก่อน (ตัวโหลดมี fallback อ่าน 'default' ให้) — กันตัวละครหายเงียบ
    const key = activeProjectId || 'default';
    try {
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername, 'characters', key), { ...c, updatedAt: new Date().toISOString() }, { merge: true });
    } catch (e) {
      console.error('[saveCharacter]', e);
      alert('บันทึกตัวละครไม่สำเร็จ ลองกดเซฟ/เจนใหม่อีกครั้ง');
    }
  };

  // ย่อรูปที่อัปโหลดเป็น JPEG base64 (ยาวสุด 768px) — ให้เล็กพอเก็บ Firestore + ส่งเข้าโมเดล
  const fileToResizedB64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const max = 768;
          const scale = Math.min(1, max / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85).split(',')[1]);
        } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  // ย่อภาพ base64 (เช่นภาพที่ AI เจนมา ~1.5MB PNG) → JPEG เล็ก ๆ ให้เก็บ Firestore ได้ (ลิมิต 1MB/doc)
  const b64ToResizedJpeg = (b64: string, mime: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const max = 768;
          const scale = Math.min(1, max / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.82).split(',')[1]);
        } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = `data:${mime};base64,${b64}`;
    });

  const handleUploadCharacter = async (file: File) => {
    try {
      const b64 = await fileToResizedB64(file);
      await saveCharacter({ desc: charDescDraft.trim() || character?.desc, imageB64: b64, mime: 'image/jpeg' });
    } catch (e) { alert('อ่านรูปไม่สำเร็จ ลองรูปอื่น'); }
  };

  // ✨ เจนตัวละครใหม่จากคำอธิบาย
  const handleGenCharacter = async () => {
    if (!charDescDraft.trim()) { alert('กรอกคำอธิบายตัวละครก่อน เช่น "ช่างหนุ่มไทยวัย 30 ใส่เอี๊ยมส้ม หน้าตาเป็นมิตร"'); return; }
    setIsCharGenerating(true);
    try {
      const p = `Create ONE character reference image for a Thai TikTok content creator persona.\nCharacter: ${charDescDraft.trim()}\n3/4 body shot, facing camera, friendly expression, clean simple studio background, good lighting. This image will be reused as a character reference, so the design must be clear and consistent. Photorealistic, high quality, vertical 9:16.`;
      const { imageB64, mime } = await generateImageAI(p, undefined, undefined, currentUsername);
      const small = await b64ToResizedJpeg(imageB64, mime); // ย่อก่อนเซฟ (PNG ที่เจนมาใหญ่เกินลิมิต Firestore)
      await saveCharacter({ desc: charDescDraft.trim(), imageB64: small, mime: 'image/jpeg' });
    } catch (e: any) {
      alert(e?.message || 'เจนตัวละครไม่สำเร็จ ลองใหม่');
    } finally { setIsCharGenerating(false); }
  };

  // 🖼️ เจนภาพประกอบทุกฉากจากสคริปต์ (ตัวละครหน้าเดิม) + prompt เคลื่อนไหวสำหรับ Google Flow
  const handleGenSceneImages = async () => {
    if (!aiResult || isSceneGenRunning) return;
    if (!character?.imageB64) { alert('ตั้ง "ตัวละคร" ของโปรเจกต์ก่อนครับ — กดปุ่มตัวละคร 🧑‍🎨 ข้างแถบโปรเจกต์'); return; }
    setIsSceneGenRunning(true);
    setSceneImages([]);
    try {
      // 1) ให้ AI อ่านสคริปต์ → แตกเป็นรายฉาก + image prompt + motion prompt (JSON)
      const directorPrompt = `You are a video production director. The Thai TikTok script below is ALREADY divided into scenes/shots (look for the 🎥 camera/shot lines and 🎙️ voiceover lines, or numbered sections). Your job is to FAITHFULLY TRANSCRIBE the script into prompts — NOT to invent your own story.

STRICT RULES:
- Go through the script IN ORDER, one item per scene/shot exactly as the script defines them. Do NOT invent, merge, reorder, add or drop scenes (only if the script has more than 10 shots, combine the most similar adjacent ones to fit 10).
- "scene" = short Thai name taken from that part of the script.
- image_prompt must depict EXACTLY what that scene's 🎥 line says to film — same action, same objects, same location mentioned in the script. Every concrete detail in the script (e.g. the specific product, tool, food, place) MUST appear in the prompt. You may only ADD visual detail (background, lighting, camera), never REPLACE the script's content.
- motion_prompt: the movement must be the action from that scene. If the character speaks, include Thai dialogue taken from that scene's 🎙️ voiceover line — but the clip is 10 seconds MAX, so KEEP IT SHORT: pick only what can be spoken naturally within ~10 seconds (roughly one sentence, max ~20-25 Thai words) from that 🎙️ line, using the script's own words (you may trim/cut, but never rewrite or invent new words), quoted, e.g.: He speaks in Thai, saying: "<short Thai excerpt from the script>". Never cram the whole voiceover into one clip.
- CONTINUITY (critical): all clips are segments of ONE continuous video that will be stitched together. Only scene 1 is the opening. For every other scene, state explicitly: "This is a mid-video continuation — the character is already mid-conversation, continuing his/her sentence naturally. NO greeting, NO waving, NO introduction, NO restarting energy, no looking at the camera as if starting a new video." The tone flows on from the previous scene.

Output ONLY a raw JSON array (no markdown, no explanations, start your reply with "[" immediately), max 10 items, one per scene:
[{"scene":"ฉาก 1 ชื่อสั้นๆ ภาษาไทย","image_prompt":"<English prompt for an AI image generator: describe this exact scene as ONE 9:16 vertical frame, RICH IN DETAIL (4-6 sentences). The main character from the reference image appears in most scenes (same face, same outfit); pure close-up B-roll scenes may omit the character. ALWAYS build a full environment that MATCHES the location this scene actually takes place in according to the script (a home bathroom, a kitchen, a garage, a shop, a market, outdoors, an office... whatever fits the content — set in Thailand where relevant): describe that background in depth — walls/surfaces and materials, furniture or fixtures, decor, light sources, everyday objects that belong to that place — plus foreground and background layers for depth of field, specific props with textures, camera angle and lens feel, lighting mood and shadows. Never leave a plain or empty background. Photorealistic, like a frame from a lifestyle video.>","motion_prompt":"<English, 2-4 sentences, for an AI image-to-video tool (Google Flow / Veo / Omni 1.1 Flash). Direct it like a professional cinematographer: name a specific camera move (slow dolly-in, handheld follow, rack focus, orbital pan, tilt-up reveal...), lens/framing feel and what moves in the frame — make it feel shot by a pro film crew, not a static image. If the character talks/mouths words, ALWAYS specify: he/she is speaking natural, conversational Thai (Thai language audio) — never English — AND describe the voice to match the CHARACTER below (gender, approximate age, personality → e.g. 'warm friendly male Thai voice, mid-30s, casual and confident, like talking to a friend'). Realistic human voice, natural pacing, no robotic tone.>"}]

CHARACTER (use this to describe the speaking voice in motion_prompt):
${(character?.desc || 'Thai person, friendly and approachable').slice(0, 500)}

SCRIPT:
${aiResult.slice(0, 12000)}`;
      // parser ทนทาน: ตัด fence → parse ตรง → หา array → ตัด trailing comma → สุดท้ายดึงเป็นราย object
      const tryParseScenes = (raw: string): any[] => {
        const t = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '').trim();
        const candidates: string[] = [t];
        const arr = t.match(/\[[\s\S]*\]/);
        if (arr) candidates.push(arr[0]);
        for (const c of candidates) {
          try {
            const p = JSON.parse(c.replace(/,\s*([\]}])/g, '$1'));
            if (Array.isArray(p)) return p;
          } catch { /* ลองตัวถัดไป */ }
        }
        const objs = t.match(/\{[\s\S]*?"image_prompt"[\s\S]*?\}(?=\s*[,\]]|\s*$)/g);
        if (objs) {
          const out: any[] = [];
          for (const o of objs) { try { out.push(JSON.parse(o.replace(/,\s*}/g, '}'))); } catch { /* ข้าม */ } }
          return out;
        }
        return [];
      };
      // เรียก director ด้วย temperature ต่ำ (0.2 = ตอบตรงฟอร์แมต) + ลองซ้ำได้ 3 ครั้ง
      let items: any[] = [];
      for (let attempt = 0; attempt < 3 && items.length === 0; attempt++) {
        try {
          const jsonText = await generateWithAI(directorPrompt, currentUsername, 0.2);
          items = tryParseScenes(jsonText).filter((it) => it && it.image_prompt).slice(0, 10);
        } catch { /* ลองใหม่รอบถัดไป */ }
      }
      if (!items.length) throw new Error('อ่านฉากจากสคริปต์ไม่สำเร็จ กรุณาลองใหม่');
      const init: SceneImage[] = items.map((it, i) => ({
        scene: it.scene || `ฉาก ${i + 1}`,
        imagePrompt: String(it.image_prompt || ''),
        motionPrompt: String(it.motion_prompt || ''),
        status: 'pending',
      }));
      setSceneImages(init);
      // เซฟ meta ของชุดนี้ไว้เลย (เปิดสคริปต์นี้ครั้งหน้าจะโหลดกลับมาได้ ไม่ต้องเจนใหม่)
      const setKey = sceneSetKey(selectedTopic || aiResult.slice(0, 60));
      saveSceneMeta(setKey, selectedTopic || '', init);
      // 2) เจนภาพทีละฉาก (ตัวละครอ้างอิงเดิมทุกภาพ) — เว้นจังหวะกันชนลิมิต + เก็บตกอันที่พลาดอัตโนมัติ
      const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
      const genOne = async (i: number): Promise<boolean> => {
        setSceneImages((prev) => prev.map((s, idx) => (idx === i ? { ...s, status: 'generating' } : s)));
        try {
          const { imageB64, mime } = await generateImageAI(
            `${init[i].imagePrompt}\n\nIMPORTANT: Use the person in the attached reference image as the main character — keep the EXACT same face, hairstyle and outfit. Vertical 9:16 composition, TikTok style. Render a highly detailed, fully-dressed environment matching the location described in the prompt: realistic background with fixtures, decor and everyday objects that belong to that place, layered foreground/background depth, rich textures, natural lighting with soft shadows — never a plain or empty backdrop.`,
            character.imageB64, character.mime || 'image/jpeg', currentUsername
          );
          setSceneImages((prev) => prev.map((s, idx) => (idx === i ? { ...s, imageB64, mime, status: 'done' } : s)));
          saveSceneImageDoc(setKey, i, imageB64, mime); // บันทึกถาวร (ไม่บล็อกการเจนต่อ)
          return true;
        } catch (e) {
          console.error('[sceneImage]', i, e);
          setSceneImages((prev) => prev.map((s, idx) => (idx === i ? { ...s, status: 'error' } : s)));
          return false;
        }
      };
      const failed: number[] = [];
      for (let i = 0; i < init.length; i++) {
        const ok = await genOne(i);
        if (!ok) failed.push(i);
        if (i < init.length - 1) await wait(6000); // เว้นจังหวะ กันชนโควตาภาพ/นาที (429)
      }
      // รอบเก็บตก: อันที่พลาด รอ 30 วิ (ให้โควตาต่อนาทีรีเซ็ต) แล้วลองใหม่ให้อัตโนมัติ 1 รอบ
      if (failed.length) {
        await wait(30000);
        for (const i of failed) {
          await genOne(i);
          await wait(6000);
        }
      }
    } catch (e: any) {
      alert(e?.message || 'เจนภาพไม่สำเร็จ กรุณาลองใหม่');
    } finally { setIsSceneGenRunning(false); }
  };

  // เจนซ้ำเฉพาะฉากที่พลาด — ให้ AI เขียน prompt ใหม่เลี่ยงคำที่ชนฟิลเตอร์ภาพก่อน
  // (เช่น "โรยผงสีขาว" โดนฟิลเตอร์เข้าใจผิด → เปลี่ยนเป็น "เทเบกกิ้งโซดาจากกล่องที่มีฉลาก")
  const handleRetryScene = async (i: number) => {
    const sc = sceneImages[i];
    if (!sc || !character?.imageB64) return;
    setSceneImages((prev) => prev.map((s, idx) => (idx === i ? { ...s, status: 'generating' } : s)));
    try {
      let prompt = sc.imagePrompt;
      try {
        const re = await generateWithAI(
          `Rewrite this AI image-generation prompt so it will NOT trigger image-safety false positives, while keeping the EXACT same everyday scene meaning. Rules: make the safe everyday context explicit; replace any ambiguous phrasing that could be misread as something harmful (e.g., loose "white powder" → "baking soda being poured directly from its clearly labeled box"); keep the character, setting and action the same. Reply with the rewritten English prompt ONLY.\n\nPROMPT: ${sc.imagePrompt}`,
          currentUsername, 0.3
        );
        if (re && re.trim().length > 20) prompt = re.trim();
      } catch { /* ใช้ prompt เดิม */ }
      const { imageB64, mime } = await generateImageAI(
        `${prompt}\n\nIMPORTANT: Use the person in the attached reference image as the main character — keep the EXACT same face, hairstyle and outfit. Vertical 9:16 composition, TikTok style. Render a highly detailed, fully-dressed environment matching the location described in the prompt: realistic background with fixtures, decor and everyday objects that belong to that place, layered foreground/background depth, rich textures, natural lighting with soft shadows — never a plain or empty backdrop.`,
        character.imageB64, character.mime || 'image/jpeg', currentUsername
      );
      setSceneImages((prev) => prev.map((s, idx) => (idx === i ? { ...s, imagePrompt: prompt, imageB64, mime, status: 'done' } : s)));
      if (selectedTopic) saveSceneImageDoc(sceneSetKey(selectedTopic), i, imageB64, mime); // บันทึกถาวรด้วย
    } catch (e) {
      setSceneImages((prev) => prev.map((s, idx) => (idx === i ? { ...s, status: 'error' } : s)));
    }
  };

  // 💬 ส่งข้อความแชท — AI ตัดสินเองว่า "คุยปกติ" หรือ "ผู้ใช้สั่งเจนภาพ" (ตอบ JSON) แล้วเจนภาพเด้งในแชท
  const handleChatSend = async () => {
    const text = chatInput.trim();
    if ((!text && !chatAttach) || isChatSending) return;
    setChatInput('');
    const attach = chatAttach;
    setChatAttach(null);
    const history = [...chatMsgs, { role: 'user' as const, text: text || 'เจนภาพจากรูปนี้', imageB64: attach?.b64, mime: attach?.mime, ts: Date.now() }];
    setChatMsgs(history);
    setIsChatSending(true);
    try {
      // 📎 มีรูปแนบ = โหมด "เจนภาพจากต้นแบบ" — ส่งรูป + คำสั่งตรงเข้าโมเดลภาพเลย (nano-banana เข้าใจคำสั่งไทย)
      if (attach) {
        setChatMsgs((prev) => [...prev, { role: 'ai', text: '🎨 กำลังเจนภาพจากรูปต้นแบบให้ครับ...', ts: Date.now() }]);
        const { imageB64, mime } = await generateImageAI(
          `Use the attached image as the base/reference. Follow this instruction (may be in Thai): "${text || 'สร้างภาพใหม่โดยใช้ภาพนี้เป็นต้นแบบ ให้สวยขึ้น คมชัดขึ้น'}"\nKeep everything not mentioned in the instruction unchanged from the reference. High quality.`,
          attach.b64, attach.mime, currentUsername
        );
        setChatMsgs((prev) => [...prev, { role: 'ai', imageB64, mime, ts: Date.now() }]);
        return;
      }
      const transcript = history.slice(-12).map((m) => `${m.role === 'user' ? 'ผู้ใช้' : 'AI'}: ${m.text || (m.imageB64 ? '[ภาพที่เจนไปแล้ว]' : '')}`).join('\n');
      const routerPrompt = `คุณคือผู้ช่วย AI ประจำเว็บ AIForYou คุยเป็นกันเองเหมือนเพื่อน ตอบภาษาไทย กระชับ ธรรมชาติ ใช้คำลงท้าย "ครับ"

กติกาสำคัญที่สุด: ถ้าข้อความล่าสุดของผู้ใช้คือการ "ขอให้สร้าง/วาด/เจน/ทำรูปภาพ" ให้ตอบเป็น JSON ล้วน ๆ บรรทัดเดียว (ห้ามมีข้อความอื่น ห้ามใส่ markdown):
{"image_prompt":"<English prompt สำหรับ AI image generator: บรรยายภาพให้ละเอียด ฉาก แสง มุมกล้อง สไตล์>","use_character":<true ถ้าผู้ใช้อยากให้คนในภาพเป็น "ตัวละครประจำ/ตัวเอง/ตัวละครของโปรเจกต์" ไม่งั้น false>,"say":"<ประโยคสั้น ๆ ภาษาไทยบอกว่ากำลังเจนภาพอะไรให้>"}
ถ้าไม่ใช่การขอภาพ → คุยตอบปกติ (ห้ามใช้ JSON)

บทสนทนา:
${transcript}
AI:`;
      const reply = await generateWithAI(routerPrompt, currentUsername);
      let imgReq: any = null;
      const t = reply.trim().replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```\s*$/, '').trim();
      if (t.startsWith('{')) { try { const p = JSON.parse(t); if (p.image_prompt) imgReq = p; } catch { /* ไม่ใช่ JSON */ } }
      if (imgReq) {
        setChatMsgs((prev) => [...prev, { role: 'ai', text: imgReq.say || '🎨 กำลังเจนภาพให้ครับ...', ts: Date.now() }]);
        const useChar = !!(imgReq.use_character && character?.imageB64);
        const { imageB64, mime } = await generateImageAI(
          `${imgReq.image_prompt}${useChar ? '\n\nIMPORTANT: Use the person in the attached reference image as the main character — keep the EXACT same face, hairstyle and outfit.' : ''}`,
          useChar ? character!.imageB64 : undefined,
          character?.mime || 'image/jpeg',
          currentUsername
        );
        setChatMsgs((prev) => [...prev, { role: 'ai', imageB64, mime, ts: Date.now() }]);
      } else {
        setChatMsgs((prev) => [...prev, { role: 'ai', text: reply.trim(), ts: Date.now() }]);
      }
    } catch (e: any) {
      setChatMsgs((prev) => [...prev, { role: 'ai', text: `ขอโทษครับ มีข้อผิดพลาด: ${e?.message || 'ลองส่งใหม่อีกครั้ง'}`, ts: Date.now() }]);
    } finally {
      setIsChatSending(false);
    }
  };

  // เลื่อนแชทลงล่างสุดเมื่อมีข้อความใหม่
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chatMsgs, isChatSending]);

  // Load user-specific data
  const loadUserData = async (username: string) => {
    try {
      console.log('🔍 [Load User Data] Loading for username:', username);
      
      // Load creator identity from Firestore
      const userDocRef = doc(db, 'coursedee_v1_users', username);
      const userDocSnap = await getDoc(userDocRef);
      
      console.log('📦 [Firestore] Document exists:', userDocSnap.exists());

      let topicsLoaded = false;
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        console.log('📄 [Firestore] Data:', data);
        
        if (data.creatorIdentity) {
          console.log('✅ [Identity Found]:', data.creatorIdentity);
          const parsed = data.creatorIdentity;
          setCreatorIdentity(parsed);
          
          // Build identity text for prompt
          const identityStr = `ฉันทำ: ${parsed.whatIDo}
ความเชี่ยวชาญของฉัน: ${parsed.myExpertise || parsed.myCustomers || ''}
ฉันกำลังพิสูจน์: ${parsed.myProof}${parsed.myProduct ? `\nสินค้าของฉัน: ${parsed.myProduct}` : ''}`;
          setIdentityText(identityStr);
          
          // Load form data
          setWhatIDo(parsed.whatIDo || '');
          setMyExpertise(parsed.myExpertise || parsed.myCustomers || '');
          setMyAudience(parsed.myAudience || '');
          setMyProof(parsed.myProof || '');
          setMyGoals(parsed.myGoals || []);
          setMyChallenge(parsed.myChallenge || '');
          setChallengeType(parsed.challengeType || '');
          setChallengeMode(!!parsed.challengeMode);
          setMyProduct(parsed.myProduct || '');
          
          // Set currentStep to 0 to show content generator (not identity setup)
          setCurrentStep(0);
        } else {
          console.log('❌ [No Identity] creatorIdentity field not found in Firestore');
          
          // Try to restore from localStorage backup
          const localBackup = localStorage.getItem(`coursedee_creator_identity_${username}`);
          if (localBackup) {
            console.log('🔄 [Recovery] Found backup in localStorage, restoring...');
            try {
              const parsed = JSON.parse(localBackup);
              setCreatorIdentity(parsed);
              
              const identityStr = `ฉันทำ: ${parsed.whatIDo}
ความเชี่ยวชาญของฉัน: ${parsed.myExpertise || parsed.myCustomers || ''}
ฉันกำลังพิสูจน์: ${parsed.myProof}${parsed.myProduct ? `\nสินค้าของฉัน: ${parsed.myProduct}` : ''}`;
              setIdentityText(identityStr);
              
              setWhatIDo(parsed.whatIDo || '');
              setMyExpertise(parsed.myExpertise || parsed.myCustomers || '');
          setMyAudience(parsed.myAudience || '');
              setMyProof(parsed.myProof || '');
          setMyGoals(parsed.myGoals || []);
          setMyChallenge(parsed.myChallenge || '');
          setChallengeType(parsed.challengeType || '');
          setChallengeMode(!!parsed.challengeMode);
              setMyProduct(parsed.myProduct || '');
              setCurrentStep(0);
              
              // Re-save to Firestore
              await setDoc(userDocRef, {
                creatorIdentity: parsed,
                updatedAt: new Date().toISOString()
              }, { merge: true });
              console.log('✅ [Recovery] Restored and re-saved to Firestore');
            } catch (e) {
              console.error('❌ [Recovery] Failed to restore from localStorage:', e);
            }
          }
        }
        
        // Load saved content generator data
        if (data.selectedTopic) setSelectedTopic(data.selectedTopic);
        if (data.selectedFormat) setSelectedFormat(data.selectedFormat);
        if (data.generatedPrompt) setGeneratedPrompt(data.generatedPrompt);

        // โหลดหัวข้อ "หลายชุด" 2 แท็บจาก Firestore (sync ข้ามอุปกรณ์ด้วย email เดียวกัน)
        const mkSet = (topics: any, scripts: any): any => ({
          id: `mig_${Math.random().toString(36).slice(2)}`,
          createdAt: data.updatedAt || new Date().toISOString(),
          topics: Array.isArray(topics) ? topics : [],
          scripts: Array.isArray(scripts) ? scripts : [],
        });
        if (data.topicSetsByMode) {
          const a = Array.isArray(data.topicSetsByMode.authority) ? data.topicSetsByMode.authority : [];
          const s = Array.isArray(data.topicSetsByMode.story) ? data.topicSetsByMode.story : [];
          const ss = Array.isArray(data.topicSetsByMode.softsell) ? data.topicSetsByMode.softsell : [];
          setTopicSetsByMode({ authority: a, story: s, softsell: ss });
          setActiveSetIdx({ authority: Math.max(0, a.length - 1), story: Math.max(0, s.length - 1), softsell: Math.max(0, ss.length - 1) });
          topicsLoaded = true;
        } else if (data.topicsByMode) {
          // migrate จากรูปแบบชุดเดียว → เป็นชุดแรกในประวัติ
          const a = data.topicsByMode.authority?.length ? [mkSet(data.topicsByMode.authority, data.scriptsByMode?.authority)] : [];
          const s = data.topicsByMode.story?.length ? [mkSet(data.topicsByMode.story, data.scriptsByMode?.story)] : [];
          setTopicSetsByMode({ authority: a, story: s, softsell: [] });
          setActiveSetIdx({ authority: 0, story: 0, softsell: 0 });
          topicsLoaded = true;
        } else if (Array.isArray(data.topicList) && data.topicList.length > 0 && (data.topicMode === 'authority' || data.topicMode === 'story')) {
          const mode = data.topicMode as ContentMode;
          setTopicSetsByMode((prev) => ({ ...prev, [mode]: [mkSet(data.topicList, data.topicScripts)] }));
          setActiveTab(mode);
          topicsLoaded = true;
        }
        // โหลดแดชบอร์ดผลลัพธ์ (Progress Tracker)
        if (data.progressTracker && Array.isArray(data.progressTracker.checkins)) {
          setProgressTracker(data.progressTracker as ProgressTracker);
        }
        // โหลดคลิปเล่าเรื่องเอง + เคสลูกค้า
        if (Array.isArray(data.customClips)) setCustomClips(data.customClips);
        if (Array.isArray(data.customCases)) setCustomCases(data.customCases);
      } else {
        console.log('❌ [No Document] Firestore document does not exist for username:', username);
        
        // Fallback to localStorage if no Firestore data
        const savedIdentityData = localStorage.getItem(`coursedee_creator_identity_${username}`);
        if (savedIdentityData) {
          try {
            const parsed = JSON.parse(savedIdentityData);
            setCreatorIdentity(parsed);
            
            const identityStr = `ฉันทำ: ${parsed.whatIDo}
ความเชี่ยวชาญของฉัน: ${parsed.myExpertise || parsed.myCustomers || ''}
ฉันกำลังพิสูจน์: ${parsed.myProof}${parsed.myProduct ? `\nสินค้าของฉัน: ${parsed.myProduct}` : ''}`;
            setIdentityText(identityStr);
            
            setWhatIDo(parsed.whatIDo || '');
            setMyExpertise(parsed.myExpertise || parsed.myCustomers || '');
          setMyAudience(parsed.myAudience || '');
            setMyProof(parsed.myProof || '');
          setMyGoals(parsed.myGoals || []);
          setMyChallenge(parsed.myChallenge || '');
          setChallengeType(parsed.challengeType || '');
          setChallengeMode(!!parsed.challengeMode);
            setMyProduct(parsed.myProduct || '');
            
            // Set currentStep to 0 to show content generator
            setCurrentStep(0);
            
            // Migrate to Firestore
            await setDoc(userDocRef, {
              creatorIdentity: parsed,
              updatedAt: new Date().toISOString()
            });
          } catch (e) {
            console.error('Error loading from localStorage:', e);
          }
        }
        
        const savedTopic = localStorage.getItem(`coursedee_v1_selectedTopic_${username}`);
        if (savedTopic) setSelectedTopic(savedTopic);
        
        const savedFormat = localStorage.getItem(`coursedee_v1_selectedFormat_${username}`);
        if (savedFormat) setSelectedFormat(savedFormat);
        
        const savedPrompt = localStorage.getItem(`coursedee_v1_generatedPrompt_${username}`);
        if (savedPrompt) setGeneratedPrompt(savedPrompt);
      }

      void topicsLoaded; // หัวข้อ sync ผ่าน Firestore แล้ว (ไม่ใช้ localStorage cache)
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  // Auto-save (user-specific)
  useEffect(() => {
    if (selectedTopic && currentUsername) {
      localStorage.setItem(`coursedee_v1_selectedTopic_${currentUsername}`, selectedTopic);
    }
  }, [selectedTopic, currentUsername]);

  useEffect(() => {
    if (selectedFormat && currentUsername) {
      localStorage.setItem(`coursedee_v1_selectedFormat_${currentUsername}`, selectedFormat);
    }
  }, [selectedFormat, currentUsername]);

  useEffect(() => {
    if (generatedPrompt && currentUsername) {
      localStorage.setItem(`coursedee_v1_generatedPrompt_${currentUsername}`, generatedPrompt);
    }
  }, [generatedPrompt, currentUsername]);

  const generatePrompt = (autoInWeb = false, topicOverride?: string, saveMode?: ContentMode, saveSetId?: string, clipNumber?: number, totalClips?: number) => {
    const topicToUse = (topicOverride ?? selectedTopic).trim();
    if (!topicToUse) {
      alert('กรุณาใส่หัวข้อที่ต้องการ');
      return;
    }
    if (topicOverride) setSelectedTopic(topicOverride);

    if (!creatorIdentity) {
      alert('กรุณาตั้งค่าตัวตนก่อน');
      return;
    }

    setIsGenerating(true);
    setShowScriptModal(true); // เด้ง popup ทันที (โชว์ loading → ผลลัพธ์)

    setTimeout(() => {
      try {
      const selectedFormatObj = contentFormats.find(f => f.id === selectedFormat);
      const formatName = selectedFormatObj ? selectedFormatObj.label : 'ทั่วไป';
      
      // Category Mapping - EXPERTISE FIRST V4
      const categoryMapping: { [key: string]: string } = {
        'shock': 'Expert + Shock',
        'pain': 'Expert + Pain',
        'curiosity': 'Expert + Curiosity',
        'reality': 'Expert + Reality',
        'hope': 'Expert + Hope',
        'pov': 'Expert + POV',
        'trend': 'Expert + Trend',
        'framework': 'Expert + Framework',
        'insight': 'Expert + Insight',
        'series': 'Expert + Series'
      };
      
      const mappedCategory = selectedFormat ? categoryMapping[selectedFormat] : 'Expert';

      // Soft Sell Logic — ผู้ใช้ระบุสินค้าไว้หรือยัง (ถ้าไม่ ให้ AI แนะนำสินค้าเฉพาะเจาะจงให้)
      const hasProduct = !!(creatorIdentity.myProduct && creatorIdentity.myProduct.trim());

      // รูปแบบสคริปต์ต่างกันตามโหมด: Authority = พูดหน้ากล้อง / Story = เสียงบรรยายทับภาพ-คลิป / Soft-sell = พูดหน้ากล้องแนะนำสินค้า
      const isStoryMode = saveMode === 'story';
      const isSoftSellMode = saveMode === 'softsell';
      const isAuthorityMode = saveMode === 'authority' || (!isStoryMode && !isSoftSellMode);
      // ตรวจเพศผู้พูดจากคำลงท้ายที่ผู้ใช้พิมพ์ในข้อมูลตัวตน → สคริปต์จะได้ลงท้ายตรงเพศ (ครับ/ค่ะ)
      const creatorGender = detectThaiGender(creatorIdentity.whatIDo, creatorIdentity.myAudience, creatorIdentity.myExpertise, (creatorIdentity as any).myChallenge, creatorIdentity.myProduct, identityText);
      // เป้าหมายที่เลือก → สไตล์การเขียน (ให้ "คอนเทนต์สนุก/ไวรัล" ฯลฯ ส่งผลจริงในสคริปต์)
      const goalKeys: string[] = ((creatorIdentity as any)?.myGoals as string[]) || myGoals || [];
      const goalStyleBlock = goalKeys.map((k) => GOAL_STYLE[k]).filter(Boolean).join('\n');
      const genderTone = creatorGender === 'male'
        ? '\n🚹 ผู้พูดเป็น "ผู้ชาย" (ดูจากคำลงท้ายที่ลูกค้าพิมพ์) → บทพูด/แคปชั่นทั้งหมดต้องลงท้ายด้วย "ครับ" อย่างสม่ำเสมอ ❌ ห้ามใช้ "ค่ะ/คะ" เด็ดขาด'
        : creatorGender === 'female'
        ? '\n🚺 ผู้พูดเป็น "ผู้หญิง" (ดูจากคำลงท้ายที่ลูกค้าพิมพ์) → บทพูด/แคปชั่นทั้งหมดต้องลงท้ายด้วย "ค่ะ/คะ" อย่างสม่ำเสมอ ❌ ห้ามใช้ "ครับ" เด็ดขาด'
        : '';
      // 📓 บันทึกการเดินทางจริง — สรุปจากคลิป "เล่าเรื่องเอง" เพื่อให้สคริปต์ใหม่สอดคล้องกับเหตุการณ์ที่เกิดขึ้นแล้ว
      const journeyLog = [
        ...customClips.slice(-8).map((c) => ({ at: c.createdAt, line: `- [${new Date(c.createdAt).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}] ${c.title}: ${c.story.slice(0, 250)}` })),
        ...customCases.slice(-4).map((cs) => ({ at: cs.createdAt, line: `- [${new Date(cs.createdAt).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}] ได้เคสงานจริง "${cs.name}": ${cs.brief.slice(0, 250)}` })),
      ].sort((a, b) => a.at.localeCompare(b.at)).map((x) => x.line).join('\n');
      const journeyBlock = journeyLog ? `
━━━━━━━━━━━━━━━━━━━━━━
[📓 บันทึกการเดินทางจริงของผู้พูด — เหตุการณ์ที่เกิดขึ้นแล้วจริง ๆ]
━━━━━━━━━━━━━━━━━━━━━━
${journeyLog}
กฎการใช้บันทึกนี้:
- ✅ นี่คือ "ข้อเท็จจริงปัจจุบัน" ของผู้พูด — สคริปต์ต้องสอดคล้องกับมัน (เช่น ถ้าได้ลูกค้ารายแรกแล้ว ห้ามพูดเหมือนยังไม่มีลูกค้าเลย)
- ✅ ถ้าเหตุการณ์ไหน "เกี่ยวข้องกับหัวข้อคลิปนี้จริง ๆ" อ้างถึงแบบธรรมชาติได้สั้น ๆ 1-2 ประโยค ในฐานะ "เรื่องที่เกิดขึ้นในชีวิต" (เช่น "ตอนนี้ผมเพิ่งได้ลูกค้ารายแรกเป็นร้านเสริมสวยครับ...")
- ❌ ห้ามอ้างในฐานะ "คลิป/วิดีโอ" เด็ดขาด (ห้ามพูด "คลิปก่อนผมเล่าว่า.../ใครยังไม่ดูคลิปนั้น...") เพราะคนดูส่วนใหญ่ไม่เคยเห็นคลิปนั้น
- ❌ ไม่เกี่ยวก็ไม่ต้องยัด — ห้ามทวนทุกเหตุการณ์ทุกคลิป (จะเลี่ยน)
` : '';
      const scriptFormatSection = `⚠️ CRITICAL — รูปแบบสคริปต์ = "ถ่ายบรรยากาศ + พากย์เสียง" (ทำง่าย ไม่ต้องพูดหน้ากล้องทั้งคลิป):

วิธีทำ: อัดเสียงพากย์รวดเดียวจบ (นั่งพูดที่เดิมก็ได้) แล้วถ่ายภาพ/คลิปตามที่บอก เอามาตัดใส่ทีหลัง
- 🎥 ทุกฉากต้องบอกชัดว่า "ถ่ายอะไร" — บรรยากาศ/มุมกล้อง/สิ่งของ/การกระทำ ที่เจ้าของถ่ายเองได้ง่าย ๆ ด้วยมือถือ (ระบุให้เห็นภาพ ทำตามได้เลย)
- 🎙️ ทุกฉากมี "เสียงพากย์" — บทพูดธรรมชาติเหมือนเล่าให้เพื่อนฟัง มีจังหวะ ไม่แข็ง ไม่เหมือน AI
- 🧑 "โผล่หน้าคน" ให้น้อยที่สุด เฉพาะจังหวะสำคัญ (เปิดคลิป + จุดพีค 1 จุด + ปิดท้าย CTA) — ที่เหลือเป็น B-roll ให้หมด · เอาไว้สร้างตัวตนพอให้คนจำได้ ไม่ต้องพูดหน้ากล้องยาว ๆ
  (สาย Story ที่เป็นการเล่าเรื่อง จะไม่มีหน้าคนเลยก็ได้ ถ้าเรื่องไม่เกี่ยวกับตัวเจ้าของ)
- ❌ ห้ามใช้ตาราง · เขียนแต่ละฉากเป็นบล็อก เว้นบรรทัดว่างคั่น อ่านง่าย ตัดต่อตามได้ทันที

รูปแบบแต่ละฉาก (เว้นบรรทัดว่างระหว่างฉากเสมอ · ระบุ [หน้ากล้อง] หรือ [B-roll] ทุกฉาก):

ฉาก 1 (Hook) [หน้ากล้อง]
🎥 ถ่าย: หน้าคุณพูดเข้ากล้องสั้น ๆ ~2 วิ (หรือ B-roll แรงๆ ถ้าไม่อยากโผล่หน้า)
🎙️ พากย์: "ประโยคเปิดที่หยุดนิ้วคนดู..."

ฉาก 2 [B-roll]
🎥 ถ่าย: [ระบุชัด เช่น โคลสอัพมือกำลังทำงาน / บรรยากาศหน้าร้าน / สินค้า]
🎙️ พากย์: "..."

... สลับ B-roll เป็นหลัก แทรกหน้ากล้องเฉพาะจุดสำคัญ · ปิดท้าย [หน้ากล้อง] + CTA

ใช้ 8-12 ฉาก · ทุกฉากต้องมี 🎥 ถ่าย + 🎙️ พากย์ + ป้าย [หน้ากล้อง]/[B-roll] · หน้ากล้องรวมแค่ 2-3 จุดพอ

⏱️ ความยาว (สำคัญ): คลิปเป้าหมาย 60-90 วินาที = บทพากย์รวมทั้งคลิป ~180-260 คำ
- แต่ละฉากพากย์ 2-4 ประโยค "มีเนื้อ" — เล่าให้ลึก มีรายละเอียด/ความรู้สึก/ตัวอย่างจริง ไม่ใช่ประโยคสั้น ๆ ผ่าน ๆ ฉากละบรรทัดเดียว
- ❌ สคริปต์ที่บทพากย์รวมสั้นกว่า ~150 คำ = สั้นเกินไป ล้มเหลว — คนดูยังไม่ทันอินก็จบแล้ว
- แต่ก็ ❌ ห้ามน้ำ/วนซ้ำเพื่อยืดความยาว — ทุกประโยคต้องเพิ่มอะไรใหม่ (ข้อมูล อารมณ์ หรือภาพ)`;

      // Series Evolution System
      const isSeriesMode = selectedFormat === 'series';
      const seriesEvolutionSection = isSeriesMode ? `

━━━━━━━━━━━━━━━━━━━━━━
[SERIES EVOLUTION SYSTEM]
━━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL

Series is NOT:

❌ Episode 1
❌ Episode 2
❌ Episode 3
❌ Generic Creator Journey
❌ Daily Life Updates
❌ Random Progress Reports

━━━━━━━━━━━━━━━━━━━━━━

Series MUST be:

Expert
↓
Mission
↓
Execution
↓
Result
↓
New Expert Insight

━━━━━━━━━━━━━━━━━━━━━━

SERIES CONTENT FORMULA

Step 1: Reveal Knowledge (Expert)
Step 2: Apply Knowledge (Mission Execution)
Step 3: Measure Outcome (Result)
Step 4: Analyze Outcome (New Expert Insight)
Step 5: Start Next Mission

━━━━━━━━━━━━━━━━━━━━━━

Example - Insurance:

🎯 วิธีเปิดบทสนทนาที่คนไม่หนี
↓
📺 วันนี้ผมจะใช้วิธีนี้กับลูกค้า 20 คน
↓
📺 ผ่านไป 7 วัน มีคนนัดคุยเพิ่มไหม
↓
💡 สิ่งที่ผมค้นพบจากการทดลอง
↓
🎯 วิธีใหม่ที่ผมจะลองต่อ

━━━━━━━━━━━━━━━━━━━━━━

Example - Sticker Shop:

🎯 ทำไม PET ทนกว่า PVC
↓
📺 ผมจะเปลี่ยนงานตัวอย่างทั้งหมดเป็น PET
↓
📺 ผ่านไป 14 วัน ลูกค้าสนใจเพิ่มไหม
↓
💡 สิ่งที่ค้นพบจากการทดลอง
↓
🎯 วิธีใหม่ที่ผมจะลองต่อ

━━━━━━━━━━━━━━━━━━━━━━

MISSION EXECUTION RULE

For every Main Mission, generate content showing:

✅ What is being attempted
✅ Why it is being attempted
✅ How it is being executed
✅ What happened
✅ What was learned

━━━━━━━━━━━━━━━━━━━━━━

MAIN STORY ARC RULE

If the creator is proving something,
the audience must regularly see:

1. Strategy
2. Execution
3. Results
4. Lessons

NOT only lessons.

━━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL

Expert builds trust.
Series builds anticipation.
The strongest content combines both.

━━━━━━━━━━━━━━━━━━━━━━

Expert without Series = Useful
Series without Expert = Entertainment
Expert + Series = Followable Expertise

━━━━━━━━━━━━━━━━━━━━━━` : '';

      const prompt = `You are a TikTok Content Creator Specialist.
${isStoryMode ? `
⚠️⚠️ โหมดนี้คือ STORY (เล่าเรื่องจริง ด้วยเสียงพากย์ทับภาพ/คลิป) ⚠️⚠️
- ใช้รูปแบบ "ถ่ายบรรยากาศ/ภาพ + พากย์เสียง" ตามด้านล่าง (🎥 ถ่าย + 🎙️ พากย์ ทุกฉาก)
- โผล่หน้าคนให้น้อยมาก เฉพาะจังหวะสำคัญ — หรือไม่มีหน้าเลยก็ได้ · ที่เหลือเป็น B-roll/ภาพประกอบของเรื่อง
- 🚨🚨 ห้ามแต่งเรื่องเด็ดขาด! ต้องเป็น "เรื่องจริง" หรือ "สถานการณ์จริงที่คนในวงการ/ลูกค้าเจอกันเป็นปกติ" — ❌ ห้ามสมมติเหตุการณ์/มโนตัวละคร/แต่งบทสนทนาขึ้นเอง (ลูกค้าที่มีอาชีพจะจับได้ทันทีว่าปลอม แล้วเลิกดู = ล้มเหลว) ถ้าไม่แน่ใจว่าจริงห้ามใส่
- 👥 เล่า "เรื่องของคนอื่น/ลูกค้า/เพื่อนร่วมอาชีพ" หรือ "สิ่งที่ทุกคนในวงการต้องเคยเจอ" มากกว่าเรื่องของตัวเราเอง (เช่น "มีลูกค้าประเภทที่...", "คนทำอาชีพนี้ทุกคนต้องเคยเจอ...")
- 🎯 ต้อง relatable: คนดูฟังแล้ว "ใช่เลย เจอประจำ / จริงอะ / เรื่องแบบนี้ก็มี" — เป็นเรื่องที่คนในวงการ/ลูกค้าพยักหน้าตาม (ไม่ใช่เรื่องแปลกจนดูปลอม)
- 🧭 อยู่ใน "วงที่เชื่อมโยงกับธุรกิจ" (ไม่ต้องขาย ไม่ต้องโยงร้าน) · ❌ ห้ามหลุดวงไปเรื่องไม่เกี่ยว · ❌ ห้ามแคบน่าเบื่อเอาแต่ประวัติสินค้า
- 🧠 โทน "อย่าทำตัวรู้ดี/ฉลาดเกินไป" — คนดูต้องรู้สึกฉลาดกว่าเรา บางคลิป (ไม่ใช่ทุกคลิป จะได้ไม่เลี่ยน) ใส่มุมถ่อมตัวแบบ "เพิ่งรู้เหมือนกันว่า..." / "ผมก็เพิ่งสังเกตว่า..." เหมือนค้นพบไปพร้อมคนดู
- ห้ามขายของ ห้ามอวด — เน้นเล่าเรื่องให้อินจนคนอยากติดตามล้วน ๆ
` : ''}
Your ONLY goal:
Create content that makes people STOP SCROLLING and feel EMOTIONALLY INVESTED.

━━━━━━━━━━━━━━━━━━━━━━
[CREATOR IDENTITY]
━━━━━━━━━━━━━━━━━━━━━━
${(creatorIdentity as any)?.challengeMode ? identityText.split('\n').map((l) => challengeEssence(l)).join('\n') : identityText}${creatorIdentity.myAudience ? `\n🎯 กลุ่มเป้าหมาย: ${creatorIdentity.myAudience}` : ''}${creatorIdentity.myExpertise ? `\n💪 จุดแข็งที่ไม่เหมือนใคร: ${creatorIdentity.myExpertise}` : ''}

🎯 สำคัญ: คลิปนี้ต้องออกแบบมาเพื่อ "ดึงกลุ่มเป้าหมายให้อยากดู" — ให้สิ่งที่ "เขาอยากรู้/อยากได้" (คิดจากมุมของกลุ่มเป้าหมาย ไม่ใช่สิ่งที่ร้านอยากขาย)${isStoryMode ? ' · โหมด Story เน้นดึงผู้ติดตามล้วน ๆ "ไม่ต้องโยงกลับร้าน"' : ' แล้วค่อยโยงกลับธุรกิจแบบเนียน'} · ใช้จุดแข็งเป็นมุมที่ต่างจากคู่แข่ง

🗣️ โทนการพูด: พูดคุยกับคนดูตรง ๆ เหมือนคุยกับเพื่อน — พยายามใช้คำว่า "คุณ" ให้คนดูรู้สึกว่า "เรากำลังคุยกับเขาอยู่" (เช่น "คุณเคยไหม...", "ถ้าคุณ...", "บอกเลยว่าคุณ...") ภาษาเป็นธรรมชาติแบบคนจริง ไม่ทางการ${genderTone}
${goalStyleBlock ? `\n🎯 สไตล์คอนเทนต์ตาม "เป้าหมายที่ลูกค้าเลือก" (สำคัญ — ต้องเห็นในสคริปต์ชัด ๆ):\n${goalStyleBlock}` : ''}

⚠️ CRITICAL: Use ONLY this identity
DO NOT assume or add anything
${isAuthorityMode ? `
━━━━━━━━━━━━━━━━━━━━━━
[AUTHORITY = HOW-TO / TUTORIAL สอนทำเป็นขั้นตอน ห้ามขาย ห้ามอวด — สำคัญที่สุด]
━━━━━━━━━━━━━━━━━━━━━━
คลิปนี้คือ "HOW-TO / TUTORIAL" — สอนทำ/แจกวิธี/แจกสูตร เป็นขั้นตอนที่คนดู "ทำตามได้จริง"
🚨 ต่างจาก Story ให้ชัด: Story = เล่าเรื่อง · Authority = สอนทำทีละขั้น — ❌ ห้ามออกมาเป็นแค่ "เล่าเรื่อง/เกร็ดความรู้เฉย ๆ" (ต้องมีวิธีทำที่ทำตามได้)
❌ ห้ามขายของ ห้ามพูดถึงสินค้า/ราคา/โปรโมชั่น ห้ามชวนซื้อ ห้ามอวดตัวเอง/อวดผลงาน
✅ ยิ่งแจกวิธีที่ใช้ได้จริง คนยิ่งเชื่อว่าเก่งจริงเอง (ไม่ต้องบอกว่าตัวเองเก่ง)

🎯 how-to ต้องตรงกับ "สิ่งที่กลุ่มเป้าหมายอยากทำได้เอง" (ดู 🎯 กลุ่มเป้าหมายด้านบน):
- 🅰️ ถ้ากลุ่มเป้าหมายเป็น "ผู้บริโภค/ลูกค้า" → สอน how-to ฝั่งคนใช้/คนกิน/คนซื้อ: "วิธีเลือก [ของดี] ทีละขั้น", "วิธีจอง/เตรียมตัว/ใช้ให้คุ้ม เป็นสเต็ป", ถ้าเป็นของกินทำเองได้ก็ "แจกสูตร/สอนทำเมนู"
- 🅱️ ถ้ากลุ่มเป้าหมาย "อยากทำเอง/อยากทำธุรกิจ" → สอนสูตร/เทคนิค/ขั้นตอนเชิงลึกได้เต็มที่

สคริปต์ต้องเป็น "ขั้นตอนที่ทำตามได้จริง" ให้คนดูดูจบแล้วทำตามได้เลย:
- 📋 วางเป็น "สเต็ป 1 → 2 → 3" หรือ "สูตร/ส่วนผสม/วิธีทำ" ที่ชัดเจน ทำตามได้ทันที
- ใส่รายละเอียดเชิงลึก/เฉพาะทางที่มืออาชีพเท่านั้นรู้ (ศัพท์เฉพาะจริง, ตัวเลข/สัดส่วน/สเปกจริง, จุดที่คนมักพลาด + วิธีแก้)
- ยกตัวอย่างรูปธรรม ไม่ใช่ทฤษฎีลอย ๆ
- ❌ ห้ามคำแนะนำกว้าง ๆ แบบ "ต้องตั้งใจ / ต้องขยัน / ต้องมีคุณภาพ" (= น้ำ ไม่ใช่ how-to = ล้มเหลว)
- 📌 ถ้าเป็นสูตร/วิธีทำเฉพาะร้าน ให้ปิดท้ายด้วยดอกจันโน้ตถึงเจ้าของร้าน (บรรทัดใหม่ ขึ้นต้นด้วย *) ว่า "* คุณปรับสูตร/วิธีนี้ให้เข้ากับสไตล์ร้านคุณเองได้"
` : ''}${isSoftSellMode ? `
━━━━━━━━━━━━━━━━━━━━━━
[SOFT-SELL = ขายสินค้าจริง แต่ขายแบบไม่หนัก — คลิปนี้ "ต้องมีสินค้า" เสมอ]
━━━━━━━━━━━━━━━━━━━━━━
🎯 เป้าหมายคลิปนี้ = แนะนำ/ขายสินค้า "จริง ๆ" ที่เจ้าของร้านเอาไปขายได้ แต่ทำแบบเนียน (ให้คุณค่าก่อน คนเลยอยากซื้อเอง)

🚨 กฎเหล็ก: คลิปนี้ต้องมี "สินค้าที่ขายได้จริง 1 อย่าง" เป็นพระเอกเสมอ — ห้ามพูดลอย ๆ โดยไม่มีสินค้าให้ขายเด็ดขาด
${hasProduct
  ? `- สินค้าของผู้ใช้: "${creatorIdentity.myProduct}" → ใช้สินค้านี้เป็นพระเอกของคลิป (เอ่ยชื่อ/ประเภทให้ชัด)`
  : `- ⚠️ ผู้ใช้ยังไม่ได้ระบุสินค้า → ให้คุณ "เลือกสินค้าเฉพาะเจาะจง 1 อย่าง" ที่เกี่ยวข้องกับธุรกิจ/กลุ่มเป้าหมายนี้ มาแนะนำ (ระบุชื่อ/ประเภท/รุ่น/แบบให้ชัด)
  ✅ ไม่จำเป็นต้องเป็น "สินค้าหลัก" ของธุรกิจอย่างเดียว — เลือก "ของที่เกี่ยวข้อง/ของเสริม/อุปกรณ์ในวงเดียวกัน" ได้ ให้หลากหลายในแต่ละคลิป (เช่น ธุรกิจขายรถยนต์ → สายยางฉีดล้างรถ / ครีมขัดเงา / ที่วางมือถือในรถ / น้ำหอมในรถ / ที่ดูดฝุ่นในรถ — ไม่ต้องเน้นขายรถอย่างเดียว)
  ❌ ห้ามพูดคำว่า "สินค้า/ของ" ลอย ๆ โดยไม่บอกว่าเป็นอะไร — แล้วสร้างสคริปต์ขายรอบ ๆ สินค้าตัวนั้น`}
- ต้องบอกให้ครบในคลิป: (1) สินค้าคืออะไร ชื่อชัด (2) แก้ปัญหา/ช่วยลูกค้าเรื่องอะไร (3) ทำไมตัวนี้ดี/ต่างจากที่อื่น (4) ซื้อ/สั่งยังไง
- โครงขายเนียน: เปิดด้วย pain/สถานการณ์จริงของลูกค้า → เฉลยว่าสินค้านี้คือทางออก → โชว์จุดเด่น/รีวิว/ก่อน-หลัง → ปิดด้วย CTA ชัดแต่ไม่กดดัน (เช่น "ทักมาถามได้เลย" / "กดลิงก์สั่งใน bio")
- ✅ ต้องมีการชวนซื้อ/แนะนำสินค้าจริง ไม่ใช่เลี่ยงการขาย — แต่ ❌ ห้ามฮาร์ดเซล/ตะโกนขาย/พูดแต่ราคา-โปรลดรัว ๆ (ให้คุณค่านำ สินค้าเป็นทางออก)
${!hasProduct ? '- 📌 ปิดท้ายด้วยดอกจันโน้ตถึงเจ้าของร้าน (บรรทัดใหม่ ขึ้นต้นด้วย *) ว่า "* นี่คือสินค้าตัวอย่างที่แนะนำ — เปลี่ยนเป็นสินค้าจริงที่ร้านคุณมี/อยากขายได้เลย"' : ''}
` : ''}
━━━━━━━━━━━━━━━━━━━━━━
[TOPIC]
━━━━━━━━━━━━━━━━━━━━━━
${topicToUse}
${clipNumber && totalClips ? `
━━━━━━━━━━━━━━━━━━━━━━
[คลิปนี้ต้อง "จบในตัวเอง" — ห้ามอ้างอิงข้ามคลิป สำคัญมาก]
━━━━━━━━━━━━━━━━━━━━━━
คลิปนี้เป็นคลิปที่ ${clipNumber} ในซีรีส์ยาว แต่คนดูส่วนใหญ่จะเห็น "คลิปนี้เดี่ยว ๆ" โดยไม่เคยดูคลิปอื่นเลย → ต้องดูรู้เรื่องและจบสมบูรณ์ในตัวเอง
- 🚨 กฎเหล็กที่สุด: เล่า/สอน/เฉลยหัวข้อนี้ให้ "ครบจบในคลิปเดียว" คนดูได้คำตอบเต็ม ๆ ไม่ค้างคา
- 🚫 ห้ามอ้างอิงถึงคลิปอื่นเด็ดขาด — ❌ ห้ามพูดว่า "คลิปที่แล้วเราคุยเรื่อง..." / "ต่อจากคลิปก่อน..." / "เดี๋ยวคลิปหน้าจะมาสอน/มาเล่าเรื่อง [X]" เพราะคลิปนี้ไม่รู้จริงว่าคลิปอื่นพูดอะไร (พูดไปก็เป็นการหลอก คนดูงงว่าไม่เห็นเคยพูดถึงเลย = ล้มเหลว)
- ✅ เปิดคลิปด้วยฮุกของ "หัวข้อนี้เอง" ทันที ไม่ต้องเกริ่นว่ามาจากคลิปไหนหรือเป็นวันที่เท่าไหร่
- ✅ ปิดคลิปด้วยการตอกย้ำคุณค่าของ "หัวข้อนี้" + จะมี CTA ชวนติดตามแบบทั่วไปก็ได้ (เช่น "กดติดตามไว้ มีคลิปดี ๆ อีกเยอะ") แต่ ❌ ห้ามบอกว่า "คลิปหน้าจะเป็นเรื่อง [X] เฉพาะเจาะจง"
- 📍 ปรับโทน/ความลึกให้เหมาะกับช่วงที่ ${clipNumber < totalClips ? `ยังไม่ใช่ตอนจบซีรีส์ → ❌ ห้ามสรุปผลรวมทั้งซีรีส์ หรือพูดว่า "วันสุดท้าย" / "${totalClips} วัน/คลิป"` : `เป็นคลิปสุดท้าย → สรุปภาพรวมได้`}
- ⚠️ ถ้าในหัวข้อมีเลข "วัน/Day" ให้ยึด "คลิปที่ ${clipNumber}" เป็นหลัก ไม่กระโดดข้าม
` : ''}${journeyBlock}
${(creatorIdentity as any)?.challengeMode && (creatorIdentity as any)?.myChallenge ? `
━━━━━━━━━━━━━━━━━━━━━━
[🔥 โหมดลุยชาเลนจ์จริง — กฎสูงสุด อ่านท้ายสุดและชนะทุกกฎด้านบนถ้าขัดกัน]
━━━━━━━━━━━━━━━━━━━━━━
ทั้งซีรีส์นี้คือ "การลุยทำภารกิจจริง" ของผู้พูด ให้คนดูลุ้น + เอาใจช่วย + ติดตามว่าจะสำเร็จไหม (สไตล์เรียลลิตี้/vlog)
🎯 ภารกิจ (ข้อมูลภายใน — ห้ามท่องให้คนดูฟัง): "${challengeEssence((creatorIdentity as any).myChallenge)}"
🚨 ข้อความภารกิจข้างบนเป็น "โน้ตภายใน" อาจมีรายละเอียดแผน/ขั้นตอนเขียนไว้ — เวลาเอ่ยภารกิจในบทพูด ให้พูดแค่ "แก่น" = เป้าหมาย + กรอบเวลา เท่านั้น (เช่น "หาแสนแรกใน 60 วัน") ❌ ห้ามท่อง/สรุปรายการขั้นตอนหรือสิ่งที่จะบันทึกจากข้อความภารกิจเด็ดขาด (มันคือสารบัญ = สปอยล์ + ฟ้องว่าเตรียมมาหมดแล้ว ไม่ใช่คนเพิ่งเริ่ม)
🚨 กฎนี้อยู่เหนือกฎโหมด Story/Authority/Soft-sell และเหนือกฎ "เปิดด้วยฮุกของหัวข้อ" — เนื้อหาของหัวข้อยังต้องครบ แต่ต้อง "เล่าผ่านมุมคนที่กำลังลุยภารกิจนี้อยู่จริง" เสมอ (ห้ามออกมาเป็นผู้รู้มาสอน/มาเล่าเฉย ๆ โดยไม่มีภารกิจ)
🚫 กฎแบรนด์ (เข้มสุด — ยกเว้นเฉพาะคลิป Soft-sell): "ชื่อธุรกิจ/ชื่อแบรนด์/ชื่อเว็บไซต์" ทุกชื่อที่ปรากฏในข้อมูลตัวตนหรือภารกิจ (รวมคำภาษาอังกฤษอย่างชื่อแบรนด์ .co/.com) = **ข้อมูลภายใน ห้ามปรากฏที่ไหนก็ตามในสคริปต์**:
  ❌ ห้ามอยู่ในบทพากย์ 🎙️ ("ธุรกิจชื่อ X", "แบรนด์ X ของผม")
  ❌ ห้ามอยู่ในคำสั่งถ่าย 🎥 (ห้ามสั่งถ่ายสมุด/ป้าย/หน้าจอที่เขียนชื่อแบรนด์)
  ❌ ห้ามอยู่ในการ์ดสรุป/Hook — อยู่ได้แค่ "แฮชแท็ก" เท่านั้น
  ✅ แทนด้วยอาชีพ/สิ่งที่ทำเสมอ: "ธุรกิจรับทำเว็บไซต์ของผม" ไม่ใช่ "ธุรกิจชื่อ X" — คนธรรมดาที่กำลังลุยไม่พูดชื่อแบรนด์ตัวเอง มันฟ้องว่าเป็นโฆษณาทันที ให้คนดูอยากรู้แล้วไปหาเองจากโปรไฟล์
🚫 ห้ามขายของเด็ดขาด (คนดูหนีทันที): ห้ามอธิบายบริการ/จุดขาย/ฟีเจอร์/ระบบของธุรกิจเหมือนโฆษณา (เช่น "เรารับทำ...ตั้งแต่...ไปจนถึง...", "บริการเราไม่ได้มีแค่...แต่ยังมีระบบ...") — บอกแค่ให้รู้ว่าทำอาชีพอะไร 1 ประโยคสั้น ๆ พอ ที่เหลือให้คนดูเห็นเองจากการลุยจริง
🚫 ห้าม "เล่าสารบัญ" ว่าซีรีส์นี้จะพาดูอะไรบ้าง — ❌ ประโยคต้องห้ามทุกรูปแบบ: "ผมจะพาคุณดูทุกขั้นตอน ตั้งแต่...จนถึง...", "ตลอด 60 วันนี้ผมจะ...", "จะได้เห็นทั้ง A, B, C" — มันสปอยล์จนหมดตื่นเต้น ให้ทิ้งปมสั้น ๆ ให้คนอยากตามเอง (เช่น "จะรอดหรือจะเจ๊ง มาดูกัน")
🎯 จุดยืนของซีรีส์ (สำคัญที่สุด): ผู้พูดคือ "ตัวแทนของคนธรรมดาที่กล้าลอง" — ❌ ไม่ใช่ "มาดูผมทำสำเร็จ/ผมจะพิสูจน์ให้คุณเห็น/ผมอยากให้คุณเห็นว่าผมทำได้" (self-centered คนดูหนี) ✅ ต้องเป็น "มาลุ้นไปด้วยกัน": คนดูดูแล้วได้แรงบันดาลใจว่า "ถ้าคนธรรมดาแบบนี้กล้าลอง เราก็อาจทำได้เหมือนกัน" — พูดถึงความกลัว/ความหวังที่คนดูก็มีเหมือนกัน ("ใครที่กำลังอยากเริ่มอะไรสักอย่างแต่ไม่กล้า... ผมก็เหมือนคุณนี่แหละ") ชวนคนดูเป็นเพื่อนร่วมทาง ไม่ใช่ผู้ชมผลงาน
🚨 โทนสำคัญที่สุด = "UNDERDOG ผู้กล้า" ไม่ใช่ "ผู้เก่งมาโชว์" — คนดูไม่ได้เชียร์คนเก่ง คนดูเชียร์คนกล้า:
  ✅ เปิดจุดอ่อนเสมอ: เริ่มจากศูนย์/ยังไม่มีลูกค้า/ตื่นเต้น/ไม่รู้จะรอดไหม ("ไม่รู้จะทำได้หรือเปล่านะครับ 😅 ลองผิดลองถูกไปด้วยกัน")
  ✅ คนเพิ่งเริ่มจริง ๆ "ยังไม่รู้อนาคต" — ห้ามพูดเหมือนรู้อยู่แล้วว่าต้องทำอะไรบ้างเป็นขั้น ๆ (คนดูจะจับได้ว่า "อ้าว เตรียมไว้หมดแล้วนี่ ไหนว่าเพิ่งเริ่ม") ให้พูดแบบคนไม่รู้ทาง เช่น "ยังไม่รู้เลยว่าต้องเริ่มตรงไหนก่อนด้วยซ้ำ 😅"
  ❌ ห้ามพูดเหมือนมั่นใจว่าทำได้แน่ ห้ามโชว์ความเก่ง/ผลงาน/ความสำเร็จเดิมเพื่อการันตี — ตัวเลขเป้าหมายคือ "เดิมพันให้คนลุ้น" ไม่ใช่คำโม้
  ✅ ยอมรับความผิดพลาด/ความพัง/โดนปฏิเสธ ตรง ๆ ระหว่างทาง = เสน่ห์ของซีรีส์ ห้ามกลบ
${clipNumber === 1 ? `- 🎬 คลิปนี้คือ "คลิปเปิดภารกิจ (คลิปแรก)" → ฉากแรกต้องเปิดด้วยการ "ประกาศชาเลนจ์" ก่อนเข้าหัวข้อ — แบบ underdog ถ่อมตัวแต่กล้า
  ✅ พูดทำนอง "ผมเพิ่งเริ่ม[ธุรกิจนี้] ยังไม่มีอะไรเลย... ขอลองตั้งเป้าโหด ๆ ดู ว่า[ภารกิจ]จะทำได้จริงไหม ไม่รู้จะรอดหรือเปล่านะครับ มาลุ้นไปด้วยกันนะ!" ให้ฟังแล้วอยากเอาใจช่วยทั้งซีรีส์ — ❌ ห้ามต่อท้ายด้วย "จะทำให้ดู/พาดูทุกขั้นตอน" หรือไล่รายการสิ่งที่จะทำ
  ✅ บอกจุดเริ่มต้นตามจริง (เริ่มจากศูนย์/ตอนนี้อยู่ตรงไหน) + ความกล้าที่จะลุย
  ❌ ห้ามพูดแบบหัวข้อแข็ง ๆ/ทางการ — ต้องเป็นภาษาพูดเหมือนชวนเพื่อนมาลุ้นด้วยกัน
  ❌ คลิปเปิดต้อง "สั้น กระชับ มีพลัง" — ประกาศเดิมพัน + จุดเริ่มต้น + ความกล้า + ปิดด้วยคำถามชวนลุ้น จบ ไม่ต้องอธิบายธุรกิจยาว ไม่ต้องบอกแผนทั้งซีรีส์
  → จากนั้นค่อยเข้าเนื้อหาของ [TOPIC] โดยเล่าให้เชื่อมกับภารกิจ` : `- 🔥 คลิปนี้คือ "ระหว่างลุยภารกิจ"${clipNumber ? ` (สเต็ป/วันที่ ${clipNumber})` : ''} → เปิดด้วยการทวนภารกิจสั้น ๆ แบบตื่นเต้น 1 ประโยค (เช่น "ยังอยู่ในภารกิจ[...]กันนะครับ!") แล้วเล่าเนื้อหาหัวข้อผ่านสิ่งที่เจอจริงระหว่างลุย
  ✅ ให้คนรู้สึกว่ากำลังดูการเดินทางจริง — มีอุปสรรค/ความคืบหน้า/จุดลุ้น (แต่ห้ามเล่าอ้างอิงเนื้อหาคลิปอื่นแบบเฉพาะเจาะจง)`}
- ทุกคลิปยังต้องมีคุณค่า/สนุกจบในตัวเอง ไม่ใช่แค่รายงานความคืบหน้าแห้ง ๆ
` : ''}

━━━━━━━━━━━━━━━━━━━━━━
[EXPERTISE FIRST SYSTEM V4]
━━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL

Regardless of selected category,
all content must follow Expertise First System.

Every content must contain at least one:

✅ useful knowledge
✅ framework
✅ checklist
✅ comparison
✅ workflow
✅ hidden truth
✅ real-world insight
✅ diagnosis
✅ case study
✅ decision process

If none exist, rewrite the content.

━━━━━━━━━━━━━━━━━━━━━━

SELECTED CATEGORY: ${mappedCategory}

Category Mapping:

😱 Shock → Expert + Shock
💔 Pain → Expert + Pain
🤔 Curiosity → Expert + Curiosity
😮 Reality → Expert + Reality
✨ Hope → Expert + Hope
🎭 POV + Roleplay → Expert + POV
🔥 Trend → Expert + Trend
🎯 Framework → Expert + Framework
💡 Insight → Expert + Insight
📺 Series → Expert + Series

━━━━━━━━━━━━━━━━━━━━━━

Emotion Outside
Expertise Inside
Always.

━━━━━━━━━━━━━━━━━━━━━━${seriesEvolutionSection}

━━━━━━━━━━━━━━━━━━━━━━
[UNIVERSAL FORMAT RULES]
━━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL: Every format works for EVERY niche

NOT just for:
❌ creators
❌ online business

BUT ALSO for:
✅ ร้านอาหาร
✅ เซลขายบ้าน
✅ ร้านมือถือ
✅ คาเฟ่
✅ ร้านนวด
✅ ฟรีแลนซ์
✅ ทุก niche

━━━━━━━━━━━━━━━━━━━━━━
[CORE PHILOSOPHY]
━━━━━━━━━━━━━━━━━━━━━━

TikTok คนไม่ได้ติดตาม niche

แต่ติดตาม:
✅ คน
✅ ความพยายาม
✅ challenge
✅ journey
✅ emotion
✅ progress
✅ transformation
✅ human struggle

System must optimize for:
"ทำยังไงให้คนอยากติดตามชีวิตคนนี้ต่อ"

NOT:
❌ "จะให้ความรู้ยังไง"

━━━━━━━━━━━━━━━━━━━━━━
[EMOTIONAL ROLLER COASTER SYSTEM - CRITICAL]
━━━━━━━━━━━━━━━━━━━━━━

⚠️ MOST IMPORTANT RULE:

TikTok ที่คนดูจนจบ ไม่ใช่แค่ "พูดจริง"
แต่ต้อง "ทำให้คนรู้สึกขึ้นลงตลอดคลิป"

Every script MUST have:
✅ Emotional up/down
✅ Tension swing
✅ Silence moment
✅ Emotional spike
✅ Relatable drop
✅ Payoff moment

อารมณ์ต้อง:
เจ็บ → เงียบ → พีค → ผ่อน → ลุ้น → อิน

❌ NEVER:
- อารมณ์นิ่งทั้งคลิป
- เล่าเรียบ
- podcast style
- motivational speech
- emotional tone เดียวทั้งคลิป

━━━━━━━━━━━━━━━━━━━━━━
[SCENE-BY-SCENE EMOTIONAL FLOW]
━━━━━━━━━━━━━━━━━━━━━━

Scene ต่อ Scene อารมณ์ต้อง "เปลี่ยน"

Example flow:

Scene 1: เปิดแรง / emotional hook
Scene 2: relatable pain
Scene 3: emotional spike
Scene 4: เงียบ / tension drop
Scene 5: เริ่มมีหวัง
Scene 6: mission / cliffhanger / emotional ending

Each scene MUST include:
✅ เป้าหมายอารมณ์
✅ Visual feeling
✅ Tension level

NOT just: "พูดอะไร"

━━━━━━━━━━━━━━━━━━━━━━
[PAUSE & SILENCE SYSTEM]
━━━━━━━━━━━━━━━━━━━━━━

Script MUST include:

✅ Pause
✅ Silence
✅ Awkward moment
✅ Reaction
✅ Visual tension
✅ Emotional shift

Examples:
- [นั่งเงียบ 2 วิ]
- [ถอนหายใจ]
- [มองยอดวิว]
- [ลังเลก่อนพูด]
- [พูดแล้วหยุด]
- [เงียบ 1 วิ]
- [หัวเราะแห้งๆ]
- [เปลี่ยนสีหน้า]
- [เปิด analytics]
- [เปิดคลิปเก่า]

━━━━━━━━━━━━━━━━━━━━━━
[DIALOGUE REALISM]
━━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL: ห้ามพูดสวยเกินไป

Dialogue MUST have:
✅ พูดซ้ำ
✅ เว้น
✅ เงียบ
✅ พูดไม่สุด
✅ ลังเล
✅ Emotional pause
✅ Awkward realism

ให้รู้สึกเหมือน:
"คนจริงกำลังพูด"

ไม่ใช่:
"AI เขียนสคริปต์"

━━━━━━━━━━━━━━━━━━━━━━
[SCRIPT REQUIREMENTS]
━━━━━━━━━━━━━━━━━━━━━━

🎯 CRITICAL: GENERATE ACTUAL READY-TO-READ SCRIPTS

DO NOT write:
❌ "(คุยกับเพื่อนสนิท...)"
❌ "(เล่าให้เพื่อนฟัง...)"
❌ "(แสดงอารมณ์...)"

INSTEAD:
✅ Write ACTUAL WORDS the creator will say
✅ Include emotional delivery cues
✅ Add visual direction
✅ Add pause and silence markers
✅ Add emotional state changes

Script MUST feel like:
✅ TikTok creator จริง
✅ Documentary moment
✅ Expertise journey
✅ Value delivery

NEVER feel like:
❌ Podcast
❌ YouTube lecture
❌ Inspirational speech
❌ AI storytelling
❌ คอร์สออนไลน์
❌ Corporate content
❌ Motivational guru

━━━━━━━━━━━━━━━━━━━━━━
[OPTIMIZATION GOAL]
━━━━━━━━━━━━━━━━━━━━━━

Optimize for BOTH:
✅ Retention + emotional engagement (make them watch to the end)
✅ REAL VALUE — the viewer must LEARN something useful, get a fresh insight, or be genuinely entertained. They should feel "this was worth watching" / "this person really knows their stuff".

⚠️ The content MUST deliver actual knowledge/tips/insight relevant to the topic — NOT vague hype, NOT an ad. Entertaining AND useful, like a top real TikTok creator.

Avoid:
❌ Empty hype with no substance
❌ Turning every video into a product pitch
❌ Boring lecture tone (keep it punchy & fun)

━━━━━━━━━━━━━━━━━━━━━━
[HOOK ENGINEERING] ⭐ CRITICAL
━━━━━━━━━━━━━━━━━━━━━━

The first 1-3 seconds decide EVERYTHING. If the hook fails, nothing else matters.

RULES for the hook:
- Drop the viewer straight INTO the tension/result/conflict. NO greetings, NO "สวัสดีครับ วันนี้จะมาเล่า", NO warm-up, NO intro.
- Open a CURIOSITY GAP the brain MUST close (ask a question in the viewer's head that only the ending answers).
- Say something that makes the viewer feel "เดี๋ยวนะ..." / "จริงเหรอ" / "อันนี้เกี่ยวกับฉัน".
- Pick a hook angle that fits the emotion category (${mappedCategory}):
  • bold/contrarian claim ("ทุกคนเข้าใจผิดเรื่องนี้")
  • painful truth ("เหตุผลจริงที่ลูกค้าไม่กลับมา ไม่ใช่เพราะราคา")
  • shocking number / result ("ผมเสียเงินไป 50,000 กว่าจะรู้เรื่องนี้")
  • unfinished/open statement ("สิ่งที่ไม่มีใครบอกคุณเกี่ยวกับ...")
  • "you're doing X wrong" ("ถ้าคุณยังทำแบบนี้อยู่ หยุดเลย")
- The hook must PROMISE a payoff that is delivered ONLY at the end (never give the answer in the hook).

Provide 3 hook options, each a different angle, each ≤ 2 sentences, each usable as the literal first spoken line.

━━━━━━━━━━━━━━━━━━━━━━
[RETENTION ENGINEERING — ดูจนจบ] ⭐ CRITICAL
━━━━━━━━━━━━━━━━━━━━━━

Goal: the viewer physically cannot stop watching until the last second.

RULES for the full script:
- OPEN LOOP: the question/promise from the hook stays UNANSWERED until the final scene. The payoff lives at the end — that is the reason to watch till the end.
- RE-HOOK constantly: every ~3-5 seconds add a new micro-tension — a twist, a "แต่เดี๋ยวก่อน...", a new question, a "และนี่คือส่วนที่หลายคนพลาด".
- ESCALATE stakes scene by scene (emotional roller coaster) — never let tension flatten.
- ZERO filler: every single line must either raise tension or deliver value. Cut anything that can be cut.
- PATTERN INTERRUPT: change pace/tone with [เงียบ], [pause], visual cue, or a sudden shift to reset attention before it drops.
- PAYOFF + LOOP at the end: resolve the open loop, then close with a line that makes them comment / rewatch / follow (a question, a cliffhanger to the next clip, or a bold takeaway).

The script's structure must read as: Hook (open loop) → escalating tension with re-hooks → payoff (close loop) → CTA/loop.

━━━━━━━━━━━━━━━━━━━━━━
[OUTPUT FORMAT]
━━━━━━━━━━━━━━━━━━━━━━

Output these sections in order, with a BLANK LINE between each section (keep it airy and easy to read). Do NOT print any "Category" label or meta headers — just the content below.

🃏 การ์ดสรุป (สั้น ๆ 2 บรรทัด ก่อนเข้าเนื้อหา — ให้เจ้าของเห็นภาพก่อนว่าคลิปนี้ได้อะไร)
🎯 ดึงเอ็นเกจเมนต์: [คลิปนี้กระตุ้นอะไร เช่น คอมเมนต์ถามต่อ / แชร์บอกต่อ / เซฟไว้ใช้ / แท็กเพื่อน — เลือกที่ตรงที่สุด]
👀 คนดูจะได้: [ผู้ชมได้อะไรจากคลิปนี้ ประโยคเดียวสั้น ๆ]

🎬 HOOK (เลือก 1 จาก 3)
ให้ 3 ตัวเลือกประโยคเปิด แต่ละอันเป็นคำพูดเปิดที่พูดได้จริง สะดุด หยุดนิ้วคนดู (${mappedCategory})

📝 สคริปต์เต็ม (พร้อมใช้)

${scriptFormatSection}

━━━━━━━━━━━━━━━━━━━━━

📱 แคปชั่นลงโพสต์ (สั้น กระชับ มี emoji ชวนกด)

#️⃣ แฮชแท็ก (10-15 อัน เกี่ยวข้องกับเนื้อหา)

━━━━━━━━━━━━━━━━━━━━━
[AUDIENCE LAYER SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

Before generating content, identify all audience groups that may care about this profession.

Do NOT assume there is only one audience.

Examples:

Gold Shop:
* First-time gold buyers
* People saving money
* Families planning for the future
* Investors
* People looking for side income
* People interested in wealth preservation
* People comparing financial options

Beauty:
* Beginners
* People with skin problems
* Busy workers
* Brides-to-be

Real Estate:
* First-time buyers
* Investors
* Families
* People wanting passive income

CRITICAL:
Every content topic must be connected to:
1. One emotion
2. One audience segment
3. One practical lesson

Add a column: **Audience** for every content item in the 90-day plan table.

━━━━━━━━━━━━━━━━━━━━━
[LIFE SITUATION LAYER]
━━━━━━━━━━━━━━━━━━━━━

Do NOT create content only around the product.
Create content around situations people experience.

Examples for Gold:
* Getting married
* Having a child
* Saving for the future
* Economic uncertainty
* First salary
* Debt recovery
* Building family security
* Receiving inheritance

Every content should connect expertise to a real human situation.
People relate to situations more than products.

━━━━━━━━━━━━━━━━━━━━━
[EMOTION DIVERSITY SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

The same emotion category must not repeat the same emotional trigger.

Pain can come from:
* Regret
* Fear
* Shame
* Frustration
* Loss
* Missed Opportunity

Hope can come from:
* Progress
* Recovery
* New Start
* Small Win
* Breakthrough

Shock can come from:
* Hidden Truth
* Unexpected Comparison
* Industry Secret
* Surprising Numbers

CRITICAL:
Even within the same category, the emotional source must change frequently.
Avoid emotional repetition.

━━━━━━━━━━━━━━━━━━━━━
[AUTHENTICITY & ETHICS]
━━━━━━━━━━━━━━━━━━━━━

CRITICAL RULES:

❌ NEVER fabricate fake stories or testimonials
❌ NEVER create fictional customer examples
❌ NEVER invent numbers or results that didn't happen
❌ NEVER promise guaranteed outcomes
❌ NEVER use extreme/dangerous claims

✅ Content MUST be based on:
- Real situations the creator has experienced
- Actual customer cases (if creator has them)
- Industry knowledge and expertise
- Honest observations and insights
- Realistic timelines and expectations

DISTINCTION:

❌ FABRICATED (ห้าม):
"ลูกค้าคนนี้ทำตามผม รวยใน 30 วัน"
→ แต่งเรื่อง ไม่มีลูกค้าคนนี้จริง

✅ REAL EXPERIENCE (ได้):
"6 เดือนแรกที่ผมเปิดร้าน ขาดทุน แต่เรียนรู้ 5 สิ่งนี้"
→ ประสบการณ์จริงของผู้สร้าง

✅ EDUCATIONAL (ได้):
"ทำไมคนซื้อบ้านหลังแรก 80% ถึงเลือกผิด"
→ ความรู้จากประสบการณ์ในวงการ

✅ REALISTIC SCENARIO (ได้):
"ถ้าคุณเริ่มเก็บทอง 1,000 บาทต่อเดือน ใน 5 ปีจะได้อะไร"
→ สถานการณ์สมมติที่สมเหตุสมผล

FORBIDDEN CONTENT:
❌ Fake customer testimonials
❌ Invented success stories
❌ Made-up statistics
❌ Fictional case studies
❌ Exaggerated results
❌ Guaranteed outcomes
❌ Unrealistic timelines

ALLOWED CONTENT:
✅ Creator's own journey
✅ Real customer stories (if they exist)
✅ Industry insights from experience
✅ Educational scenarios
✅ Honest mistakes and lessons
✅ Realistic expectations
✅ Transparent processes

CONTENT MUST BE:
1. TRUE - Based on real experience or knowledge
2. HONEST - No exaggeration or false promises
3. HELPFUL - Provides real value
4. ETHICAL - Doesn't mislead or harm
5. SUSTAINABLE - Builds long-term trust

Examples:
✅ "5 ข้อผิดพลาดที่ผมทำตอนเริ่มขายของออนไลน์"
✅ "ทำไมผมใช้เวลา 2 ปีถึงขายได้สม่ำเสมอ"
✅ "สิ่งที่ไม่มีใครบอกเกี่ยวกับการซื้อทอง"
✅ "ถ้าคุณมีเงิน 10,000 บาท ควรเริ่มต้นอย่างไร"

❌ "ลูกค้าผมคนนี้รวยใน 7 วัน" (ถ้าไม่มีลูกค้าคนนี้จริง)
❌ "ทำตามนี้รับประกันขาย 100,000 ใน 1 เดือน"
❌ "วิธีลับที่ทำให้ผมรวยข้ามคืน" (ถ้าไม่เป็นความจริง)

━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL REMINDERS:
- Script MUST have emotional roller coaster
- Each scene MUST have different emotional tone
- Include [pause], [silence], [visual cues]
- Dialogue MUST feel real, not AI-written
- MUST deliver REAL value: a useful tip, insight, or knowledge the viewer can actually use (not empty hype, not a product pitch)
- Keep it entertaining AND useful — retention + substance together

⚠️ FORMATTING RULES:
- DO NOT use escape sequences like \\n or \\n\\n
- Use actual line breaks instead
- Make the output clean and readable
- No code formatting or markdown code blocks in dialogue

All outputs MUST be in Thai.`;

      setGeneratedPrompt(prompt);
      setIsGenerating(false);

      // เจนในเว็บต่อทันทีด้วย prompt ที่เพิ่งสร้าง + ผูกกับหัวข้อ (เพื่อบันทึกไว้ดูย้อนหลัง)
      if (autoInWeb) {
        handleGenerateInWeb(prompt, topicToUse, saveMode, saveSetId);
      }

      // Auto-scroll to prompt result
      setTimeout(() => {
        promptResultRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
      } catch (err: any) {
        console.error('[generatePrompt] error building script prompt', err);
        setAiError('สร้างสคริปต์ไม่สำเร็จ: ' + (err?.message || String(err)));
        setIsGenerating(false);
      }
    }, 500);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPrompt);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 2000);
  };

  // เปิด ChatGPT ในแท็บใหม่ + คัดลอก prompt เต็มลง clipboard (โหมด full: ผู้ใช้กด Ctrl+V เอง)
  const openInChatGPT = (prompt: string) => {
    window.open('https://chatgpt.com/', '_blank', 'noopener,noreferrer');
    // คัดลอกแบบ fire-and-forget เพื่อไม่ตัด user gesture
    navigator.clipboard.writeText(prompt).catch(() => {});
  };

  // เปิด ChatGPT พร้อมเติม prompt อัตโนมัติผ่าน ?q= → ChatGPT พิมพ์ + กดส่งให้เอง (โหมด short)
  // ถ้า URL ยาวเกินลิมิต (ผู้ใช้กรอกข้อมูลยาวมาก) จะ fallback เป็น เปิด + คัดลอกให้กด Ctrl+V แทน
  // เพื่อไม่ให้ prompt โดนตัดกลางคันแบบเงียบ ๆ
  const openInChatGPTAuto = (prompt: string) => {
    // ChatGPT ตอบ HTTP 431 เมื่อ URL ยาว (ยิ่ง login cookie ใหญ่ ลิมิตยิ่งต่ำ) จากที่วัดจริง
    // URL ~4,000 ยิงผ่านได้ auto, ~6,900 พัง 431 จึงกันขอบปลอดภัยไว้ที่ 4,000 ตัวอักษร
    const SAFE_URL_LENGTH = 4000;
    const url = `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`;
    if (url.length <= SAFE_URL_LENGTH) {
      // สั้นพอ → auto เจนเลย ไม่ต้องกด Ctrl+V
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // ยาวเกิน → เปิด ChatGPT + คัดลอกให้ แล้วบอกให้วาง (กัน error 431)
      window.open('https://chatgpt.com/', '_blank', 'noopener,noreferrer');
      navigator.clipboard.writeText(prompt)
        .then(() => alert('✅ คัดลอก prompt ให้แล้ว!\n\nไปที่แท็บ ChatGPT ที่เพิ่งเปิด แล้วกด Ctrl+V เพื่อวาง จากนั้นกด Enter ได้เลย'))
        .catch(() => {});
    }
  };

  // เจนคอนเทนต์ในเว็บด้วย Gemini (ไม่ต้องออกไป ChatGPT / ไม่ต้องวาง)
  // topicKey = หัวข้อที่กดมา → ใส่หัวข้อไว้บนสคริปต์ + บันทึกไว้ดูย้อนหลัง
  const handleGenerateInWeb = async (promptOverride?: string, topicKey?: string, saveMode?: ContentMode, saveSetId?: string) => {
    const p = promptOverride || generatedPrompt;
    if (!p) return;
    setIsAiGenerating(true);
    setAiError('');
    setAiResult('');
    try {
      const text = await generateWithAI(p, currentUsername);
      const finalText = topicKey ? `📌 หัวข้อ: ${topicKey}\n\n${text}` : text;
      setAiResult(finalText);
      if (topicKey && saveMode && saveSetId) {
        // บันทึกสคริปต์ลงในชุดที่ถูกต้อง (แทนที่ของเดิมถ้ามี) + sync Firestore
        setTopicSetsByMode((prev) => {
          const modeSets = prev[saveMode].map((set) =>
            set.id === saveSetId
              ? { ...set, scripts: [...set.scripts.filter((s) => s.topic !== topicKey), { topic: topicKey, script: finalText }] }
              : set
          );
          const next = { ...prev, [saveMode]: modeSets };
          saveTopicsToFirestore(next);
          return next;
        });
      }
      setShowScriptModal(true); // เปิด popup ดูสคริปต์เดิม
    } catch (e: any) {
      setAiError(e?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsAiGenerating(false);
    }
  };

  // เจนรายการหัวข้อในเว็บ (Authority/Story) แล้ว parse เป็นการ์ดคลิกได้
  const parseTopics = (text: string): string[] => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    // ชั้นหลัก: รับทั้ง "1. ..." / "1) ..." และ "คลิป 1: ..." (Authority สั่งให้ขึ้นต้นด้วย "คลิป N:")
    const strict = lines
      .filter((l) => /^(\d+[.)]|คลิป\s*\d+\s*[:：])/.test(l))
      .map((l) => l.replace(/^\d+[.)]\s*/, '').trim()) // ตัดเฉพาะเลขนำแบบ "1." (ปล่อย "คลิป N:" ไว้ให้ badge/handleUseTopic จัดการ)
      .filter(Boolean);
    if (strict.length >= 5) return strict;
    // ชั้นสำรอง: บางครั้ง AI ไม่ใส่เลขนำ → เก็บบรรทัดที่เป็นหัวข้อจริง (ยาวพอ ไม่ใช่หัวเรื่อง/คำเกริ่น)
    return lines
      .filter((l) => l.length > 6 && !/^[#*_=—–\-•>]/.test(l) && !/^(here|below|sure|okay|topics?|list|หัวข้อ|รายการ|ต่อไปนี้|นี่คือ)/i.test(l))
      .map((l) => l.replace(/^[-•*▪◦]\s*/, '').replace(/^\d+[.)]\s*/, '').trim())
      .filter(Boolean)
      .slice(0, 30);
  };

  // เซฟชุดหัวข้อทั้งหมด (ประวัติ) ลง Firestore (sync ข้ามอุปกรณ์)
  const saveTopicsToFirestore = async (setsObj: { authority: TopicSet[]; story: TopicSet[]; softsell: TopicSet[] }) => {
    if (!currentUsername) return;
    try {
      await setDoc(
        doc(db, 'coursedee_v1_users', currentUsername),
        { topicSetsByMode: setsObj, updatedAt: new Date().toISOString() },
        { merge: true }
      );
      mirrorToProject({ topicSetsByMode: setsObj });
    } catch (e) {
      console.error('[saveTopics] error', e);
    }
  };

  // เซฟแดชบอร์ดผลลัพธ์ลง Firestore (sync ข้ามอุปกรณ์)
  const saveProgressTracker = async (pt: ProgressTracker) => {
    setProgressTracker(pt);
    if (!currentUsername) return;
    try {
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername), { progressTracker: pt, updatedAt: new Date().toISOString() }, { merge: true });
      mirrorToProject({ progressTracker: pt });
    } catch (e) {
      console.error('[saveProgress] error', e);
    }
  };

  // เปิดฟอร์มเช็คอิน (เติมค่าเดิมจากเช็คอินล่าสุด) หรือฟอร์มตั้งค่าเริ่มต้น
  const openCheckinForm = () => {
    const last = progressTracker?.checkins?.[progressTracker.checkins.length - 1];
    const draft: Record<string, string> = {};
    PROGRESS_METRICS.forEach((m) => { draft[m.key] = last && last[m.key] !== undefined ? String(last[m.key]) : ''; });
    setCheckinDraft(draft);
  };

  // บันทึกเช็คอิน (ครั้งแรก = สร้าง tracker + ตั้งเป้าจากความท้าทาย)
  const submitCheckin = () => {
    if (!checkinDraft) return;
    const entry: ProgressCheckin = { date: new Date().toISOString() };
    PROGRESS_METRICS.forEach((m) => {
      const v = parseFloat((checkinDraft[m.key] || '').replace(/,/g, ''));
      if (!isNaN(v)) (entry as any)[m.key] = v;
    });
    if (progressTracker) {
      saveProgressTracker({ ...progressTracker, checkins: [...progressTracker.checkins, entry] });
    } else {
      // ครั้งแรก → สร้าง tracker · เดาเป้าผู้ติดตามจากข้อความความท้าทาย
      const target: Partial<Record<MetricKey, number>> = {};
      const guessed = parseTargetNum(myChallenge);
      if (guessed && /ติดตาม|follow/i.test(myChallenge)) target.followers = guessed;
      else if (guessed && /ขาย|บาท|ยอด/i.test(myChallenge)) target.sales = guessed;
      saveProgressTracker({ startDate: entry.date, target, checkins: [entry] });
    }
    setCheckinDraft(null);
  };

  const deleteLastCheckin = () => {
    if (!progressTracker || progressTracker.checkins.length === 0) return;
    if (!window.confirm('ลบผลการเช็คอินล่าสุด?')) return;
    const next = progressTracker.checkins.slice(0, -1);
    saveProgressTracker({ ...progressTracker, checkins: next });
  };

  const handleGenerateTopics = async (mode: ContentMode) => {
    if (!creatorIdentity) {
      alert('กรุณาตั้งค่าตัวตนก่อน');
      return;
    }
    setActiveTab(mode);
    // ข: มีคลิปแทรก/เคสเก่าค้างอยู่ → ถามว่าจะล้างด้วยไหม (เจนชุดใหม่ = มักเริ่มแผนใหม่)
    if (customClips.length > 0 || customCases.length > 0) {
      const clearInserted = window.confirm(`คุณมี ${customClips.length ? `คลิปเล่าเอง ${customClips.length} อัน` : ''}${customClips.length && customCases.length ? ' + ' : ''}${customCases.length ? `เคสลูกค้า ${customCases.length} เคส` : ''} แทรกอยู่ในแผน\n\nจะล้างออกด้วยไหม? (สคริปต์ของพวกมันจะหายถาวร)\n\nตกลง = ล้าง (เริ่มแผนใหม่หมดจด)\nยกเลิก = เก็บไว้`);
      if (clearInserted) {
        setCustomClips([]); saveCustomClips([]);
        setCustomCases([]); saveCustomCases([]);
      }
    }
    setIsTopicGenerating(true);
    setTopicError('');
    try {
      const existingTopics = topicSetsByMode[mode].flatMap((s) => s.topics); // หัวข้อเก่าทุกชุดของสายนี้ → กันซ้ำ
      const startClip = existingTopics.length + 1; // ชุดใหม่ต่อเลขคลิปจากของเดิม (เช่น ชุด 2 = คลิป 31-60)
      // ⚠️ ต้องส่ง challengeMode/myChallenge ด้วย — ไม่งั้นโหมดเส้นเรื่อง 4 เฟสไม่ทำงาน
      const prompt = generateTopicsPrompt({ whatIDo, myExpertise, myProof, myProduct, myAudience, myChallenge, challengeMode } as any, mode, existingTopics, startClip);
      // ลองซ้ำได้ถึง 3 ครั้ง ถ้า parse ได้ 0 หัวข้อ หรือเกิด error ชั่วคราว (internal/timeout)
      let list: string[] = [];
      let lastErr: any = null;
      for (let attempt = 0; attempt < 3 && list.length === 0; attempt++) {
        try {
          const text = await generateWithAI(prompt, currentUsername);
          list = parseTopics(text);
        } catch (err) { lastErr = err; }
      }
      if (list.length === 0) throw lastErr || new Error('ไม่ได้หัวข้อ กรุณาลองใหม่');
      const newSet: TopicSet = {
        id: `set_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        createdAt: new Date().toISOString(),
        topics: list,
        scripts: [],
      };
      const next = { ...topicSetsByMode, [mode]: [...topicSetsByMode[mode], newSet] };
      setTopicSetsByMode(next);
      setActiveSetIdx((idx) => ({ ...idx, [mode]: next[mode].length - 1 })); // เปิดชุดใหม่ล่าสุด
      saveTopicsToFirestore(next);
    } catch (e: any) {
      setTopicError(e?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setIsTopicGenerating(false);
    }
  };

  // พอบันทึกตัวตน → เจนหัวข้อทั้ง 3 สายให้เลย (เฉพาะสายที่ยังไม่มีหัวข้อ) จะได้ไม่เสียเวลา
  // resetAll=true → ล้างของเก่าทั้งหมดก่อน แล้วเจนใหม่ทั้ง 3 สาย (ใช้ตอนเปลี่ยนตัวตนใหม่)
  const autoGenerateBothSets = async (resetAll = false) => {
    const identity = { whatIDo, myExpertise, myProof, myProduct, myAudience, myChallenge, challengeMode } as any; // challengeMode → เส้นเรื่อง 4 เฟส
    const emptySets = { authority: [] as TopicSet[], story: [] as TopicSet[], softsell: [] as TopicSet[] };
    const baseSets = resetAll ? emptySets : topicSetsByMode;
    if (resetAll) { // เปลี่ยนตัวตน = เริ่มแผนใหม่ → ล้างคลิปแทรก/เคสเก่าด้วย
      setCustomClips([]); saveCustomClips([]);
      setCustomCases([]); saveCustomCases([]);
    }
    if (resetAll) setTopicSetsByMode(emptySets); // ล้างทันทีเพื่อให้ตัวเลขรีเซ็ตเป็น 0
    const modesToGen = (['authority', 'story', 'softsell'] as const).filter((m) => baseSets[m].length === 0);
    if (modesToGen.length === 0) return;
    setActiveTab('authority');
    setIsTopicGenerating(true);
    setTopicError('');
    try {
      const generated: Partial<Record<ContentMode, TopicSet>> = {};
      for (const m of modesToGen) {
        // ลองเจนซ้ำได้ถึง 3 ครั้ง ถ้า parse ได้ 0 หัวข้อ หรือเกิด error ชั่วคราว (Authority/Story บางครั้งพลาด)
        let list: string[] = [];
        for (let attempt = 0; attempt < 3 && list.length === 0; attempt++) {
          try {
            const text = await generateWithAI(generateTopicsPrompt(identity, m, baseSets[m].flatMap((s) => s.topics)), currentUsername);
            list = parseTopics(text);
          } catch { /* ลองใหม่รอบถัดไป */ }
        }
        if (list.length) {
          generated[m] = {
            id: `set_${Date.now()}_${m}_${Math.random().toString(36).slice(2)}`,
            createdAt: new Date().toISOString(),
            topics: list,
            scripts: [],
          };
        }
      }
      setTopicSetsByMode((prev) => {
        // resetAll → สร้างจากฐานว่าง (ล้างของเก่า) · ไม่งั้น → ต่อท้ายของเดิม
        const start = resetAll ? emptySets : prev;
        const next = { authority: [...start.authority], story: [...start.story], softsell: [...start.softsell] };
        if (generated.authority) next.authority.push(generated.authority);
        if (generated.story) next.story.push(generated.story);
        if (generated.softsell) next.softsell.push(generated.softsell);
        saveTopicsToFirestore(next);
        return next;
      });
    } catch (e: any) {
      setTopicError(e?.message || 'เจนหัวข้ออัตโนมัติไม่สำเร็จ — กดเจนเองได้');
    } finally {
      setIsTopicGenerating(false);
    }
  };

  // ลบชุดหัวข้อ (พร้อมสคริปต์ในชุดนั้น)
  const handleDeleteSet = (mode: ContentMode, setId: string) => {
    if (!window.confirm('ลบชุดหัวข้อนี้? (สคริปต์ที่เจนไว้ในชุดนี้จะหายด้วย)')) return;
    const remaining = topicSetsByMode[mode].filter((s) => s.id !== setId);
    const next = { ...topicSetsByMode, [mode]: remaining };
    setTopicSetsByMode(next);
    setActiveSetIdx((idx) => ({ ...idx, [mode]: Math.max(0, Math.min(idx[mode], remaining.length - 1)) }));
    saveTopicsToFirestore(next);
    setAiResult('');
  };

  // คลิกหัวข้อ → ถ้าเจนแล้ว = เปิดดูย้อนหลัง (ไม่เสียเงิน) · ถ้ายัง = เจนใหม่ (ในชุดที่เปิดอยู่)
  const handleUseTopic = (topic: string) => {
    if (isGenerating || isAiGenerating) return;
    const mode = activeTab;
    const set = topicSetsByMode[mode][activeSetIdx[mode]];
    if (!set) return;
    const existing = set.scripts.find((s) => s.topic === topic);
    if (existing) {
      // เปิดดูสคริปต์เดิม ไม่เรียก AI ซ้ำ
      setAiError('');
      setAiResult(existing.script);
      setSelectedTopic(topic);
      setShowScriptModal(true); // เปิด popup ดูสคริปต์เดิม
      return;
    }
    setSelectedTopic(topic);
    // เลขคลิปแบบต่อเนื่องข้ามชุด (ชุด 2 = คลิป 31-60) + จำนวนคลิปรวมทั้งซีรีส์
    const clipOffset = topicSetsByMode[mode].slice(0, activeSetIdx[mode]).reduce((sum, s) => sum + s.topics.length, 0);
    const totalClips = topicSetsByMode[mode].reduce((sum, s) => sum + s.topics.length, 0);
    const clipNumber = clipOffset + set.topics.indexOf(topic) + 1;
    generatePrompt(true, topic, mode, set.id, clipNumber, totalClips);
  };

  // สร้างสคริปต์จาก "แผน 60 วัน" — ระบุ mode ตรง ๆ (ไม่อิง activeTab) หาชุด+เลขคลิปให้เอง
  const handlePlanGenerate = (topic: string, mode: ContentMode) => {
    if (isGenerating || isAiGenerating) return;
    const modeSets = topicSetsByMode[mode];
    // เลขคลิปนับ "ภายในชุดของตัวเอง" (แต่ละชุด = ซีรีส์ 1-30 ของตัวเอง ไม่นับข้ามชุดเก่า)
    let setId = '', clipNumber = 0, totalClips = 0;
    for (const s of modeSets) {
      const idx = s.topics.indexOf(topic);
      if (idx >= 0) { setId = s.id; clipNumber = idx + 1; totalClips = s.topics.length; break; }
    }
    if (!setId) return;
    setActiveTab(mode); // ให้ panel ผลลัพธ์อยู่ในบริบทสายเดียวกัน
    const set = modeSets.find((s) => s.id === setId)!;
    const existing = set.scripts.find((s) => s.topic === topic);
    setSelectedTopic(topic);
    if (existing) { // มีสคริปต์แล้ว → เปิดดู ไม่เรียก AI ซ้ำ
      setAiError('');
      setAiResult(existing.script);
      setShowScriptModal(true); // เปิด popup ดูสคริปต์เดิม
      return;
    }
    generatePrompt(true, topic, mode, setId, clipNumber, totalClips);
  };

  // สร้างแผนถ่ายทำ 60 วัน: สลับ Story/Authority วันเว้นวัน + แทรก Soft-sell เป็น "คลิปที่ 2" กระจายทุก ~20 วัน
  const buildDayPlan = () => {
    // ใช้ "ชุดที่เลือกอยู่" (activeSetIdx = ชุดล่าสุดโดยปกติ) — เจนชุดใหม่แล้วแผนต้องเปลี่ยนตาม ไม่ใช่ค้างชุดเก่า
    const pick = (mode: ContentMode) => topicSetsByMode[mode][activeSetIdx[mode]]?.topics ?? topicSetsByMode[mode][topicSetsByMode[mode].length - 1]?.topics ?? [];
    const story = pick('story');
    const authority = pick('authority');
    const softsell = pick('softsell');
    const pairs = Math.min(story.length, authority.length, 30); // สูงสุด 30 คู่ = 60 วัน
    const days: { day: number; main: { topic: string; mode: ContentMode }; sell?: { topic: string; mode: ContentMode } }[] = [];
    for (let i = 0; i < pairs; i++) {
      days.push({ day: 2 * i + 1, main: { topic: story[i], mode: 'story' } });      // วันคี่ = Story
      days.push({ day: 2 * i + 2, main: { topic: authority[i], mode: 'authority' } }); // วันคู่ = Authority
    }
    // แทรก Soft-sell เป็น "คลิปเสริม (ไม่บังคับ)" กระจายทั่วแผน — ใช้ครบเท่าที่มี (สูงสุดวันละ 1) ผู้ใช้เลือกเองว่าวันไหนจะลง
    const total = days.length;
    const ssCount = Math.min(softsell.length, total);
    for (let i = 0; i < ssCount && total > 0; i++) {
      const dayIdx = Math.floor(((i + 0.5) * total) / ssCount); // กระจายเท่า ๆ กันทั้งแผน
      if (days[dayIdx] && !days[dayIdx].sell) days[dayIdx].sell = { topic: softsell[i], mode: 'softsell' };
    }
    return days;
  };

  // เช็คว่าหัวข้อนี้เจนสคริปต์แล้วยัง (สำหรับแสดงสถานะในแผน)
  const isScriptDone = (topic: string, mode: ContentMode) =>
    topicSetsByMode[mode].some((s) => s.scripts.some((sc) => sc.topic === topic));

  // 🖊️ เซฟคลิปเล่าเรื่องเอง (main doc + mirror โปรเจกต์)
  const saveCustomClips = async (list: CustomClip[]) => {
    if (!currentUsername) return;
    try {
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername), { customClips: list, updatedAt: new Date().toISOString() }, { merge: true });
      mirrorToProject({ customClips: list });
    } catch (e) { console.error('[customClips] save error', e); }
  };

  // 🖊️ เจนสคริปต์จากเรื่องที่ผู้ใช้เล่าเอง → แทรกในแผนต่อจากวันล่าสุดที่เจนสคริปต์ไปแล้ว
  const handleGenCustomClip = async () => {
    const story = customStoryDraft.trim();
    if ((!story && customAttach.length === 0) || isCustomGenerating) return;
    if (!creatorIdentity) { alert('กรุณาตั้งค่าตัวตนก่อนครับ'); return; }
    setIsCustomGenerating(true);
    try {
      const ci: any = creatorIdentity;
      const creatorGender = detectThaiGender(ci.whatIDo, ci.myAudience, ci.myExpertise, ci.myChallenge, ci.myProduct, identityText);
      const genderTone = creatorGender === 'male'
        ? '\n🚹 ผู้พูดเป็น "ผู้ชาย" → บทพูด/แคปชั่นลงท้าย "ครับ" เสมอ ❌ ห้าม "ค่ะ/คะ"'
        : creatorGender === 'female'
        ? '\n🚺 ผู้พูดเป็น "ผู้หญิง" → บทพูด/แคปชั่นลงท้าย "ค่ะ/คะ" เสมอ ❌ ห้าม "ครับ"'
        : '';
      const challengeBlock = ci.challengeMode && ci.myChallenge ? `
[🔥 โหมดลุยชาเลนจ์จริง — ผู้พูดกำลังลุยภารกิจ: "${challengeEssence(ci.myChallenge)}"]
- เล่าเรื่องนี้ผ่านมุม "คนที่กำลังลุยภารกิจนี้อยู่จริง" — เปิดด้วยการทวนภารกิจสั้น ๆ 1 ประโยคแบบตื่นเต้น แล้วเข้าเรื่องที่เกิดขึ้น
- โทน UNDERDOG ถ่อมตัว: ยอมรับความพัง/ความเฟล/ความไม่รู้ตรง ๆ ห้ามพูดมั่นใจว่าทำได้แน่ ห้ามอวด — คนดูเชียร์คนกล้า ไม่ใช่คนเก่ง` : '';
      const prevLog = customClips
        .slice(-8)
        .map((c) => `- [${new Date(c.createdAt).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}] ${c.title}: ${c.story.slice(0, 250)}`)
        .join('\n');
      const prevBlock = prevLog ? `
[📓 เหตุการณ์ก่อนหน้าที่เกิดขึ้นแล้วจริง — ให้สคริปต์สอดคล้องกับข้อเท็จจริงเหล่านี้]
${prevLog}
- อ้างถึงแบบธรรมชาติได้สั้น ๆ เฉพาะที่เกี่ยวกับเรื่องใหม่นี้จริง ๆ (ในฐานะเหตุการณ์ในชีวิต) ❌ ห้ามอ้างในฐานะ "คลิป/วิดีโอก่อนหน้า"
` : '';
      const prompt = `คุณคือนักเขียนสคริปต์ TikTok มืออาชีพ จงเขียนสคริปต์วิดีโอ 1 คลิป จาก "เรื่องจริงที่เพิ่งเกิดขึ้นกับเจ้าของช่อง" ที่เล่ามาด้านล่าง

[ตัวตนผู้พูด — ใช้เฉพาะข้อมูลนี้ ห้ามสมมติเพิ่ม]
ฉันทำ: ${ci.whatIDo}
กลุ่มเป้าหมาย: ${ci.myAudience || '-'}
จุดแข็ง: ${ci.myExpertise || '-'}${ci.myProduct ? `\nสินค้า/บริการ: ${ci.myProduct}` : ''}
${challengeBlock}
${prevBlock}
[เรื่องจริงที่เกิดขึ้น — นี่คือแกนของคลิปนี้]
"""
${story || '(เจ้าของช่องไม่ได้พิมพ์เล่า — ให้ดูจากรูปที่แนบมาเป็นหลัก)'}
"""
${customAttach.length ? `
[🖼️ รูปแนบ ${customAttach.length} รูป — ดูรูปจริงที่แนบมาด้วย]
- รูปนี้คือของจริงของเจ้าของช่อง (เช่น ผลงานที่ทำเสร็จ / สินค้า / หน้างาน) — จง "ดูรูปอย่างละเอียด" แล้วใช้สิ่งที่เห็นจริงในรูปมาเขียนสคริปต์ (สี รูปทรง องค์ประกอบ จุดเด่นที่เห็น)
- ฉาก 🎥 ให้สั่งถ่ายอ้างอิงจากของจริงในรูป (เช่น "ซูมช้า ๆ ที่หน้าเมนูสีเขียวของเว็บ") ไม่ใช่บรรยายลอย ๆ
- ถ้าเรื่องที่เล่า+รูปเข้าทางรีวิว/โชว์ผลงาน ให้เขียนแนวรีวิวเล่าเบื้องหลังแบบถ่อมตัว ไม่อวด` : ''}

🚨 กฎเหล็กที่สุด: ต้องใช้ "เรื่องที่เล่ามานี้${customAttach.length ? ' + สิ่งที่เห็นจริงในรูปแนบ' : ''}" เป็นแกนของคลิป 100% — ห้ามแต่งเหตุการณ์เพิ่ม ห้ามเปลี่ยนข้อเท็จจริง ห้ามเติมรายละเอียดที่ไม่ได้เล่า/ไม่เห็นในรูป สิ่งที่เติมได้มีแค่ "วิธีเล่า" (ฮุก จังหวะ มุมกล้อง การเรียงลำดับ)

รูปแบบสคริปต์ = "ถ่ายบรรยากาศ + พากย์เสียง": ทุกฉากมี 🎥 ถ่าย: (บอกชัดว่าถ่ายอะไร ถ่ายเองด้วยมือถือได้) + 🎙️ พากย์: (บทพูดธรรมชาติเหมือนเล่าให้เพื่อนฟัง) + ป้าย [หน้ากล้อง] หรือ [B-roll] — โผล่หน้าคนเฉพาะเปิดคลิป/จุดพีค/ปิดท้าย
โทน: คุยกับคนดูด้วยคำว่า "คุณ" เป็นธรรมชาติ ถ่อมตัว ไม่ทำตัวรู้ดี${genderTone}
คลิปนี้ต้องดูรู้เรื่องจบในตัวเอง — ห้ามอ้างอิงคลิปอื่น ("คลิปที่แล้ว/คลิปหน้า")

จงตอบเรียงตามโครงนี้เป๊ะ ๆ (ภาษาไทยทั้งหมด ไม่ใช้ \\n ใช้ขึ้นบรรทัดจริง):
📌 หัวข้อ: <ตั้งชื่อคลิปสั้น ๆ ดึงดูด สรุปจากเรื่องที่เล่า>
(เว้นบรรทัด)
🃏 การ์ดสรุป
<คนดูจะได้อะไร 1-2 บรรทัด>
🎯 ดึงเอ็นเกจเมนต์: <วิธีชวนคอมเมนต์/แชร์>
👀 คนดูจะได้: <คุณค่าหลัก>
(เว้นบรรทัด)
🎬 HOOK (เลือก 1 จาก 3)
1. ...
2. ...
3. ...
(เว้นบรรทัด)
📝 สคริปต์เต็ม (พร้อมใช้)
ฉาก 1 ... ถึงฉากสุดท้าย (8-12 ฉาก · เป้าคลิป 60-90 วิ = พากย์รวม ~180-260 คำ · แต่ละฉากพากย์ 2-4 ประโยคมีเนื้อ ห้ามสั้นฉากละบรรทัด · ห้ามน้ำ)
(เว้นบรรทัด)
📱 แคปชั่นลงโพสต์
<แคปชั่น>
(เว้นบรรทัด)
#️⃣ แฮชแท็ก
<10-15 แฮชแท็ก>`;
      const text = await generateWithAI(prompt, currentUsername, undefined, customAttach.length ? customAttach : undefined);
      const m = text.match(/📌\s*หัวข้อ\s*[:：]\s*(.+)/);
      const title = (m ? m[1].trim() : (story || 'คลิปจากรูปที่แนบ').slice(0, 45)).slice(0, 80);
      // แทรกต่อจากวันล่าสุดที่เจนสคริปต์คอนเทนต์หลักไปแล้ว (0 = ยังไม่เจนเลย → ไว้บนสุด)
      const plan = buildDayPlan();
      let lastDone = 0;
      for (const d of plan) if (isScriptDone(d.main.topic, d.main.mode)) lastDone = Math.max(lastDone, d.day);
      const clip: CustomClip = { id: `cc_${Date.now()}`, title, story, script: text, afterDay: lastDone, createdAt: new Date().toISOString() };
      setCustomClips((prev) => { const next = [...prev, clip]; saveCustomClips(next); return next; });
      setCustomStoryDraft(''); setCustomAttach([]); setShowCustomClipBox(false);
      setAiError(''); setAiResult(text); setSelectedTopic(title);
      setShowScriptModal(true);
    } catch (e: any) {
      alert(e?.message || 'เจนสคริปต์ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally { setIsCustomGenerating(false); }
  };

  // เปิดดูสคริปต์คลิปเล่าเรื่องเองย้อนหลัง
  const handleViewCustomClip = (c: CustomClip) => {
    setAiError(''); setAiResult(c.script); setSelectedTopic(c.title);
    setShowScriptModal(true);
  };

  // ลบคลิปเล่าเรื่องเอง
  const handleDeleteCustomClip = (id: string) => {
    if (!window.confirm('ลบคลิปนี้ออกจากแผน? (สคริปต์จะหายถาวร)')) return;
    setCustomClips((prev) => { const next = prev.filter((c) => c.id !== id); saveCustomClips(next); return next; });
  };

  // 📁 เซฟเคสลูกค้า
  const saveCustomCases = async (list: CustomCase[]) => {
    if (!currentUsername) return;
    try {
      await setDoc(doc(db, 'coursedee_v1_users', currentUsername), { customCases: list, updatedAt: new Date().toISOString() }, { merge: true });
      mirrorToProject({ customCases: list });
    } catch (e) { console.error('[customCases] save error', e); }
  };

  // 📁 เปิดเคสลูกค้า: เล่าโจทย์ → AI แตกเป็นมินิซีรีส์ 4-6 คลิป (ชื่อ+โครงย่อ) แทรกในแผน
  const handleCreateCase = async () => {
    const brief = caseBriefDraft.trim();
    if (!brief || isCaseGenerating) return;
    if (!creatorIdentity) { alert('กรุณาตั้งค่าตัวตนก่อนครับ'); return; }
    setIsCaseGenerating(true);
    try {
      const ci: any = creatorIdentity;
      const casePrompt = `You are a TikTok reality-series director. อาชีพของครีเอเตอร์: ${ci.whatIDo}${ci.challengeMode && ci.myChallenge ? ` · กำลังลุยภารกิจ: "${challengeEssence(ci.myChallenge)}"` : ''}

ครีเอเตอร์เพิ่งได้ "เคสงานจริงจากลูกค้า" และอยากทำเป็นมินิซีรีส์หลายคลิป (เหมือนสร้างบ้าน 1 หลัง = 5 คลิป ตั้งแต่รับโจทย์จนส่งมอบ):

[โจทย์เคสจริงที่ครีเอเตอร์เล่ามา]
"""
${brief}
"""

จงแตกเคสนี้เป็นมินิซีรีส์ 4-6 คลิป ไล่ตามลำดับเวลาจริงของงาน: รับโจทย์/ทำความเข้าใจลูกค้า → วางแผน/เริ่มทำ → ปัญหา/จุดพลิกระหว่างทาง → เทคนิค/สิ่งที่ช่วยลูกค้าได้ → ส่งมอบ+ผลลัพธ์ (ปรับจำนวน/ลำดับให้เหมาะกับเคสจริง ห้ามแต่งข้อเท็จจริงที่ไม่ได้เล่า — ช่องที่ยังไม่เกิด (เช่น ยังทำไม่เสร็จ) ให้ตั้งเป็นหัวข้อที่ "จะตามไปดู" ได้)

ตอบเป็น raw JSON เท่านั้น (no markdown, start with "{" immediately):
{"case_name":"<ชื่อเคสสั้น ๆ ภาษาไทย เช่น 'เว็บร้านเสริมสวย Nisa La Beauty'>","clips":[{"title":"<ชื่อคลิปสไตล์ TikTok ชวนลุ้น ไม่เกิน 15 คำ>","outline":"<โครงย่อ 1-2 ประโยค: คลิปนี้เล่า/โชว์อะไร>"}]}`;
      const raw = await generateWithAI(casePrompt, currentUsername, 0.3);
      let parsed: any = null;
      try {
        const t = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '').trim();
        const obj = t.match(/\{[\s\S]*\}/);
        parsed = JSON.parse((obj ? obj[0] : t).replace(/,\s*([\]}])/g, '$1'));
      } catch { /* ตกไป error ด้านล่าง */ }
      const clips = Array.isArray(parsed?.clips) ? parsed.clips.filter((c: any) => c?.title).slice(0, 6) : [];
      if (!clips.length) throw new Error('AI แตกเคสไม่สำเร็จ ลองกดใหม่อีกครั้ง');
      const plan = buildDayPlan();
      let lastDone = 0;
      for (const d of plan) if (isScriptDone(d.main.topic, d.main.mode)) lastDone = Math.max(lastDone, d.day);
      const cs: CustomCase = {
        id: `case_${Date.now()}`, name: String(parsed.case_name || 'เคสลูกค้า').slice(0, 80), brief,
        afterDay: lastDone, createdAt: new Date().toISOString(),
        clips: clips.map((c: any) => ({ title: String(c.title).slice(0, 120), outline: String(c.outline || '').slice(0, 300), script: '' })),
      };
      setCustomCases((prev) => { const next = [...prev, cs]; saveCustomCases(next); return next; });
      setCaseBriefDraft(''); setShowCaseBox(false);
    } catch (e: any) {
      alert(e?.message || 'สร้างเคสไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally { setIsCaseGenerating(false); }
  };

  // 📁 เจนสคริปต์คลิปหนึ่งในเคส (หรือเปิดดูถ้ามีแล้ว)
  const handleCaseClip = async (cs: CustomCase, idx: number) => {
    const clip = cs.clips[idx];
    if (!clip) return;
    if (clip.script) { setAiError(''); setAiResult(clip.script); setSelectedTopic(clip.title); setShowScriptModal(true); return; }
    const busyKey = `${cs.id}_${idx}`;
    if (caseClipBusy) return;
    setCaseClipBusy(busyKey);
    try {
      const ci: any = creatorIdentity;
      const creatorGender = detectThaiGender(ci.whatIDo, ci.myAudience, ci.myExpertise, ci.myChallenge, ci.myProduct, identityText);
      const genderTone = creatorGender === 'male' ? '\n🚹 ผู้พูดเป็น "ผู้ชาย" → ลงท้าย "ครับ" เสมอ ❌ ห้าม "ค่ะ/คะ"' : creatorGender === 'female' ? '\n🚺 ผู้พูดเป็น "ผู้หญิง" → ลงท้าย "ค่ะ/คะ" เสมอ ❌ ห้าม "ครับ"' : '';
      const doneBefore = cs.clips.slice(0, idx).map((c, i) => `- ตอนที่ ${i + 1}: ${c.title}`).join('\n');
      const prompt = `คุณคือนักเขียนสคริปต์ TikTok มืออาชีพ จงเขียนสคริปต์ 1 คลิป ซึ่งเป็น "ตอนที่ ${idx + 1} จาก ${cs.clips.length}" ของมินิซีรีส์เคสงานจริง

[ตัวตนผู้พูด — ใช้เฉพาะข้อมูลนี้ ห้ามสมมติเพิ่ม]
ฉันทำ: ${ci.whatIDo}
กลุ่มเป้าหมาย: ${ci.myAudience || '-'}
จุดแข็ง: ${ci.myExpertise || '-'}
${ci.challengeMode && ci.myChallenge ? `กำลังลุยภารกิจจริง: "${challengeEssence(ci.myChallenge)}" — โทน UNDERDOG ถ่อมตัว ยอมรับความพัง/ความไม่รู้ตรง ๆ ห้ามอวด ห้ามการันตีว่าสำเร็จ` : ''}

[เคสงานจริง: ${cs.name}]
โจทย์เคสตามที่เจ้าของช่องเล่า: """${cs.brief}"""
${doneBefore ? `ตอนก่อนหน้าในซีรีส์เคสนี้ (เกิดขึ้นแล้ว):\n${doneBefore}` : ''}

[คลิปนี้ = ตอนที่ ${idx + 1}: ${clip.title}]
โครงตอนนี้: ${clip.outline || '-'}

🚨 กฎเหล็ก: ใช้ข้อเท็จจริงจากโจทย์เคสเท่านั้น ห้ามแต่งรายละเอียดงาน/ตัวเลข/เหตุการณ์ที่ไม่ได้เล่า (ส่วนที่ยังไม่รู้ ให้เล่าแบบ "จะพาไปดู/รอลุ้นด้วยกัน")
- เล่าในฐานะ "เหตุการณ์จริงที่กำลังทำอยู่" — ทวนบริบทเคสสั้น ๆ 1 ประโยคตอนเปิด (เช่น "ตอนนี้ผมกำลังทำ[งาน]ให้ลูกค้ารายแรกอยู่ครับ") เพื่อให้คนที่เพิ่งเห็นคลิปนี้ครั้งแรกดูรู้เรื่อง — ❌ ห้ามพูดว่า "คลิปที่แล้ว/ตอนที่แล้ว" ในฐานะวิดีโอ
- สอดแทรกความรู้/เทคนิคจริงของอาชีพนี้ให้คนดูได้ประโยชน์ ไม่ใช่แค่รายงานงาน
รูปแบบสคริปต์ = "ถ่ายบรรยากาศ + พากย์เสียง": ทุกฉากมี 🎥 ถ่าย: + 🎙️ พากย์: + ป้าย [หน้ากล้อง]/[B-roll] — โผล่หน้าเฉพาะเปิด/พีค/ปิด
โทน: คุยกับคนดูด้วย "คุณ" ธรรมชาติ ถ่อมตัว${genderTone}

จงตอบเรียงตามโครงนี้ (ภาษาไทยทั้งหมด ใช้ขึ้นบรรทัดจริง):
📌 หัวข้อ: ${clip.title}
🃏 การ์ดสรุป (+🎯 ดึงเอ็นเกจเมนต์ +👀 คนดูจะได้)
🎬 HOOK (เลือก 1 จาก 3)
📝 สคริปต์เต็ม (ฉาก 1 ถึงฉากสุดท้าย 8-12 ฉาก · เป้าคลิป 60-90 วิ = พากย์รวม ~180-260 คำ · แต่ละฉากพากย์ 2-4 ประโยคมีเนื้อ ห้ามน้ำ)
📱 แคปชั่นลงโพสต์
#️⃣ แฮชแท็ก`;
      const text = await generateWithAI(prompt, currentUsername);
      setCustomCases((prev) => {
        const next = prev.map((c) => c.id === cs.id ? { ...c, clips: c.clips.map((cl, i) => i === idx ? { ...cl, script: text } : cl) } : c);
        saveCustomCases(next);
        return next;
      });
      setAiError(''); setAiResult(text); setSelectedTopic(clip.title);
      setShowScriptModal(true);
    } catch (e: any) {
      alert(e?.message || 'เจนสคริปต์ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally { setCaseClipBusy(''); }
  };

  // 📁 ลบเคส
  const handleDeleteCase = (id: string) => {
    if (!window.confirm('ลบเคสนี้ทั้งชุดออกจากแผน? (สคริปต์ทุกตอนของเคสจะหายถาวร)')) return;
    setCustomCases((prev) => { const next = prev.filter((c) => c.id !== id); saveCustomCases(next); return next; });
  };

  // กดปุ่มเลือกโหมด (Authority / Story) → เจนใน ChatGPT
  // ไฮบริด: ถ้า URL สั้นพอ (ไม่เกินลิมิต 431) → auto ยิงผ่าน ?q= ไม่ต้องกด Ctrl+V
  //         ถ้ายาวเกิน → คัดลอกให้วางแทน (กัน error 431)
  const handleGenerateInChatGPT = (mode: ContentMode) => {
    const identity = { whatIDo, myExpertise, myProof, myProduct, myAudience };
    const prompt = mode === 'authority'
      ? generateAuthorityPromptShort(identity)
      : generateStoryPromptShort(identity);
    openInChatGPTAuto(prompt);
  };

  const clearAll = () => {
    setSelectedTopic('');
    setSelectedFormat('');
    setGeneratedPrompt('');
    localStorage.removeItem('coursedee_v1_selectedTopic');
    localStorage.removeItem('coursedee_v1_selectedFormat');
    localStorage.removeItem('coursedee_v1_generatedPrompt');
  };

  // บันทึกตัวตน (ใช้กับ wizard modal ดีไซน์ใหม่)
  const handleSaveIdentity = async () => {
    if (!whatIDo.trim() || !myAudience.trim() || !myExpertise.trim()) {
      alert('กรุณากรอกข้อมูลพื้นฐานให้ครบ (ธุรกิจ / กลุ่มเป้าหมาย / จุดแข็ง)');
      setCurrentStep(1);
      return;
    }
    if (myGoals.length === 0) {
      alert('กรุณาเลือกเป้าหมายอย่างน้อย 1 อัน');
      setCurrentStep(2);
      return;
    }
    setIsSaving(true);
    try {
      const derivedProof = [
        myChallenge.trim() ? `ความท้าทายภายใน 60 วัน: ${myChallenge.trim()}` : '',
        myGoals.length ? deriveProofFromGoals(myGoals) : '',
      ].filter(Boolean).join(' | ') || myProof;
      const identityData = {
        whatIDo, myAudience, myExpertise, myProof: derivedProof, myGoals, myChallenge, challengeType, challengeMode, myProduct,
        savedAt: new Date().toISOString(),
      };
      const userDocRef = doc(db, 'coursedee_v1_users', currentUsername);
      await setDoc(userDocRef, { creatorIdentity: identityData, updatedAt: new Date().toISOString() }, { merge: true });
      mirrorToProject({ creatorIdentity: identityData });
      localStorage.setItem(`coursedee_creator_identity_${currentUsername}`, JSON.stringify(identityData));
      setCreatorIdentity(identityData);
      setIdentityText(`ฉันทำ: ${whatIDo}\nความเชี่ยวชาญของฉัน: ${myExpertise}\nฉันกำลังพิสูจน์: ${derivedProof}${myProduct ? `\nสินค้าของฉัน: ${myProduct}` : ''}`);
      setCurrentStep(0);
      // ถ้ามีข้อมูลเดิมอยู่แล้ว ถามว่าจะล้างของเก่าแล้วเริ่มใหม่ตามตัวตนล่าสุดไหม (กันหัวข้อ/ผลลัพธ์ของตัวตนเก่าปนมา)
      const existingTotal = topicSetsByMode.authority.reduce((n, s) => n + s.topics.length, 0)
        + topicSetsByMode.story.reduce((n, s) => n + s.topics.length, 0)
        + topicSetsByMode.softsell.reduce((n, s) => n + s.topics.length, 0);
      const hasTracker = !!(progressTracker && progressTracker.checkins.length > 0);
      const resetAll = existingTotal > 0 || hasTracker
        ? window.confirm(`คุณมีข้อมูลเดิมอยู่แล้ว${existingTotal > 0 ? ` (${existingTotal} หัวข้อ)` : ''}${hasTracker ? ' + ผลลัพธ์ที่เช็คอินไว้' : ''}\n\nต้องการล้าง "หัวข้อ + สคริปต์ + ผลลัพธ์" เก่าทั้งหมด แล้วเริ่มใหม่ตามตัวตนล่าสุดไหม?\n\nตกลง = ล้างแล้วเริ่มใหม่ (แนะนำเมื่อเปลี่ยนตัวตน)\nยกเลิก = เก็บของเดิมไว้`)
        : false;
      if (resetAll) {
        // ล้างแดชบอร์ดผลลัพธ์ด้วย (ให้ตรงกับตัวตนใหม่)
        setProgressTracker(null);
        setCheckinDraft(null);
        try { await setDoc(doc(db, 'coursedee_v1_users', currentUsername), { progressTracker: null, updatedAt: new Date().toISOString() }, { merge: true }); } catch {}
      }
      autoGenerateBothSets(resetAll);
    } catch (error) {
      console.error('Error saving identity:', error);
      alert('❌ เกิดข้อผิดพลาดในการบันทึก');
    } finally {
      setIsSaving(false);
    }
  };

  // บันทึกคอนเทนต์เป็น PDF (html2canvas → jsPDF · รองรับไทย + หลายหน้า)
  const handleSavePdf = async () => {
    const el = scriptContentRef.current;
    if (!el || savingPdf) return;
    setSavingPdf(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const canvas = await html2canvas(el, { backgroundColor: theme === 'dark' ? '#12101f' : '#ffffff', scale: 2, useCORS: true });
      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      const margin = 24;
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW - margin * 2;
      const pxPerPt = canvas.width / imgW;
      const pageContentHpx = (pageH - margin * 2) * pxPerPt;
      let srcY = 0;
      let first = true;
      while (srcY < canvas.height) {
        const sliceHpx = Math.min(pageContentHpx, canvas.height - srcY);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceHpx;
        slice.getContext('2d')!.drawImage(canvas, 0, srcY, canvas.width, sliceHpx, 0, 0, canvas.width, sliceHpx);
        if (!first) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', margin, margin, imgW, sliceHpx / pxPerPt);
        srcY += sliceHpx;
        first = false;
      }
      pdf.save(`coursedee-${activeTab}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error('[PDF] error', e);
      alert('บันทึก PDF ไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setSavingPdf(false);
    }
  };

  // Login screen
  if (!user) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4 bg-gradient-to-br from-slate-950 via-blue-950 to-purple-950">
        <div className="w-full max-w-md relative z-10">
          <div className="bg-slate-950/40 backdrop-blur-sm rounded-2xl shadow-xl shadow-blue-500/20 p-8 border border-blue-400/40">
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-2xl shadow-lg">
                <Lock className="w-8 h-8 text-white" />
              </div>
            </div>
            
            <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              AIForYou V1
            </h1>
            <p className="text-gray-300 text-center mb-8">
              Content Generator
            </p>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-blue-400/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                  placeholder="Username"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-blue-400/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                  placeholder="Password"
                  required
                />
              </div>

              {loginError && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 text-red-300 text-sm">
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 text-white font-semibold py-3 rounded-xl hover:from-blue-600 hover:via-purple-600 hover:to-blue-700 transition-all shadow-lg"
              >
                เข้าสู่ระบบ
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // (เดิม) หน้าตั้งค่าตัวตนแบบเต็มหน้า — ปิดแล้ว เปลี่ยนไปใช้ wizard modal ในดีไซน์ใหม่ (โค้ดด้านล่างเป็น dead code ที่ esbuild ตัดทิ้ง)
  if (false && user) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="fixed inset-0 z-0">
          {/* Mobile background */}
          <div 
            className="absolute inset-0 bg-center bg-no-repeat bg-cover md:hidden"
            style={{
              backgroundImage: 'url(/Coursedee-bg-mobile-V1.jpg)',
              backgroundColor: '#0f172a'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-blue-950/50 to-purple-950/70" />
          </div>
          {/* Desktop background */}
          <div 
            className="hidden md:block absolute inset-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: 'url(/Coursedee-bg-web-V1.jpg)',
              backgroundColor: '#0f172a'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-blue-950/50 to-purple-950/70" />
          </div>
        </div>

        <div className="relative z-10 pt-[calc(env(safe-area-inset-top)+1rem)] pb-4 md:pt-8 md:pb-8 px-3 md:px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-6 md:mb-8">
              <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-blue-500 mb-2 flex items-center justify-center gap-3">
                <BookOpen className="w-10 h-10 text-blue-400" />
                AIForYou V1
                <Sparkles className="w-10 h-10 text-purple-400" />
              </h1>
              <p className="text-blue-200 text-lg md:text-xl font-semibold mb-1">
                Prompt Builder สำหรับคนทำคอนเทนต์ AI
              </p>
              <p className="text-slate-300 text-sm md:text-base">
                กรุณาตั้งค่าตัวตนก่อนใช้งาน (ทำครั้งเดียว)
              </p>
            </div>

            {/* Identity Setup Form - Multi-Step */}
            <div id="identity-setup" className="bg-slate-950/60 backdrop-blur-xl rounded-xl md:rounded-2xl p-6 md:p-8 border border-blue-400/30">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-3 rounded-xl">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-3xl font-bold text-white">ตั้งค่าตัวตน</h2>
                    <p className="text-slate-400 text-sm mt-1">Step {currentStep}/4</p>
                  </div>
                </div>
                {/* ปุ่มปิด - แสดงเฉพาะเมื่อมีตัวตนแล้ว (กำลังแก้ไข) */}
                {creatorIdentity && (
                  <button
                    onClick={() => setCurrentStep(0)}
                    className="flex items-center gap-2 bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white px-3 py-2 rounded-lg transition-all text-sm font-medium"
                  >
                    <X className="w-4 h-4" />
                    ปิด
                  </button>
                )}
              </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => setCurrentStep(1)} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold cursor-pointer ${currentStep >= 1 ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                      {currentStep > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
                    </div>
                    <span className={`text-xs md:text-sm font-medium ${currentStep >= 1 ? 'text-blue-400' : 'text-slate-500'}`}>ข้อมูลพื้นฐาน</span>
                  </button>
                  <div className={`flex-1 h-1 mx-2 rounded ${currentStep > 1 ? 'bg-blue-500' : 'bg-slate-700'}`} />
                  <button onClick={() => setCurrentStep(2)} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold cursor-pointer ${currentStep >= 2 ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                      {currentStep > 2 ? <CheckCircle2 className="w-5 h-5" /> : '2'}
                    </div>
                    <span className={`text-xs md:text-sm font-medium ${currentStep >= 2 ? 'text-purple-400' : 'text-slate-500'}`}>เป้าหมาย</span>
                  </button>
                  <div className={`flex-1 h-1 mx-2 rounded ${currentStep > 2 ? 'bg-purple-500' : 'bg-slate-700'}`} />
                  <button onClick={() => setCurrentStep(3)} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold cursor-pointer ${currentStep >= 3 ? 'bg-green-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                      {currentStep > 3 ? <CheckCircle2 className="w-5 h-5" /> : '3'}
                    </div>
                    <span className={`text-xs md:text-sm font-medium ${currentStep >= 3 ? 'text-green-400' : 'text-slate-500'}`}>สินค้า</span>
                  </button>
                  <div className={`flex-1 h-1 mx-2 rounded ${currentStep > 3 ? 'bg-green-500' : 'bg-slate-700'}`} />
                  <button className="flex items-center gap-2 cursor-not-allowed opacity-50">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep >= 4 ? 'bg-yellow-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                      4
                    </div>
                    <span className={`text-xs md:text-sm font-medium ${currentStep >= 4 ? 'text-yellow-400' : 'text-slate-500'}`}>บันทึก</span>
                  </button>
                </div>
              </div>

              {/* Step 1 */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-base md:text-lg font-bold text-white mb-2">
                      ประเภทธุรกิจของคุณ <span className="text-red-400">*</span>
                    </label>
                    <p className="text-xs text-slate-400 mb-2">แตะเลือกประเภท แล้วกรอกชื่อธุรกิจ</p>
                    {/* หมวดธุรกิจ — แตะเพื่อเริ่มเร็ว */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {BUSINESS_CATEGORIES.map((cat) => {
                        const Icon = cat.icon;
                        const active = whatIDo.trim().startsWith(cat.label);
                        return (
                          <button
                            key={cat.label}
                            type="button"
                            onClick={() => setWhatIDo(cat.label + ' — ')}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs md:text-sm font-medium border transition-all ${active ? 'bg-blue-500 border-blue-400 text-white' : 'bg-slate-800/60 border-slate-600 text-slate-300 hover:border-blue-400'}`}
                          >
                            <Icon className="w-4 h-4" />
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>
                    <textarea
                      value={whatIDo}
                      onChange={(e) => setWhatIDo(e.target.value)}
                      placeholder="กรอกชื่อธุรกิจ เช่น ร้านเล็บ Bibi Nail / คาเฟ่ริมน้ำ / เพจขายครีม"
                      rows={2}
                      className="w-full px-3 md:px-4 py-2 md:py-3 bg-slate-900/50 border border-blue-400/30 rounded-lg text-sm md:text-base text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>

                  <div>
                    <label className="block text-base md:text-lg font-bold text-white mb-2">
                      กลุ่มเป้าหมายของคุณคือใคร? <span className="text-red-400">*</span>
                    </label>
                    <p className="text-xs text-slate-400 mb-2">คนที่คุณอยากให้เข้ามาดู/เป็นลูกค้า — คอนเทนต์จะถูกออกแบบมาดึงคนกลุ่มนี้</p>
                    <textarea
                      value={myAudience}
                      onChange={(e) => setMyAudience(e.target.value)}
                      placeholder="เช่น ผู้หญิงรักสวยรักงาม 20-35 / เจ้าของร้านอาหารที่อยากได้ลูกค้าเพิ่ม / คนอยากวางแผนการเงินให้ครอบครัว"
                      rows={2}
                      className="w-full px-3 md:px-4 py-2 md:py-3 bg-slate-900/50 border border-blue-400/30 rounded-lg text-sm md:text-base text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>

                  <div>
                    <label className="block text-base md:text-lg font-bold text-white mb-2">
                      จุดแข็งที่ไม่เหมือนใครของคุณคืออะไร? <span className="text-red-400">*</span>
                    </label>
                    <p className="text-xs text-slate-400 mb-2">อะไรที่ทำให้คุณต่างจากคู่แข่ง — ใช้เป็นมุมของคอนเทนต์</p>
                    <textarea
                      value={myExpertise}
                      onChange={(e) => setMyExpertise(e.target.value)}
                      placeholder="เช่น คั่วกาแฟเองทุกวัน / ใช้วัตถุดิบออร์แกนิก 100% / สอนจากประสบการณ์จริง 10 ปี / บริการเร็วที่สุดในย่าน"
                      rows={2}
                      className="w-full px-3 md:px-4 py-2 md:py-3 bg-slate-900/50 border border-blue-400/30 rounded-lg text-sm md:text-base text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>

                  <div className="pt-4 space-y-3">
                    <button
                      onClick={() => {
                        if (!whatIDo.trim() || !myAudience.trim() || !myExpertise.trim()) {
                          alert('กรุณากรอกข้อมูลให้ครบทั้ง 3 ช่อง');
                          return;
                        }
                        setCurrentStep(2);
                      }}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-3 md:py-4 rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all flex items-center justify-center gap-2"
                    >
                      ถัดไป: เป้าหมาย
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    
                    {/* ปุ่มล้างตัวตน */}
                    {(whatIDo || myExpertise || myProof || myProduct) && (
                      <button
                        onClick={async () => {
                          if (confirm('คุณต้องการล้างข้อมูลตัวตนทั้งหมดใช่หรือไม่?')) {
                            try {
                              // ลบจาก Firestore
                              const userDocRef = doc(db, 'coursedee_v1_users', currentUsername);
                              await setDoc(userDocRef, {
                                creatorIdentity: null,
                                updatedAt: new Date().toISOString()
                              }, { merge: true });
                              
                              // ลบจาก state และ localStorage
                              setWhatIDo('');
                              setMyAudience('');
                              setMyExpertise('');
                              setMyProof('');
                              setMyGoals([]);
                              setMyChallenge('');
                              setChallengeType('');
                              setMyProduct('');
                              setCreatorIdentity(null);
                              setIdentityText('');
                              localStorage.removeItem(`coursedee_creator_identity_${currentUsername}`);
                              setCurrentStep(1); // กลับไป Step 1 เพื่อให้แสดงฟอร์มตั้งค่าใหม่
                              alert('✅ ล้างข้อมูลตัวตนเรียบร้อยแล้ว');
                            } catch (error) {
                              console.error('Error clearing identity:', error);
                              alert('❌ เกิดข้อผิดพลาดในการลบข้อมูล');
                            }
                          }
                        }}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 md:py-4 rounded-lg transition-all flex items-center justify-center gap-2"
                      >
                        <Trash2 className="w-5 h-5" />
                        ล้างตัวตน
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: เป้าหมาย (แตะเลือก ไม่ต้องพิมพ์) */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-400/50 rounded-lg p-4">
                    <label className="block text-base md:text-lg font-bold text-yellow-400 mb-1 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      เป้าหมายของคุณคือ <span className="text-red-400">*</span>
                    </label>
                    <p className="text-xs md:text-sm text-slate-300 mb-4">
                      แตะเลือก 1-2 อัน — ระบบจะแปลงเป็นแนวคอนเทนต์ให้เอง (ไม่ต้องคิดเอง)
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {CONTENT_GOALS.map((goal) => {
                        const Icon = goal.icon;
                        const active = myGoals.includes(goal.key);
                        return (
                          <button
                            key={goal.key}
                            type="button"
                            onClick={() => {
                              setMyGoals((prev) => {
                                if (prev.includes(goal.key)) return prev.filter((k) => k !== goal.key);
                                if (prev.length >= 2) return prev; // เลือกได้สูงสุด 2
                                return [...prev, goal.key];
                              });
                            }}
                            className={`flex items-center gap-2.5 px-3 py-3 rounded-xl text-left text-sm font-medium border-2 transition-all ${active ? 'bg-yellow-500/20 border-yellow-400 text-white' : 'bg-slate-900/60 border-slate-700 text-slate-300 hover:border-yellow-400/50'}`}
                          >
                            <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-yellow-500' : 'bg-slate-800'}`}>
                              <Icon className="w-5 h-5 text-white" />
                            </span>
                            <span className="flex-1">{goal.label}</span>
                            {active && <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-500 mt-3">เลือกแล้ว {myGoals.length}/2</p>
                  </div>

                  {/* ความท้าทาย 60 วัน (ไม่บังคับ) — ถ้าใส่ จะกลายเป็นแกนซีรีส์ที่คนอยากติดตาม */}
                  <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border-2 border-pink-400/40 rounded-lg p-4">
                    <label className="block text-base md:text-lg font-bold text-pink-300 mb-1 flex items-center gap-2">
                      <Rocket className="w-5 h-5" />
                      ความท้าทาย 60 วัน <span className="text-slate-400 text-sm font-normal">(ไม่บังคับ)</span>
                    </label>
                    <p className="text-xs md:text-sm text-slate-300 mb-3">
                      มีอะไรที่อยากทำให้สำเร็จใน 60 วันไหม? ถ้าใส่มา ระบบจะทำเป็น<strong className="text-pink-300">ซีรีส์ที่คนอยากติดตาม</strong>ให้
                    </p>
                    <textarea
                      value={myChallenge}
                      onChange={(e) => setMyChallenge(e.target.value)}
                      placeholder="เช่น อยากสร้างเงินแสนจากธุรกิจใน 60 วัน / อยากมีลูกค้าต่อคิว 100 คน / เปิดสาขา 2 ให้ได้"
                      rows={2}
                      className="w-full px-3 md:px-4 py-2 md:py-3 bg-slate-900/70 border border-pink-400/30 rounded-lg text-sm md:text-base text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-400"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 md:py-4 rounded-lg transition-all"
                    >
                      ย้อนกลับ
                    </button>
                    <button
                      onClick={() => {
                        if (myGoals.length === 0) {
                          alert('กรุณาเลือกเป้าหมายอย่างน้อย 1 อัน');
                          return;
                        }
                        const proof = [
                          myChallenge.trim() ? `ความท้าทายภายใน 60 วัน: ${myChallenge.trim()}` : '',
                          deriveProofFromGoals(myGoals),
                        ].filter(Boolean).join(' | ');
                        setMyProof(proof);
                        setCurrentStep(3);
                      }}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 md:py-4 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all flex items-center justify-center gap-2"
                    >
                      ถัดไป: สินค้า/บริการ
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-base md:text-lg font-bold text-white mb-2">
                      สินค้าที่ฉันมี <span className="text-slate-400 text-sm">(ไม่บังคับ - ถ้ามีจะแทรก Soft Sell)</span>
                    </label>
                    <textarea
                      value={myProduct}
                      onChange={(e) => setMyProduct(e.target.value)}
                      placeholder="พิมพ์สิ่งที่คุณขาย เช่น คอร์ส / ครีม / อาหาร / บริการ (แนะนำ: ใส่สินค้าทั้งหมดที่มี หรือสินค้าที่คิดจะขายในอนาคต)"
                      rows={6}
                      className="w-full px-3 md:px-4 py-2 md:py-3 bg-slate-900/50 border border-green-400/30 rounded-lg text-sm md:text-base text-white placeholder-slate-500 italic focus:outline-none focus:ring-2 focus:ring-green-400"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 md:py-4 rounded-lg transition-all"
                    >
                      ย้อนกลับ
                    </button>
                    <button
                      onClick={() => setCurrentStep(4)}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-3 md:py-4 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all flex items-center justify-center gap-2"
                    >
                      ถัดไป: ดู Prompt และบันทึก
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Prompt และบันทึก */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  {/* Preview Identity */}
                  <div className="bg-slate-900/50 border border-yellow-400/30 rounded-lg p-4">
                    <h3 className="text-sm font-bold text-yellow-400 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      ตัวตนของคุณ
                    </h3>
                    <div className="text-xs md:text-sm text-slate-300 space-y-1">
                      <p><strong className="text-blue-400">ธุรกิจ:</strong> {whatIDo || '...'}</p>
                      <p><strong className="text-blue-400">กลุ่มเป้าหมาย:</strong> {myAudience || '...'}</p>
                      <p><strong className="text-blue-400">จุดแข็ง:</strong> {myExpertise || '...'}</p>
                      <p><strong className="text-yellow-400">เป้าหมาย:</strong> {myGoals.length ? CONTENT_GOALS.filter((g) => myGoals.includes(g.key)).map((g) => g.label).join(', ') : '...'}</p>
                      {myChallenge.trim() && <p><strong className="text-pink-300">ท้าทาย 60 วัน:</strong> {myChallenge}</p>}
                      {myProduct && <p><strong className="text-green-400">สินค้า:</strong> {myProduct}</p>}
                    </div>
                  </div>

                  <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-3 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <p className="text-xs md:text-sm text-emerald-200">
                      กด <strong className="text-white">"บันทึกตัวตน"</strong> แล้วไปที่หน้าหลัก — เลือก Authority หรือ Story เพื่อ<strong className="text-white">เจนหัวข้อ + สคริปต์ในเว็บได้เลย</strong>
                    </p>
                  </div>

                  {/* Content Mode Display */}
                  <div>
                    {/* Authority Mode */}
                    {selectedMode === 'authority' && (
                      <div className="hidden bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-2 border-blue-400/30 rounded-xl p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2.5 rounded-lg">
                          <GraduationCap className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 flex items-center gap-2">
                          <h3 className="text-lg font-bold text-white">Authority Mode</h3>
                          <div className="group relative">
                            <Info className="w-4 h-4 text-blue-300 cursor-help" />
                            <div className="absolute left-0 top-6 w-64 bg-slate-800 border border-blue-400/30 rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl">
                              <p className="text-xs font-semibold text-blue-300 mb-2">เหมาะสำหรับ:</p>
                              <ul className="text-xs text-slate-300 space-y-1 mb-3">
                                <li>• สายสอน</li>
                                <li>• ผู้เชี่ยวชาญ</li>
                                <li>• ขายบริการ</li>
                                <li>• ที่ปรึกษา</li>
                                <li>• สายสร้าง Authority</li>
                              </ul>
                              <p className="text-xs font-semibold text-yellow-300 mb-1">ผลลัพธ์:</p>
                              <p className="text-xs text-slate-300">"คนนี้รู้จริง"</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-white">สร้างคอนเทนต์ด้วย AI</h4>
                        <button
                          onClick={async () => {
                            setIsCopyingIdentityPrompt(true);
                          
                          const userInput = `ฉันทำ: ${whatIDo}\nความเชี่ยวชาญของฉัน: ${myExpertise}\nฉันกำลังพิสูจน์: ${myProof}${myProduct ? `\nสินค้าของฉัน: ${myProduct}` : ''}`;
                          
                          const fullPrompt = `You are a TikTok Content Creator Specialist.

Your ONLY goal:
Create content that makes people STOP SCROLLING.

Content must feel:
✅ emotional
✅ relatable
✅ addictive
✅ real TikTok creator
✅ human

NOT:
❌ educational lecture
❌ corporate presentation
❌ business strategy document
❌ marketing framework
❌ online course material

━━━━━━━━━━━━━━━━━━━━━
[CRITICAL SYSTEM RULE]
━━━━━━━━━━━━━━━━━━━━━

- DO NOT ask the user any questions
- DO NOT pause or wait for confirmation
- DO NOT offer options
- ALWAYS generate FULL output in ONE response

❌ Forbidden:
"Do you want me to continue?"
"Which option do you prefer?"

✅ Required:
Generate ALL outputs immediately:
- Creator Identity
- Main Story Arc
- 90 Day Expertise Journey Plan
- Content Categories (แสดงเฉพาะชื่อหมวด)
- Monetization Roadmap

⚠️ CRITICAL OUTPUT ORDER:
1. Creator Identity
2. 90-Day Content Plan (แสดงก่อน - เป็นส่วนหลัก)
3. Content Categories (แสดงเฉพาะชื่อหมวด)
4. Monetization Roadmap

❌ ห้าม: แสดง "50 Content Ideas" ออกมา
✅ ต้อง: สร้าง 90-Day Plan โดยตรงจาก Creator Identity และ Main Story Arc

━━━━━━━━━━━━━━━━━━━━━
[MONETIZATION ROADMAP]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL PURPOSE:
This is NOT a product sales plan.
This is a CHANNEL MONETIZATION MAP.

Goal: Help creators see "If I build this channel successfully, what can I monetize?"

⚠️ ANALYSIS REQUIREMENTS:
Analyze monetization opportunities based on:
1. Creator Identity
2. Main Story Arc
3. Expertise
4. Audience segments

❌ FORBIDDEN:
- Random product suggestions
- Generic recommendations
- Products without clear reasoning
- Suggestions that don't match creator identity

✅ REQUIRED:
Every product MUST have reasoning based on:
1. Expertise Match - Does it align with their knowledge?
2. Lifestyle Match - Do they actually use/understand this?
3. Audience Match - Does their audience need this?

━━━━━━━━━━━━━━━━━━━━━
[4-LEVEL MONETIZATION SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

**A-Level Products** 🟢
= Easiest to sell because directly related to core expertise

Requirements:
- Direct expertise match
- Audience already expects this
- High credibility alignment
- Can sell from Day 1

Example for Gold Shop Owner:
✅ Gold investment course
✅ Gold buying consultation
✅ Gold market analysis service
Why: Direct expertise, audience expects this, builds credibility

Example for Beauty Expert:
✅ Skincare consultation
✅ Makeup tutorial course
✅ Beauty product reviews (affiliate)
Why: Core expertise, audience trusts their knowledge

**B-Level Products** 🟡
= Sellable because related to lifestyle, work tools, or things creator actually uses

Requirements:
- Lifestyle/workflow match
- Creator genuinely uses this
- Relevant to content creation process
- Audience sees creator use it

Example for Gold Shop Owner:
✅ Business management tools they use
✅ Customer service systems
✅ Financial planning apps
Why: They use it in their business, audience sees the results

Example for Beauty Expert:
✅ Camera/lighting equipment for content
✅ Editing software they use
✅ Skincare fridge or storage
Why: Part of their workflow, audience asks about it

**C-Level Products** 🟠
= Can sell AFTER building trust and credibility

Requirements:
- Requires established trust
- Adjacent to main expertise
- Needs proof of results first
- Audience must see value demonstration

Example for Gold Shop Owner:
✅ Real estate investment (after proving financial expertise)
✅ Stock market courses (after showing investment success)
✅ Business coaching (after showing business growth)
Why: Related but needs proven track record first

Example for Beauty Expert:
✅ Wellness products (after beauty authority established)
✅ Fashion items (after style credibility built)
✅ Lifestyle coaching (after personal brand strong)
Why: Extension of expertise, needs trust foundation

**D-Level Products** 🔴
= Should NOT sell in early stages - may damage credibility

Characteristics:
- No connection to creator identity
- Confuses audience
- Damages trust
- Looks like random promotion

Example for Gold Shop Owner:
❌ Gaming products
❌ Pet supplies
❌ Random supplements
Why: No expertise connection, confuses positioning

Example for Beauty Expert:
❌ Car accessories
❌ Tech gadgets (unless tech beauty tools)
❌ Random affiliate products
Why: Breaks trust, looks desperate

━━━━━━━━━━━━━━━━━━━━━
[MONETIZATION ROADMAP OUTPUT FORMAT]
━━━━━━━━━━━━━━━━━━━━━

For each level, provide:

**A-Level: Immediate Monetization** 🟢
[Product/Service Name]
- **Expertise Match**: [Why it aligns with their knowledge]
- **Audience Match**: [Why their audience needs this]
- **Timeline**: Can start immediately
- **Credibility Impact**: Strengthens authority

**B-Level: Lifestyle Monetization** 🟡
[Product/Service Name]
- **Lifestyle Match**: [How they use this in their work/life]
- **Audience Interest**: [Why audience cares about this]
- **Timeline**: After showing consistent usage
- **Credibility Impact**: Neutral to positive

**C-Level: Trust-Based Monetization** 🟠
[Product/Service Name]
- **Trust Requirement**: [What must be proven first]
- **Expertise Extension**: [How it connects to main expertise]
- **Timeline**: After 6-12 months of proven results
- **Credibility Impact**: Requires strong foundation

**D-Level: Avoid These** 🔴
[Product/Service Name]
- **Why Avoid**: [Clear reason why this damages credibility]
- **Risk**: [What could go wrong]
- **Alternative**: [What to do instead]

━━━━━━━━━━━━━━━━━━━━━
[MONETIZATION ANALYSIS RULES]
━━━━━━━━━━━━━━━━━━━━━

1. **Specificity Required**
   - Don't say "online courses" - specify what course topic
   - Don't say "affiliate products" - specify which products
   - Every suggestion must be concrete and actionable

2. **Reasoning Required**
   - Every product must have clear expertise/lifestyle/audience match
   - Explain WHY it's in that level
   - Show the connection to creator identity

3. **Timeline Clarity**
   - A-Level: Day 1
   - B-Level: After consistent content (1-3 months)
   - C-Level: After proven results (6-12 months)
   - D-Level: Never (or only after major pivot)

4. **Credibility Protection**
   - Prioritize products that strengthen authority
   - Warn against products that damage trust
   - Consider long-term brand building

5. **Audience-Centric**
   - Every product must solve audience problems
   - Match audience sophistication level
   - Consider audience buying power

━━━━━━━━━━━━━━━━━━━━━
[EXAMPLES BY PROFESSION]
━━━━━━━━━━━━━━━━━━━━━

**Gold Shop Owner:**

A-Level 🟢:
- Gold investment consultation service
- Gold buying guide course
- Gold market analysis membership

B-Level 🟡:
- Business management software they use
- Customer relationship tools
- Financial tracking apps

C-Level 🟠:
- Real estate investment course (after proving investment success)
- Business coaching (after showing shop growth)
- Financial planning services (after demonstrating wealth building)

D-Level 🔴:
- Random supplements
- Gaming products
- Unrelated affiliate products

**Beauty Expert:**

A-Level 🟢:
- Skincare consultation
- Makeup tutorial course
- Beauty product recommendations (affiliate)

B-Level 🟡:
- Ring light/camera equipment
- Editing software
- Content creation tools

C-Level 🟠:
- Wellness coaching (after beauty authority)
- Fashion styling (after style credibility)
- Personal branding course (after channel success)

D-Level 🔴:
- Tech gadgets (unless beauty tech)
- Random supplements
- Unrelated products

**Real Estate Agent:**

A-Level 🟢:
- Home buying consultation
- Property investment course
- Market analysis reports

B-Level 🟡:
- CRM tools they use
- Property management software
- Virtual tour equipment

C-Level 🟠:
- Interior design services (after showing property expertise)
- Mortgage broker partnerships (after trust built)
- Investment coaching (after proven results)

D-Level 🔴:
- Beauty products
- Gaming items
- Unrelated affiliate products

━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL REMINDER:
The Monetization Roadmap protects channel credibility by:
1. Showing what to sell NOW (A-Level)
2. Showing what to sell LATER (B & C-Level)
3. Showing what to NEVER sell (D-Level)

This prevents creators from destroying trust by promoting random products too early.

━━━━━━━━━━━━━━━━━━━━━
[INPUT FROM USER]
━━━━━━━━━━━━━━━━━━━━━

${userInput}

━━━━━━━━━━━━━━━━━━━━━
[IDENTITY CREATION - USE ONLY USER INPUT]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL RULES:
- Use ONLY information from user input above
- DO NOT guess or assume anything
- DO NOT add extra details
- Simply organize what user wrote into a clear identity

Create a strong creator identity:

- Who I am (clear persona)
- Who I help (specific audience)
- What I do (clear value)
- ⭐ What I'm proving (MAIN STORY ARC - CRITICAL)

Must feel:
"This person is the real deal"

✅ Keep it simple and authentic
✅ Use user's own words
❌ Don't make it sound polished or fake

━━━━━━━━━━━━━━━━━━━━━
[PROOF MISSION - CRITICAL]
━━━━━━━━━━━━━━━━━━━━━

⚠️ IMPORTANT:
"What I'm proving" is the MAIN STORY ARC.

This is:
✅ emotional engine
✅ reason people follow
✅ expertise journey
✅ transformation story

Examples:
- "Proving that a small shop can have customers lining up"
- "Proving that ordinary people can make money from their online presence"
- "Proving that someone who's not good at speaking can still sell houses"

⚠️ RULE:
Every content idea and 90-day plan
MUST connect back to this proof mission.

━━━━━━━━━━━━━━━━━━━━━
[CONTENT CONNECTION RULE]
━━━━━━━━━━━━━━━━━━━━━

Content MUST connect together.

NOT:
❌ random isolated topics

BUT:
✅ episodes from same expertise journey
✅ progress
✅ continuity
✅ knowledge progression

The viewer must feel:
"If I don't watch this, I'll miss something important"

━━━━━━━━━━━━━━━━━━━━━
[10 EMOTION WRAPPERS - LOCKED]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL: Use ONLY these 10 emotion wrappers
❌ DO NOT create new categories
❌ DO NOT change category names
❌ DO NOT merge categories

1. 😱 Shock
2. 💔 Pain
3. 🤔 Curiosity
4. 😮 Reality
5. ✨ Hope
6. 🎭 POV + Roleplay
7. 🔥 Trend
8. 🎯 Framework
9. 💡 Insight
10. 📺 Series

⚠️ CRITICAL:
These are emotion wrappers for expertise content.
All content must contain knowledge/value inside.

━━━━━━━━━━━━━━━━━━━━━
[CORE THINKING]
━━━━━━━━━━━━━━━━━━━━━

Category = "Viewer's emotion"

NOT:
❌ Business category
❌ Marketing category
❌ Strategy category

BUT:
✅ Shock
✅ Pain
✅ Hope
✅ Curiosity
✅ Reality

System must:
- Extract pain / situation / reality from that profession
- Then convert to "emotion"

NOT:
- Use profession to create new categories

━━━━━━━━━━━━━━━━━━━━━
[EXPERT MODE - AUTHORITY + PRACTICAL VALUE]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL

Expert is NOT:
❌ Motivation
❌ Mindset content
❌ Personal reflection only
❌ Generic life lessons
❌ Generic business advice
❌ Creator opinions without evidence
❌ "What I learned" content with no practical takeaway

Expert MUST make viewers feel:
✅ "I learned something useful"
✅ "I can use this immediately"
✅ "This person knows what they're doing"
✅ "I should save this"
✅ "This is insider knowledge"
✅ "This creator is a real practitioner"

━━━━━━━━━━━━━━━━━━━━━
[NEW CORE PRINCIPLE]
━━━━━━━━━━━━━━━━━━━━━

People follow expertise.

People stay because they continuously learn something useful.

People rarely care about the creator's daily life.

People care about:

✅ knowledge
✅ experience
✅ hidden truths
✅ decision making
✅ real-world application

The creator's life is only useful when it helps explain expertise.

Therefore:

Every content must teach, reveal, demonstrate, compare, analyze, explain, diagnose, or expose something useful.

Story is not the destination.

Story is only the delivery vehicle.

Expertise is the destination.

━━━━━━━━━━━━━━━━━━━━━

NEW RULE

Do NOT create content because it is emotional.

Create content because it is valuable.

Then use emotion to improve retention.

Value First.
Emotion Second.
━━━━━━━━━━━━━━━━━━━━━
EXPERT CONTENT PRIORITY

LEVEL 1 (HIGHEST PRIORITY)
REAL RECIPES / REAL FORMULAS / REAL METHODS / REAL PROCESS
REAL WORKFLOWS / REAL SYSTEMS / REAL CHECKLISTS
REAL COMPARISONS / REAL DECISION FRAMEWORKS / REAL STEP-BY-STEP EXECUTION

LEVEL 2
MISTAKES / TROUBLESHOOTING / DIAGNOSIS / CASE STUDIES
BEFORE/AFTER ANALYSIS / COMMON FAILURES / FIELD EXPERIENCE

LEVEL 3
INDUSTRY INSIGHTS / PATTERNS / OBSERVATIONS
THINGS ONLY PRACTITIONERS NOTICE / HIDDEN FACTORS / BEHIND-THE-SCENES THINKING

━━━━━━━━━━━━━━━━━━━━━
WHEN GENERATING EXPERT CONTENT

Always ask: "What practical knowledge can the viewer use immediately?"
NOT: "What advice can the creator give?"

━━━━━━━━━━━━━━━━━━━━━
EXPERT MUST REVEAL

✅ How / ✅ Why / ✅ When
✅ What to avoid
✅ What most people misunderstand
✅ Real examples / ✅ Real decisions

━━━━━━━━━━━━━━━━━━━━━
UNIVERSAL EXPERT EXAMPLES

Restaurant: Exact recipe / Ingredient ratio / Cooking workflow / Why dishes fail
Coffee Shop: Exact drink recipe / Extraction comparison / Milk temperature guide
Beauty: Product selection process / Technique comparison / Application workflow
Hair Stylist: Face shape analysis / Consultation workflow / Color selection logic
Real Estate: Property inspection checklist / Deal analysis process / Rental yield calculation
Fitness: Exercise form comparison / Program structure / Recovery checklist
Repair Service: Diagnosis workflow / Common failure points / Tool selection logic
Any Profession: Exact process / Exact workflow / Exact checklist / Exact method / Exact reasoning

━━━━━━━━━━━━━━━━━━━━━
EXPERT CONTENT STRUCTURE

Hook → Problem → Real Explanation → Demonstration → Example → Actionable Takeaway → Soft Sell (Optional)

━━━━━━━━━━━━━━━━━━━━━
EXPERT CONTENT IDEA FORMULA

1. Wrong Belief + Real Explanation
Example: "90% of people think X causes this... but the real reason is Y."

2. Comparison + Decision Logic
Example: "A vs B - Which should you choose and why?"

3. Problem + Diagnosis
Example: "If this happens, check these 3 things first."

4. Workflow + Real Process
Example: "This is exactly how I do it."

5. Case Study + Analysis
Example: "Here's why this result happened."

━━━━━━━━━━━━━━━━━━━━━
EXPERT EMOTIONAL TARGET

Curiosity → Discovery → Understanding → Trust → Respect

━━━━━━━━━━━━━━━━━━━━━
EXPERT SUCCESS TEST

After watching, the viewer must be able to say:
"I learned something useful."
"I understand this better now."
"I can apply this."
"This creator is the real deal."

If viewers only think "Interesting story" or "Nice motivation"
then it is NOT Expert content. Rewrite immediately.

━━━━━━━━━━━━━━━━━━━━━
90-DAY EXPERT DISTRIBUTION RULE

⚠️ For skill-based professions (Beauty/Food/Coffee/Health/Repair/Trade/Service/Consulting/Real Estate/Education):
Expert content must represent 20%-30% of total content.

━━━━━━━━━━━━━━━━━━━━━
[EXPERTISE FIRST DISTRIBUTION]
━━━━━━━━━━━━━━━━━━━━━

Expert = 60%

Expert + Case Study = 10%

Expert + Reality = 10%

Expert + POV / Roleplay = 5%

Expert + Trend / Reaction / Situation = 5%

Expert + Story = 5%

Expert + Soft Sell = 5%

━━━━━━━━━━━━━━━━━━━━━

CRITICAL

Expert is no longer a category.

Expert is the foundation of every category.

Every content must contain useful knowledge.

The only thing that changes is the delivery format.

Example:

Expert

Expert through Story

Expert through Case Study

Expert through Trend

Expert through POV

Expert through Reality

Expert through Soft Sell

━━━━━━━━━━━━━━━━━━━━━

━━━━━━━━━━━━━━━━━━━━━
FINAL RULE

An expertise journey creates attention.
Expert knowledge creates trust.
The strongest creator combines both.

Story makes people watch.
Expertise makes people follow.

━━━━━━━━━━━━━━━━━━━━━
[SERIES MODE - CHALLENGE SYSTEM V2]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL

Series is NOT:

❌ Episode 1
❌ Episode 2
❌ Episode 3

❌ Part 1
❌ Part 2
❌ Part 3

❌ Same topic split into multiple episodes

❌ Generic lifestyle content

❌ General discussion topics

❌ Questions without measurable outcomes

━━━━━━━━━━━━━━━━━━━━━

Series MUST be:

✅ Challenge

✅ Mission

✅ Experiment

✅ Public Goal

✅ Measurable Outcome

✅ Real Stakes

✅ Possible Failure

━━━━━━━━━━━━━━━━━━━━━

Every Series Idea MUST contain:

1. A Goal

Example:

* Make 100,000 baht
* Reach 10,000 followers
* Get first customer
* Post every day
* Bring back an inactive channel

2. A Time Frame

Example:

* 7 days
* 14 days
* 30 days
* 60 days
* 90 days

3. A Measurable Result

Example:

* Followers
* Views
* Income
* Leads
* Engagement
* Number of posts

4. A Chance of Failure

The audience must think:

"I don't know if this will work."

━━━━━━━━━━━━━━━━━━━━━

BAD SERIES EXAMPLES

❌ Can storytelling build followers?

❌ Can online identity generate income?

❌ Can a dead channel grow again?

❌ What happens if I post every day?

Reason:

No stakes

No deadline

No measurable outcome

Too vague

━━━━━━━━━━━━━━━━━━━━━

GOOD SERIES EXAMPLES

✅ 90 Days: Can I make 100,000 baht from a channel that has been inactive for 1 year?

✅ 30 Days: Can I bring my old audience back without spending on ads?

✅ 14 Days: Can storytelling alone get my first inquiry?

✅ 60 Days: Can I grow from 2,000 to 10,000 followers?

✅ 7 Days: Can I post every day without missing a single day?

✅ 21 Days: Can I revive engagement on a dead channel?

✅ 30 Days: Can I get my first content income?

✅ 90 Days: Can consistency beat talent?

━━━━━━━━━━━━━━━━━━━━━

CRITICAL RULE

Every Series Idea MUST create this feeling:

"I need to know whether this person succeeds or fails."

If the audience already knows the answer,

rewrite the idea.

━━━━━━━━━━━━━━━━━━━━━

SERIES GENERATION FORMULA

Challenge
+
Time Limit
+
Specific Goal
+
Risk of Failure

Examples:

"90 Days + 100,000 Baht + Dead Channel"

"30 Days + First Customer + No Ads"

"60 Days + 10,000 Followers + Storytelling Only"

"14 Days + Daily Posting + No Excuses"

━━━━━━━━━━━━━━━━━━━━━

SERIES OUTPUT FORMAT

📺 [Challenge Question] [Series]

Examples:

📺 90 วัน ปั้นช่องที่หายไป 1 ปี ให้ทำเงิน 100,000 บาทได้ไหม? [Series]

📺 30 วัน ปลุกคนดูเก่าให้กลับมาได้จริงไหม? [Series]

📺 60 วัน จาก 2,000 ไป 10,000 ผู้ติดตามได้หรือเปล่า? [Series]

📺 14 วัน ใช้การเล่าเรื่องอย่างเดียว จะมีคนทักมาหรือไม่? [Series]

📺 21 วัน ลงคลิปทุกวันแบบไม่สนยอดวิว ผลจะเปลี่ยนไหม? [Series]

━━━━━━━━━━━━━━━━━━━━━

CRITICAL

Series Category should generate:

Different Challenges

Different Missions

Different Experiments

Different Public Goals

NOT

Different Episodes

of the same challenge.

The 90-Day Plan will later turn one selected challenge into a full reality-show story arc.

━━━━━━━━━━━━━━━━━━━━━
[TREND / POV MODE V2]
━━━━━━━━━━━━━━━━━━━━━

At least 30% of output must be:

✅ Trend
✅ POV + Roleplay
✅ Lip-sync
✅ Reaction
✅ Situation Content

━━━━━━━━━━━━━━━━━━━━━

CRITICAL:

Trend is NOT only:

❌ Viral Music
❌ Trending Sound
❌ Viral Dance

Trend also includes:

✅ Lip-sync
✅ POV
✅ Roleplay
✅ Reaction
✅ Situation Content

━━━━━━━━━━━━━━━━━━━━━

At least 50% of Trend Ideas
must be:

* Lip-sync
* POV
* Roleplay
* Reaction
* Situation Content

NOT only trend sounds.

━━━━━━━━━━━━━━━━━━━━━

Trend / POV / Lip-sync content must:

✅ Look like real TikTok
✅ Use text overlays effectively
✅ Use emotion effectively
✅ Use music effectively
✅ Feel native to TikTok
✅ Not rely on talking-head content only

━━━━━━━━━━━━━━━━━━━━━

The 90-Day Plan must regularly include:

✅ Trend
✅ Lip-sync
✅ POV
✅ Roleplay
✅ Reaction
✅ Situation Content

These formats should appear throughout
the expertise journey.

NOT only inside the Trend category.

━━━━━━━━━━━━━━━━━━━━━

Use these formats to express:

✅ Progress
✅ Failure
✅ Frustration
✅ Hope
✅ Breakthroughs
✅ Milestones
✅ Setbacks
✅ Unexpected Results

━━━━━━━━━━━━━━━━━━━━━

Examples:

Lip-sync:

Using a trending song
with emotional text overlays.

POV:

POV: You came back to content creation
after disappearing for 1 year.

Reaction:

Reacting to:

* Analytics
* Comments
* Milestones
* Failures
* Breakthroughs

Roleplay:

Past Self
vs
Current Self

Day 1
vs
Day 60

Situation Content:

"When you promised yourself
you would upload every day..."

"When you thought success
would happen faster..."

━━━━━━━━━━━━━━━━━━━━━

CRITICAL:

Trend / Lip-sync / POV / Reaction

must still connect to:

⭐ What I'm Proving

They should support
the Main Story Arc.

NOT become random viral content.

━━━━━━━━━━━━━━━━━━━━━

BAD EXAMPLES

❌ Random dance trend

❌ Viral sound with no connection
to the expertise journey

❌ Random meme unrelated
to the mission

━━━━━━━━━━━━━━━━━━━━━

GOOD EXAMPLES

✅ Viral trend showing challenge progress

✅ Lip-sync expressing frustration
during the challenge

✅ POV showing a setback

✅ Reaction to unexpected results

✅ Situation Content showing
a real creator struggle

━━━━━━━━━━━━━━━━━━━━━

The audience should feel:

"This is real TikTok."

NOT

"This is an online lesson."

━━━━━━━━━━━━━━━━━━━━━
[SOFT SELL LAYER - AUTOMATIC]
━━━━━━━━━━━━━━━━━━━━━

⚠️ Soft Sell is NOT a category
It's a "Layer" that can be inserted in any category

If there is "My Product", the system should:
✅ Insert Soft Sell at emotionally appropriate moments (no need to force by date)
✅ Insert when emotion is right, e.g., after Pain, Reality, or Hope
✅ NO direct selling
✅ NEVER use words like: Buy now / Promotion / Special price

Sell through:
✅ pain
✅ situation
✅ hidden truth
✅ wrong belief
✅ real consequence

CTA must be subtle, e.g.:
✅ "Who else is experiencing this?"
✅ "Try checking yours"
✅ "I'll share more about this later"

Goal:
Make people "want to reach out themselves"

━━━━━━━━━━━━━━━━━━━━━
[PRODUCT PLACEHOLDER SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

When creating scripts or Soft Sell
Use placeholder: {{PRODUCT}}

Example:
"That's when I started using {{PRODUCT}}
to help me get back to posting consistently again"

━━━━━━━━━━━━━━━━━━━━━
[QUESTION DIVERSITY SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL: DO NOT repeat the same question patterns frequently

The following words can be used MAX 2 times per category:
❌ Is it true?
❌ Right?
❌ Can you?
❌ Or not?

✅ Must alternate question patterns, e.g.:
- Why...
- What if...
- What happens if...
- When should...
- What do most people miss...
- What's the thing nobody tells you...
- What makes...
- What would you do if...
- If you could go back...
- Which is better...
- What should you know before...
- What's the sign that...
- What's the truth most people don't know...
- What's the first thing you should do...
- What makes most people fail...
- If you had to choose just one...
- Where's the turning point...
- What do most people misunderstand...
- What makes the results different...
- If you started fresh today...

━━━━━━━━━━━━━━━━━━━━━
[CONTENT ANGLE DIVERSITY SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL: DO NOT create topics from the same angle repeatedly

Even if it's the same subject, AI must create topics from multiple angles:

✅ Angles to use:
- Fear
- Shame
- Regret
- Hope
- Mistakes
- Success
- Truth discovered
- Decision-making
- Turning point
- Past vs Present comparison
- Old audience vs New audience
- Income
- Time
- Discipline
- Consistency
- Starting over
- Pressure
- Expectations
- Wrong beliefs

❌ DO NOT create topics that are just Part 1, Part 2, Part 3 without new angles

✅ If making sequential episodes, each must have:
- New perspective
- New situation
- New outcome

⚠️ CRITICAL RULE:
If 2 topics are more than 70% similar in meaning
Rewrite immediately

━━━━━━━━━━━━━━━━━━━━━
[DIRECT JOURNEY GENERATION SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL

The system must NOT generate:

❌ 50 Content Ideas
❌ 50 Foundation Ideas
❌ Intermediate Idea Lists
❌ Idea Brainstorm Lists

━━━━━━━━━━━━━━━━━━━━━

The system must generate directly:

Creator Identity
↓
Main Story Arc
↓
Expertise Journey
↓
Mission Execution
↓
Mission Results
↓
90-Day Plan

━━━━━━━━━━━━━━━━━━━━━

Think in this order:

Who is the creator?
↓
What is the mission?
↓
What expertise is required?
↓
What actions will be taken?
↓
What results may happen?
↓
Generate the full 90-Day Plan

━━━━━━━━━━━━━━━━━━━━━

NOT

Creator Identity
↓
50 Ideas
↓
90-Day Plan

━━━━━━━━━━━━━━━━━━━━━

The 90-Day Plan is the primary output.

No idea generation stage is allowed.

━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL

Every 5-10 days
the audience must see:

1. Knowledge
2. Mission
3. Execution
4. Results
5. Lessons Learned

NOT knowledge only.
NOT mission only.

Both must exist.

━━━━━━━━━━━━━━━━━━━━━

FINAL RULE

The system should never output:
"50 Foundation Ideas"
under any circumstance.

Generate the 90-Day Plan immediately.
Always.

━━━━━━━━━━━━━━━━━━━━━
[90-DAY STORY ARC SYSTEM - NEW]
━━━━━━━━━━━━━━━━━━━━━

CRITICAL:

The 90-Day Plan is NOT a content calendar.

The 90-Day Plan is an Expertise Journey.

━━━━━━━━━━━━━━━━━━━━━

STEP 1

Select ONE primary mission from:

⭐ What I'm Proving

This becomes the MAIN STORY ARC
for the entire 90-Day Plan.

━━━━━━━━━━━━━━━━━━━━━

STEP 2

At least 60% of all content
must directly connect to the Main Story Arc.

Examples:

✅ Progress updates
✅ Failures
✅ Setbacks
✅ Experiments
✅ Wins
✅ Turning points
✅ Emotional breakdowns
✅ Discoveries
✅ Unexpected outcomes
✅ Lessons learned

━━━━━━━━━━━━━━━━━━━━━

STEP 3

All emotion categories should support
the Main Story Arc.

Shock
Pain
Reality
Hope
POV
Trend
Expert
Insight
Series

must all connect back to the same mission.

━━━━━━━━━━━━━━━━━━━━━

Example:

Main Story Arc:

"Can I make 100,000 baht in 90 days?"

Pain:

"The day I almost gave up."

Shock:

"I discovered something that changed everything."

Reality:

"The truth nobody told me about content."

Hope:

"The first sign this might actually work."

POV:

"POV: Watching your channel struggle."

Expert:

"What I learned after 30 days."

Insight:

"The mistake that slowed me down."

Trend:

"Using a trend to document progress."

Series:

"Weekly challenge update."

━━━━━━━━━━━━━━━━━━━━━

STEP 4

The audience must always feel:

"I need to come back tomorrow
to see what happens next."

Every 5-10 days include:

✅ Progress
✅ Failure
✅ Conflict
✅ Turning Point
✅ Hope
✅ Surprise
✅ Cliffhanger

━━━━━━━━━━━━━━━━━━━━━

STEP 5

The creator must visibly transform.

Day 1 creator should feel different
from Day 90 creator.

Show:

✅ Doubt → Confidence
✅ Confusion → Clarity
✅ Inconsistency → Discipline
✅ Struggle → Progress
✅ Failure → Improvement

━━━━━━━━━━━━━━━━━━━━━

CRITICAL RULE

The 90-Day Plan should feel like:

Netflix Documentary
Expertise Journey
Public Challenge
Knowledge Progression

NOT:

Content Calendar
Educational Schedule
Posting Strategy

━━━━━━━━━━━━━━━━━━━━━

CONTENT DISTRIBUTION RULE

60% = Main Story Arc

20% = Supporting Insights

10% = Trend / Lip-sync / POV / Roleplay / Reaction / Situation Content

10% = Community / Audience Interaction

This ensures the audience follows
the creator's mission,
not random content topics.

━━━━━━━━━━━━━━━━━━━━━

FINAL TEST

Before generating the 90-Day Plan,
ask internally:

"If someone misses Day 7,
will they feel like they missed part of the story?"

If the answer is NO,

rewrite the plan.

If the answer is YES,

the story arc is working correctly.

━━━━━━━━━━━━━━━━━━━━━

TABLE FORMAT

⚠️ CRITICAL: Must create complete Day 1–90 table for ALL days

❌ ABSOLUTELY FORBIDDEN:
- DO NOT summarize as "repeat this pattern for 90 days"
- DO NOT use words like "repeat this", "do again", "use same pattern"
- DO NOT abbreviate / skip / use "..."
- DO NOT write only partial examples
- DO NOT reference idea numbers (e.g., "46 EP1", "17 EP2")
- DO NOT use idea IDs or foundation idea references
- DO NOT use abbreviated topic titles

✅ MUST DO:
- Show complete Day 1–90 table for ALL days
- Every row must have: Day | Phase | Category | Format | Topic
- Write all 90 days, no need to ask to continue
- **Must generate directly from:**
  * Creator Identity
  * Main Story Arc
  * Expertise Journey
  * Mission Execution Loop
- **60% must connect to Main Story Arc**

━━━━━━━━━━━━━━━━━━━━━
[TOPIC EXPANSION RULE - CRITICAL]
━━━━━━━━━━━━━━━━━━━━━

⚠️ The Topic column must contain:

✅ A complete content topic
✅ Self-contained and readable without context
✅ Understandable without referring to another list
✅ Ready to film immediately

❌ NOT a shortened title
❌ NOT an abbreviated reference
❌ NOT an idea label
❌ NOT an idea number

━━━━━━━━━━━━━━━━━━━━━

Forbidden Examples:

❌ 46 EP1 ประกาศภารกิจ 90 วัน
❌ 17 EP1 ผู้ติดตามไม่ใช่ยอดดู
❌ 39 EP2 วิเคราะห์คลิปพุ่ง
❌ เหตุผลที่คนกลับมาแล้วเลิก
❌ ผู้ติดตามไม่ใช่ยอดดู
❌ วิเคราะห์คลิปพุ่ง

━━━━━━━━━━━━━━━━━━━━━

Correct Examples:

✅ ประกาศภารกิจ 90 วัน ปั้นช่องให้สร้างรายได้ 100,000 บาท
✅ ทำไมผู้ติดตาม 2,000 คน ไม่ได้แปลว่าจะมีคนดู 2,000 คน
✅ วิเคราะห์คลิปที่มียอดดูสูงที่สุดในเดือนนี้ และอะไรที่ทำให้คนดูจนจบ
✅ ทำไมหลายคนกลับมาทำช่องอีกครั้ง แต่เลิกภายใน 30 วัน

━━━━━━━━━━━━━━━━━━━━━

The 90-Day Plan must read like:

A real content plan.

NOT a spreadsheet referencing idea IDs.

━━━━━━━━━━━━━━━━━━━━━

Display as table:

| Day | Phase | Category | Format | Topic |
| 1 | Opening | Shock | POV | [Complete expanded topic here] |
| 2 | Opening | Pain | Story | [Complete expanded topic here] |
| 3 | Opening | Pain | Story | [Complete expanded topic here] |
...
| 90 | Close+Reopen | Series | Talk | [Complete expanded topic here] |

━━━━━━━━━━━━━━━━━━━━━
[ROLLER COASTER EMOTION SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

90-day content must arrange emotions like:
Up → Down → Up → Down → Peak → Release → Peak

❌ DO NOT arrange flatly
❌ DO NOT keep same emotion for too long

Goal:
Make people want to keep watching non-stop
Like watching a real-life series

━━━━━━━━━━━━━━━━━━━━━
[30-DAY PATTERN - 6 PHASES]
━━━━━━━━━━━━━━━━━━━━━

Day 1-5: Opening (Shock / Pain)
Day 6-10: Pull them in (Reality / Curiosity)
Day 11-15: Build credibility (Expert / Trend)
Day 16-20: Push hope (Hope / Insight)
Day 21-25: Suspense+Follow (POV / Series)
Day 26-30: Close+Reopen (Series / Insight)

Use this pattern 3 cycles (Day 1-30, 31-60, 61-90)

━━━━━━━━━━━━━━━━━━━━━
[EXPERTISE DELIVERY SYSTEM]
━━━━━━━━━━━━━━━━━━━━━

System must optimize for:
✅ knowledge delivery
✅ curiosity loops
✅ progressive learning
✅ audience transformation
✅ relatability
✅ real-world application
✅ practical value
✅ anticipation for next insight

NOT:
❌ lifestyle content
❌ creator's daily life
❌ motivational speeches
❌ entertainment without value

The goal is:
Make people trust the creator's expertise.

People return because they receive value repeatedly.

━━━━━━━━━━━━━━━━━━━━━
[EXPERTISE FIRST SYSTEM V3 - FINAL]
━━━━━━━━━━━━━━━━━━━━━

⚠️ CRITICAL

The creator is NOT the product.

The expertise is the product.

People do not follow because they care about the creator's daily life.

People follow because the creator repeatedly reveals useful knowledge.

━━━━━━━━━━━━━━━━━━━━━

NEW PRIMARY QUESTION

Before generating any content ask:

"What useful thing will the viewer learn?"

NOT

"What happened in the creator's life?"

━━━━━━━━━━━━━━━━━━━━━

CORE PRINCIPLE

People follow expertise.

People stay because they continuously receive value.

People return because they believe this creator knows something useful.

━━━━━━━━━━━━━━━━━━━━━

VALUE FIRST

Emotion Second.

Story Second.

Lifestyle Second.

Expertise First.

━━━━━━━━━━━━━━━━━━━━━

CRITICAL

Every content must contain at least one:

✅ lesson

✅ framework

✅ checklist

✅ comparison

✅ diagnosis

✅ decision process

✅ case study

✅ hidden truth

✅ workflow

✅ real-world insight

If none exist,

rewrite the content.

━━━━━━━━━━━━━━━━━━━━━

EXPERTISE DEMONSTRATION RULE

Do NOT tell viewers you are an expert.

Show expertise through:

✅ analysis

✅ comparisons

✅ explanations

✅ decisions

✅ examples

✅ observations

✅ diagnosis

Viewers trust demonstrations.

Not claims.

Bad:

"I am good at insurance."

Good:

"Here is why two people of the same age can pay different premiums."

Bad:

"I am an expert sticker designer."

Good:

"Here is why PET survives outdoors longer than PVC."

━━━━━━━━━━━━━━━━━━━━━

AUDIENCE ATTACHMENT RULE

The goal is NOT:

"Make viewers attached to the creator."

The goal IS:

"Make viewers trust the creator's expertise."

Viewers should feel:

✅ I learned something.

✅ I understand this better.

✅ I never knew this before.

✅ I want to learn more from this creator.

━━━━━━━━━━━━━━━━━━━━━

REPLACE CREATOR TRANSFORMATION

Old:

Day 1 creator should feel different from Day 90 creator.

New:

Day 1 audience understanding should feel different from Day 90 audience understanding.

The audience should become:

✅ smarter

✅ more informed

✅ more aware

✅ more confident

✅ better at making decisions

━━━━━━━━━━━━━━━━━━━━━

REPLACE REALITY SHOW RULE

Old:

The 90-Day Plan is a Reality Show.

New:

The 90-Day Plan is an Expertise Journey.

The creator's journey exists only to provide context.

The expertise journey is the main attraction.

━━━━━━━━━━━━━━━━━━━━━

CONTENT DISTRIBUTION

Expert = 60%

Expert + Case Study = 10%

Expert + Reality = 10%

Expert + POV / Roleplay = 5%

Expert + Trend / Reaction = 5%

Expert + Story = 5%

Expert + Soft Sell = 5%

━━━━━━━━━━━━━━━━━━━━━

CRITICAL

Expert is NOT a category.

Expert is the foundation of every category.

Every content must provide useful knowledge.

Only the delivery format changes.

━━━━━━━━━━━━━━━━━━━━━

FINAL SUCCESS TEST

After consuming the content,

the viewer should say:

"I learned something useful."

"I trust this creator."

"I want to come back for more."

NOT

"I know more about the creator's life."

━━━━━━━━━━━━━━━━━━━━━

FINAL VIEWER FEELING

Replace:

"I want to keep watching this person's life."

With:

"I want to keep learning from this person."

or

"I want to see what this person reveals next."

━━━━━━━━━━━━━━━━━━━━━

FINAL RULE

The creator's life is only useful when it helps explain expertise.

Never create lifestyle content unless it directly supports expertise.

Expertise First.

Always.

━━━━━━━━━━━━━━━━━━━━━
[NEW TRUE CORE QUESTION]
━━━━━━━━━━━━━━━━━━━━━

The system must think:

"What useful thing will the viewer learn next?"

NOT

"What happened in the creator's life?"

The audience should return because:

✅ they gain knowledge

✅ they gain understanding

✅ they gain insight

✅ they gain decision-making ability

NOT because they are following the creator's lifestyle.

━━━━━━━━━━━━━━━━━━━━━

All outputs MUST be in Thai.

━━━━━━━━━━━━━━━━━━━━━━`;
                          
                            if (USE_SHORT_CHATGPT) {
                              // โหมดย่อ: กดปุ่มเดียว ChatGPT เจนให้เลย
                              openInChatGPTAuto(generateAuthorityPromptShort({ whatIDo, myExpertise, myProof, myProduct, myAudience }));
                            } else {
                              // โหมดเต็ม (backup): เปิด ChatGPT + คัดลอก prompt เต็ม ให้กด Ctrl+V
                              openInChatGPT(fullPrompt);
                            }

                            setTimeout(() => {
                              setIsCopyingIdentityPrompt(false);
                              setCopiedIdentityPrompt(true);
                              
                              setTimeout(() => {
                                setCopiedIdentityPrompt(false);
                              }, 5000);
                            }, 500);
                          }}
                          disabled={isCopyingIdentityPrompt || copiedIdentityPrompt}
                          className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg transition-all text-xs font-semibold disabled:opacity-90 disabled:cursor-not-allowed"
                        >
                          {isCopyingIdentityPrompt ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              กำลังเปิด ChatGPT...
                            </>
                          ) : copiedIdentityPrompt ? (
                            <>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {USE_SHORT_CHATGPT ? 'เปิด ChatGPT แล้ว!' : 'เปิดแล้ว! วาง Ctrl+V'}
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3.5 h-3.5" />
                              สร้างใน ChatGPT
                            </>
                          )}
                        </button>
                      </div>
                      <div className="hidden bg-slate-900/50 rounded-lg p-3 border border-blue-400/20 max-h-64 overflow-y-auto">
                        <pre className="text-slate-300 text-xs whitespace-pre-wrap font-mono leading-relaxed">
{`You are a TikTok Content Creator Specialist...

[INPUT FROM USER]
━━━━━━━━━━━━━━━━━━━━━

ฉันทำ: ${whatIDo}
ความเชี่ยวชาญของฉัน: ${myExpertise}
⭐ ฉันกำลังพิสูจน์: ${myProof}${myProduct ? `\nสินค้าของฉัน: ${myProduct}` : ''}

━━━━━━━━━━━━━━━━━━━━━
[OUTPUT]
━━━━━━━━━━━━━━━━━━━━━

✅ Creator Identity (รวม MAIN STORY ARC)
✅ 100 หัวข้อคอนเทนต์ (10 หมวด)
✅ 90-day content plan (Expertise Journey)

(คลิก "สร้างใน ChatGPT" — ระบบจะเปิด ChatGPT แล้วสร้างคอนเทนต์ให้อัตโนมัติ)`}
                        </pre>
                        </div>
                      </div>
                    )}

                    {/* Story Discovery Mode */}
                    {selectedMode === 'story' && (
                      <div className="hidden bg-gradient-to-br from-orange-500/10 to-amber-500/10 border-2 border-orange-400/30 rounded-xl p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2.5 rounded-lg">
                          <BookOpenIcon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 flex items-center gap-2">
                          <h3 className="text-lg font-bold text-white">Story Discovery Mode</h3>
                          <div className="group relative">
                            <Info className="w-4 h-4 text-orange-300 cursor-help" />
                            <div className="absolute left-0 top-6 w-64 bg-slate-800 border border-orange-400/30 rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl">
                              <p className="text-xs font-semibold text-orange-300 mb-2">เหมาะสำหรับ:</p>
                              <ul className="text-xs text-slate-300 space-y-1 mb-3">
                                <li>• ท่องเที่ยว</li>
                                <li>• สารคดี</li>
                                <li>• ประวัติศาสตร์</li>
                                <li>• เรื่องลึกลับ</li>
                                <li>• คดี</li>
                                <li>• เล่าเรื่อง</li>
                                <li>• ความรู้เชิงเล่า</li>
                              </ul>
                              <p className="text-xs font-semibold text-yellow-300 mb-1">ผลลัพธ์:</p>
                              <p className="text-xs text-slate-300">"เล่าต่อสิ"</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-white">สร้างคอนเทนต์ด้วย AI</h4>
                        <button
                          onClick={async () => {
                            setIsCopyingStoryPrompt(true);
                            
                            const storyPromptText = generateStoryDiscoveryPrompt({
                              whatIDo,
                              myExpertise,
                              myProof,
                              myProduct
                            });
                            
                            setStoryPrompt(storyPromptText);
                            if (USE_SHORT_CHATGPT) {
                              // โหมดย่อ: กดปุ่มเดียว ChatGPT เจนให้เลย
                              openInChatGPTAuto(generateStoryPromptShort({ whatIDo, myExpertise, myProof, myProduct, myAudience }));
                            } else {
                              // โหมดเต็ม (backup): เปิด ChatGPT + คัดลอก prompt เต็ม ให้กด Ctrl+V
                              openInChatGPT(storyPromptText);
                            }

                            setTimeout(() => {
                              setIsCopyingStoryPrompt(false);
                              setCopiedStoryPrompt(true);
                              
                              setTimeout(() => {
                                setCopiedStoryPrompt(false);
                              }, 5000);
                            }, 500);
                          }}
                          disabled={isCopyingStoryPrompt || copiedStoryPrompt}
                          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg transition-all text-xs font-semibold disabled:opacity-90 disabled:cursor-not-allowed"
                        >
                          {isCopyingStoryPrompt ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              กำลังเปิด ChatGPT...
                            </>
                          ) : copiedStoryPrompt ? (
                            <>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {USE_SHORT_CHATGPT ? 'เปิด ChatGPT แล้ว!' : 'เปิดแล้ว! วาง Ctrl+V'}
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3.5 h-3.5" />
                              สร้างใน ChatGPT
                            </>
                          )}
                        </button>
                      </div>
                      <div className="hidden bg-slate-900/50 rounded-lg p-3 border border-orange-400/20 max-h-64 overflow-y-auto">
                        <pre className="text-slate-300 text-xs whitespace-pre-wrap font-mono leading-relaxed">
{`You are a TikTok Story Discovery Content Specialist...

[CORE PRINCIPLE]
━━━━━━━━━━━━━━━━━━━━━

The creator is NOT the product.
The story is the product.

People follow because they want to discover something interesting.
NOT because they want to learn a framework.

[CONTENT DISTRIBUTION]
━━━━━━━━━━━━━━━━━━━━━

70% Story (เรื่องเล่าหลัก)
20% Hidden Facts (เกร็ด ความเชื่อ เบื้องหลัง)
10% Practical Tips (ทริค ข้อควรรู้)

[INPUT FROM USER]
━━━━━━━━━━━━━━━━━━━━━

ฉันทำ: ${whatIDo}
ความเชี่ยวชาญของฉัน: ${myExpertise}
⭐ ฉันกำลังพิสูจน์: ${myProof}${myProduct ? `\nสินค้าของฉัน: ${myProduct}` : ''}

━━━━━━━━━━━━━━━━━━━━━
[OUTPUT]
━━━━━━━━━━━━━━━━━━━━━

✅ Creator Identity + Main Discovery Theme
✅ 90-day Story Discovery Plan (ตาราง 90 วัน)
✅ Monetization Roadmap

(คลิก "สร้างใน ChatGPT" — ระบบจะเปิด ChatGPT แล้วสร้างคอนเทนต์ให้อัตโนมัติ)`}
                        </pre>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3 pt-2">
                    <button
                      onClick={() => setCurrentStep(3)}
                      className="text-slate-400 hover:text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors"
                    >
                      ← ย้อนกลับ
                    </button>
                    <button
                      onClick={async () => {
                        setIsSaving(true);
                        try {
                          console.log('💾 [Save Identity] Starting save for username:', currentUsername);
                          
                          // derive "ฉันกำลังพิสูจน์" จากความท้าทาย 60 วัน + เป้าหมายที่เลือก (กันกรณี myProof ยังไม่ถูกตั้ง)
                          const derivedProof = [
                            myChallenge.trim() ? `ความท้าทายภายใน 60 วัน: ${myChallenge.trim()}` : '',
                            myGoals.length ? deriveProofFromGoals(myGoals) : '',
                          ].filter(Boolean).join(' | ') || myProof;
                          const identityData = {
                            whatIDo,
                            myAudience,
                            myExpertise,
                            myProof: derivedProof,
                            myGoals,
                            myChallenge,
                            myProduct,
                            savedAt: new Date().toISOString()
                          };
                          
                          console.log('📦 [Save Identity] Data to save:', identityData);
                          
                          // Save to Firestore
                          const userDocRef = doc(db, 'coursedee_v1_users', currentUsername);
                          await setDoc(userDocRef, {
                            creatorIdentity: identityData,
                            updatedAt: new Date().toISOString()
                          }, { merge: true });
                          
                          console.log('✅ [Save Identity] Saved to Firestore successfully');
                          
                          // Verify the save by reading it back
                          const verifyDoc = await getDoc(userDocRef);
                          if (verifyDoc.exists() && verifyDoc.data().creatorIdentity) {
                            console.log('✅ [Verify] Data verified in Firestore:', verifyDoc.data().creatorIdentity);
                          } else {
                            console.error('❌ [Verify] Data NOT found in Firestore after save!');
                            throw new Error('Failed to verify saved data');
                          }
                          
                          // Also save to localStorage as backup
                          localStorage.setItem(`coursedee_creator_identity_${currentUsername}`, JSON.stringify(identityData));
                          console.log('✅ [Backup] Saved to localStorage');
                          
                          setCreatorIdentity(identityData);
                          
                          const identityStr = `ฉันทำ: ${whatIDo}
ความเชี่ยวชาญของฉัน: ${myExpertise}
ฉันกำลังพิสูจน์: ${myProof}${myProduct ? `\nสินค้าของฉัน: ${myProduct}` : ''}`;
                          setIdentityText(identityStr);
                          
                          alert('✅ บันทึกตัวตนสำเร็จ! ระบบกำลังสร้างหัวข้อทั้ง 2 สายให้อัตโนมัติ');
                          setCurrentStep(0);
                          autoGenerateBothSets(); // เจนหัวข้อ Authority + Story ให้เลย (เฉพาะที่ยังว่าง)
                        } catch (error) {
                          console.error('Error saving identity:', error);
                          alert('❌ เกิดข้อผิดพลาดในการบันทึก');
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      disabled={isSaving}
                      className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-2.5 px-6 rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      {isSaving ? 'กำลังบันทึก...' : 'บันทึกตัวตน'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== ดีไซน์ใหม่ (นำเข้าจาก Claude Design) =====
  const rootStyle: React.CSSProperties = {
    ...(CD_THEME[theme].vars as Record<string, string>),
    ['--g1' as any]: CD_G1,
    ['--g2' as any]: CD_G2,
    minHeight: '100vh',
    background: CD_THEME[theme].bg,
    color: 'var(--ink)',
    fontFamily: "'IBM Plex Sans Thai', system-ui, sans-serif",
  };
  const hasIdentity = !!creatorIdentity;
  const goalLabels = CONTENT_GOALS.filter((g) => myGoals.includes(g.key)).map((g) => g.label);
  const modeIcons: Record<ContentMode, any> = { authority: GraduationCap, story: BookOpenIcon, softsell: ShoppingBag };
  // เลขคลิปเริ่มต้นของชุดที่กำลังดู (ชุด 2 เริ่มคลิป 31) — เพื่อแสดง badge ต่อเนื่อง
  const clipDisplayOffset = sets.slice(0, activeSetIdx[activeTab]).reduce((sum, s) => sum + s.topics.length, 0);
  const openIdentityWizard = () => {
    if (creatorIdentity) {
      setWhatIDo(creatorIdentity.whatIDo || '');
      setMyExpertise(creatorIdentity.myExpertise || creatorIdentity.myCustomers || '');
      setMyAudience(creatorIdentity.myAudience || '');
      setMyProof(creatorIdentity.myProof || '');
      setMyProduct(creatorIdentity.myProduct || '');
    }
    setCurrentStep(1);
  };

  return (
    <div style={rootStyle}>
      {/* ปุ่มอัปเดต — โผล่เฉพาะเมื่อมีเวอร์ชันใหม่ (กดแล้วรีโหลดหน้าเว็บให้เป็นเวอร์ชันล่าสุด) */}
      {updateAvailable && (
        <button
          onClick={() => window.location.reload()}
          style={{ position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px', borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,var(--g1),var(--g2))', boxShadow: '0 12px 32px -6px rgba(124,92,255,.65)' }}
        >
          <RefreshCw className="w-4 h-4" /> มีอัปเดตใหม่ — กดอัปเดต
        </button>
      )}
      {/* ===== Topbar ===== */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: '1px solid var(--bd)', background: 'var(--topbar)', backdropFilter: 'blur(12px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 33, height: 33, borderRadius: 9, background: 'linear-gradient(135deg,var(--g1),var(--g2))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15 }}>A</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 17, color: 'var(--ink)' }}>AIForYou</span>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, fontSize: 10, color: 'var(--cv)', background: 'rgba(124,92,255,.16)', padding: '4px 6px', borderRadius: 5 }}>V1</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={toggleTheme} title="สลับโหมดสว่าง/มืด" style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--sbg)', border: '1px solid var(--bd)', color: 'var(--ink2)', padding: '7px 12px', borderRadius: 99, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500 }}>
            <span style={theme === 'light' ? { width: 13, height: 13, borderRadius: '50%', background: 'linear-gradient(135deg,var(--g1),var(--g2))', flex: 'none' } : { width: 13, height: 13, borderRadius: '50%', boxShadow: 'inset -3px -2px 0 var(--ink2)', flex: 'none' }}></span>
            <span className="hidden sm:inline">{theme === 'light' ? 'โหมดมืด' : 'โหมดสว่าง'}</span>
          </button>
          <a href="/" title="กลับหน้าแรก" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 99, background: 'var(--sbg)', border: '1px solid var(--bd)' }}>
            <Home className="w-4 h-4" style={{ color: 'var(--ink2)' }} />
          </a>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--sbg)', border: '1px solid var(--bd)', padding: '4px 12px 4px 4px', borderRadius: 99 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg,#ff8fab,#c77dff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12 }}>{currentUsername[0]?.toUpperCase() || 'U'}</div>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink2)', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUsername}</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 16px 64px' }}>
        {/* ===== Hero ===== */}
        <div className="cd-hero" style={{ textAlign: 'center', padding: '32px 0 22px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--sbg)', border: '1px solid var(--bd)', padding: '5px 13px', borderRadius: 99, marginBottom: 14 }}>
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: hasIdentity ? '#34d399' : '#f59e0b', boxShadow: hasIdentity ? '0 0 8px #34d399' : '0 0 8px #f59e0b' }}></span>
            <span style={{ fontSize: 12.5, color: 'var(--sub)' }}>{hasIdentity ? `ตัวตนพร้อมใช้งาน · ${whatIDo || currentUsername}` : 'ยังไม่ได้ตั้งค่าตัวตน'}</span>
          </div>
          <h1 style={{ margin: '0 0 8px', fontWeight: 700, color: 'var(--ink)', letterSpacing: '-.01em', lineHeight: 1.15, fontSize: 'clamp(22px,4vw,30px)' }}>
            Prompt Builder <span style={{ background: 'linear-gradient(90deg,var(--g1),var(--g2))', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>สำหรับคนทำคอนเทนต์</span>
          </h1>
          <p style={{ margin: 0, fontSize: 14.5, color: 'var(--sub)' }}>ระบบสร้าง Prompt ดีดีไว้ — ใช้ง่าย ได้ผลจริง</p>
        </div>

        {/* ===== Shell 2 คอลัมน์ ===== */}
        <div className="cd-shell">
          {/* Sidebar */}
          <aside style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'var(--panel)', border: '1px solid var(--bd)', borderRadius: 16, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, fontSize: 9.5, letterSpacing: '.12em', color: 'var(--sub2)' }}>ตัวตนของคุณ</div>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: hasIdentity ? '#34d399' : 'var(--track2)', boxShadow: hasIdentity ? '0 0 0 3px rgba(52,211,153,.15)' : 'none' }}></span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div><div style={{ fontSize: 11, color: 'var(--sub2)', marginBottom: 2 }}>ธุรกิจ</div><div style={{ fontSize: 13, color: 'var(--ink2)' }}>{whatIDo || '—'}</div></div>
                <div><div style={{ fontSize: 11, color: 'var(--sub2)', marginBottom: 2 }}>กลุ่มเป้าหมาย</div><div style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.5 }}>{myAudience || '—'}</div></div>
                {goalLabels.length > 0 && (
                  <div><div style={{ fontSize: 11, color: 'var(--sub2)', marginBottom: 6 }}>เป้าหมาย</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{goalLabels.map((g) => (<span key={g} style={{ fontSize: 11.5, color: 'var(--cv)', background: 'rgba(124,92,255,.12)', border: '1px solid rgba(124,92,255,.28)', padding: '3px 9px', borderRadius: 99 }}>{g}</span>))}</div></div>
                )}
              </div>
              <button onClick={openIdentityWizard} style={{ marginTop: 15, width: '100%', border: '1px solid var(--bd2)', background: 'transparent', color: 'var(--soft)', fontSize: 12.5, padding: 9, borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit' }}>{hasIdentity ? 'แก้ไขตัวตน' : 'ตั้งค่าตัวตน'}</button>
            </div>

            {myChallenge && myChallenge.trim() && (
              <div style={{ background: 'linear-gradient(135deg,rgba(124,92,255,.16),rgba(255,92,157,.1))', border: '1px solid rgba(124,92,255,.3)', borderRadius: 16, padding: 16 }}>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, fontSize: 9.5, letterSpacing: '.12em', color: 'var(--cv)', marginBottom: 8 }}>ท้าทาย 60 วัน</div>
                <div style={{ fontSize: 13.5, color: 'var(--ink)', fontWeight: 500, lineHeight: 1.45 }}>{myChallenge}</div>
              </div>
            )}

            {/* การ์ดสูตรคอนเทนต์ 80-15-5 + ความคืบหน้าการเจนหัวข้อ (ต่อแต่ละหมวด) */}
            <div style={{ background: 'var(--panel)', border: '1px solid var(--bd)', borderRadius: 16, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <PieChart className="w-4 h-4" style={{ color: 'var(--cv)' }} />
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>สูตรคอนเทนต์ 80-15-5</div>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--sub)', lineHeight: 1.55, marginBottom: 13 }}>
                สัดส่วนที่แนะนำสำหรับแผน 60 วัน — <strong style={{ color: 'var(--soft)' }}>เล่าเรื่องดึงคนตามเยอะ ๆ (80%)</strong> ให้ความรู้บ้าง (15%) ขายนิดเดียว (5%) คนถึงจะติดตามแล้วซื้อเอง
              </div>
              {(() => {
                const pct: Record<ContentMode, number> = { story: 80, authority: 15, softsell: 5 };
                const days: Record<ContentMode, number> = { story: 48, authority: 9, softsell: 3 };
                const rowOrder: ContentMode[] = ['story', 'authority', 'softsell']; // เรียงตามสัดส่วนมาก→น้อย
                // นับ "หัวข้อที่เจนมา" (ตัวส่วน) และ "สคริปต์ที่สร้างแล้ว" (ตัวตั้ง) ต่อหมวด — เป็นจำนวนเต็ม
                const topicsN: Record<ContentMode, number> = {
                  authority: topicSetsByMode.authority.reduce((n, s) => n + s.topics.length, 0),
                  story: topicSetsByMode.story.reduce((n, s) => n + s.topics.length, 0),
                  softsell: topicSetsByMode.softsell.reduce((n, s) => n + s.topics.length, 0),
                };
                const scriptsN: Record<ContentMode, number> = {
                  authority: topicSetsByMode.authority.reduce((n, s) => n + s.scripts.length, 0),
                  story: topicSetsByMode.story.reduce((n, s) => n + s.scripts.length, 0),
                  softsell: topicSetsByMode.softsell.reduce((n, s) => n + s.scripts.length, 0),
                };
                const totalTopics = topicsN.authority + topicsN.story + topicsN.softsell;
                const totalScripts = scriptsN.authority + scriptsN.story + scriptsN.softsell;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {rowOrder.map((k) => {
                      const made = scriptsN[k], total = topicsN[k]; // สคริปต์ที่ทำ / หัวข้อที่เจน
                      const ratio = total > 0 ? Math.min(1, made / total) : 0;
                      const done = total > 0 && made >= total;
                      const Icon = modeIcons[k];
                      return (
                        <div key={k}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5, gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                              <Icon className="w-3.5 h-3.5" style={{ flex: 'none', color: 'var(--cv)' }} />
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{MODE_LABEL[k]}</span>
                            </div>
                            <span style={{ fontSize: 10.5, color: 'var(--sub2)', flex: 'none' }}>แนะนำ {pct[k]}% · ~{days[k]} วัน</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 6, borderRadius: 99, background: 'var(--track2)', overflow: 'hidden' }}>
                              <div style={{ width: `${ratio * 100}%`, height: '100%', borderRadius: 99, background: done ? 'linear-gradient(90deg,#34d399,#10b981)' : 'linear-gradient(90deg,var(--g1),var(--g2))', transition: 'width .4s' }}></div>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: done ? '#34d399' : 'var(--sub)', flex: 'none', minWidth: 40, textAlign: 'right' }}>{made}/{total}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ marginTop: 2, paddingTop: 11, borderTop: '1px solid var(--bd)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11.5, color: 'var(--sub)' }}>เจนหัวข้อแล้ว</span>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>{totalTopics} หัวข้อ</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11.5, color: 'var(--sub)' }}>สร้างสคริปต์แล้ว</span>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cv)' }}>{totalScripts} สคริปต์</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </aside>

          {/* Main */}
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 📁 แถบ Project (แอดมินเท่านั้น) — สร้างใหม่ / สลับโปรเจกต์ที่เคยเจนไว้ */}
            {isAdmin && (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--panel)', border: '1px solid var(--bd)', borderRadius: 14, padding: '8px 10px' }}>
                <span style={{ fontSize: 12, color: 'var(--sub2)', fontWeight: 600, flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}><BookText className="w-4 h-4" style={{ color: 'var(--cv)' }} /> โปรเจกต์</span>
                <button onClick={() => setShowProjectMenu((v) => !v)} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 10, padding: '9px 12px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)', fontSize: 13, fontWeight: 600 }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{projects.find((p) => p.id === activeProjectId)?.name || (activeProjectId ? 'โปรเจกต์' : 'ค่าเริ่มต้น')}</span>
                  <ArrowRight className="w-4 h-4" style={{ flex: 'none', color: 'var(--sub2)', transform: showProjectMenu ? 'rotate(90deg)' : 'rotate(90deg)' }} />
                </button>
                <button onClick={handleNewProject} style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontWeight: 700, fontSize: 12.5, padding: '9px 13px', borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                  <Sparkles className="w-4 h-4" /> โปรเจกต์ใหม่
                </button>
                {/* 🧑‍🎨 ปุ่มตัวละครของโปรเจกต์ (สำหรับเจนภาพฉากหน้าเดิม) */}
                <button onClick={() => setShowCharPanel((v) => !v)} title="ตัวละครของโปรเจกต์ (ใช้เจนภาพประกอบ)" style={{ flex: 'none', width: 38, height: 38, borderRadius: 10, padding: 0, overflow: 'hidden', border: character?.imageB64 ? '2px solid var(--cv)' : '1px dashed var(--bd2)', background: 'var(--field)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {character?.imageB64
                    ? <img src={`data:${character.mime || 'image/jpeg'};base64,${character.imageB64}`} alt="ตัวละคร" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <User className="w-4 h-4" style={{ color: 'var(--sub2)' }} />}
                </button>
                {showProjectMenu && (
                  <>
                    <div onClick={() => setShowProjectMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                    <div style={{ position: 'absolute', top: '100%', left: 10, right: 10, marginTop: 6, zIndex: 31, background: 'var(--modal)', border: '1px solid var(--bd2)', borderRadius: 12, boxShadow: '0 20px 50px -20px rgba(0,0,0,.6)', overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
                      {projects.length === 0 ? (
                        <div style={{ padding: 16, fontSize: 12.5, color: 'var(--sub2)', textAlign: 'center' }}>ยังไม่มีโปรเจกต์ที่บันทึกไว้ — กด "โปรเจกต์ใหม่" เพื่อเริ่ม</div>
                      ) : (
                        projects.map((p) => {
                          const on = p.id === activeProjectId;
                          return (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--bd)', background: on ? 'rgba(124,92,255,.12)' : 'transparent' }}>
                              <button onClick={() => handleSelectProject(p.id)} style={{ flex: 1, minWidth: 0, textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '11px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
                                <span style={{ fontSize: 13, fontWeight: on ? 700 : 500, color: on ? 'var(--cv)' : 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                                {on && <CheckCircle2 className="w-4 h-4" style={{ flex: 'none', color: '#34d399' }} />}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); handleRenameProject(p.id); }} title="เปลี่ยนชื่อโปรเจกต์" style={{ flex: 'none', border: 'none', background: 'transparent', padding: '11px 12px', cursor: 'pointer', color: 'var(--sub2)', display: 'flex', alignItems: 'center' }}>
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* 🧑‍🎨 แผงตั้งค่าตัวละคร (แอดมิน) */}
            {isAdmin && showCharPanel && (
              <div style={{ background: 'var(--panel)', border: '1px solid rgba(124,92,255,.35)', borderRadius: 14, padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 7 }}><User className="w-4 h-4" style={{ color: 'var(--cv)' }} /> ตัวละครของโปรเจกต์</div>
                  <button onClick={() => setShowCharPanel(false)} style={{ border: 'none', background: 'transparent', color: 'var(--sub2)', cursor: 'pointer', fontFamily: 'inherit' }}><X className="w-4 h-4" /></button>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--sub)', lineHeight: 1.55, marginBottom: 12 }}>ตัวละครนี้จะเป็น "หน้าเดิม ชุดเดิม" ในทุกภาพประกอบฉากของโปรเจกต์นี้ — เอาภาพไปทำวิดีโอต่อใน Google Flow ได้เลย (~฿1.35/ภาพ)</div>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 'none', width: 108, height: 144, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--bd2)', background: 'var(--field)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isCharGenerating
                      ? <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--g1)' }} />
                      : character?.imageB64
                        ? <img src={`data:${character.mime || 'image/jpeg'};base64,${character.imageB64}`} alt="ตัวละคร" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 11, color: 'var(--sub2)', textAlign: 'center', padding: 8 }}>ยังไม่มี<br />ตัวละคร</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <textarea value={charDescDraft} onChange={(e) => setCharDescDraft(e.target.value)} placeholder="อธิบายตัวละคร — เช่น ช่างหนุ่มไทยวัย 30 ใส่เอี๊ยมส้ม หน้าตาเป็นมิตร ยิ้มเก่ง" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 10, padding: '10px 12px', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13, minHeight: 64, resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
                      <button onClick={handleGenCharacter} disabled={isCharGenerating} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontWeight: 700, fontSize: 12.5, padding: '9px 14px', borderRadius: 9, border: 'none', cursor: isCharGenerating ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: isCharGenerating ? 0.6 : 1 }}>
                        {isCharGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} เจนตัวละครด้วย AI
                      </button>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--bd2)', background: 'transparent', color: 'var(--soft)', fontWeight: 600, fontSize: 12.5, padding: '9px 14px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <FileDown className="w-4 h-4" style={{ transform: 'rotate(180deg)' }} /> อัปโหลดรูปที่มีอยู่
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadCharacter(f); e.target.value = ''; }} />
                      </label>
                      {character?.imageB64 && (
                        <a href={`data:${character.mime || 'image/jpeg'};base64,${character.imageB64}`} download="character.jpg" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--bd2)', color: 'var(--soft)', fontWeight: 600, fontSize: 12.5, padding: '9px 14px', borderRadius: 9, textDecoration: 'none', fontFamily: 'inherit' }}>⬇ ดาวน์โหลด</a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* สลับมุมมอง: เจนหัวข้อ | แผน 60 วัน | ผลลัพธ์ */}
            <div style={{ display: 'flex', gap: 6, background: 'var(--panel)', border: '1px solid var(--bd)', borderRadius: 14, padding: 6 }}>
              {(([
                { key: 'topics', label: 'เจนหัวข้อ', Icon: Sparkles },
                { key: 'plan', label: 'แผน 60 วัน', Icon: CalendarDays },
                { key: 'results', label: 'ผลลัพธ์', Icon: TrendingUp },
                ...(isAdmin ? [{ key: 'chat', label: 'แชท AI', Icon: MessageSquare }] : []),
              ]) as { key: 'topics' | 'plan' | 'results' | 'chat'; label: string; Icon: any }[]).map(({ key, label, Icon }) => {
                const on = mainView === key;
                return (
                  <button key={key} onClick={() => setMainView(key)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 6px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: on ? 700 : 600, border: 'none', color: on ? '#fff' : 'var(--sub)', background: on ? 'linear-gradient(135deg,var(--g1),var(--g2))' : 'transparent', whiteSpace: 'nowrap' }}>
                    <Icon className="w-4 h-4" style={{ flex: 'none' }} /> {label}
                  </button>
                );
              })}
            </div>

            {/* Panel: แผนจัดการคอนเทนต์ 60 วัน */}
            {mainView === 'plan' && (
              <div style={{ background: 'var(--panel)', border: '1px solid var(--bd)', borderRadius: 18, padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
                  <CalendarDays className="w-5 h-5" style={{ color: 'var(--cv)' }} />
                  <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>แผนถ่ายทำ 60 วัน</h2>
                </div>
                <p style={{ margin: '0 0 12px 30px', fontSize: 12.5, color: 'var(--sub)', lineHeight: 1.5 }}>สลับ Story / Authority วันเว้นวัน · บางวันมี "คลิปขาย (Soft-sell)" ให้เลือกลงเพิ่มเป็นคลิปที่ 2 ได้ (ไม่บังคับ — จะลง 2 คลิป หรือเลือกลงอย่างใดอย่างหนึ่งก็ได้) · แต่ละคลิปกด "สร้างสคริปต์" ได้เลย</p>
                {/* 🖊️ เล่าเรื่องเอง — เจอเหตุการณ์จริงระหว่างลุย เอามาเล่าให้ AI เจนเป็นสคริปต์แทรกในแผน */}
                <div style={{ margin: '0 0 16px 0' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: (showCustomClipBox || showCaseBox) ? 10 : 0 }}>
                    {!showCustomClipBox && (
                      <button onClick={() => { setShowCustomClipBox(true); setShowCaseBox(false); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, padding: '9px 14px', borderRadius: 10, border: '1px dashed var(--cv)', background: 'rgba(124,92,255,.08)', color: 'var(--cv)', cursor: 'pointer', fontFamily: 'inherit' }}>
                        <Pencil className="w-4 h-4" /> เล่าเรื่องเอง
                      </button>
                    )}
                    {!showCaseBox && (
                      <button onClick={() => { setShowCaseBox(true); setShowCustomClipBox(false); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, padding: '9px 14px', borderRadius: 10, border: '1px dashed #f59e0b', background: 'rgba(245,158,11,.08)', color: '#f59e0b', cursor: 'pointer', fontFamily: 'inherit' }}>
                        <Briefcase className="w-4 h-4" /> เปิดเคสลูกค้า — งานจริง 1 เคส = มินิซีรีส์
                      </button>
                    )}
                  </div>
                  {showCaseBox && (
                    <div style={{ border: '1px solid #f59e0b', borderRadius: 12, padding: 14, background: 'rgba(245,158,11,.05)', marginBottom: showCustomClipBox ? 10 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}><Briefcase className="w-4 h-4" style={{ color: '#f59e0b' }} /> เล่าโจทย์เคสลูกค้า</div>
                        <button onClick={() => setShowCaseBox(false)} style={{ border: 'none', background: 'transparent', color: 'var(--sub2)', cursor: 'pointer', fontFamily: 'inherit' }}><X className="w-4 h-4" /></button>
                      </div>
                      <textarea value={caseBriefDraft} onChange={(e) => setCaseBriefDraft(e.target.value)} rows={4} placeholder={'เล่าโจทย์ของเคสจริง เช่น\n"ได้ลูกค้าร้านเสริมสวย Nisa La Beauty งบ 5,000 อยากได้เว็บจองคิวออนไลน์ ลูกค้าไม่เก่งเทคโนโลยีเลย ต้องใช้ง่ายมาก ๆ มีไลน์แจ้งเตือนคิว..."\n\nAI จะแตกเป็นมินิซีรีส์ 4-6 คลิป ตั้งแต่รับโจทย์จนส่งมอบ'} style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.55, resize: 'vertical' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                        <button onClick={handleCreateCase} disabled={isCaseGenerating || !caseBriefDraft.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, padding: '9px 15px', borderRadius: 10, border: 'none', color: '#fff', background: isCaseGenerating || !caseBriefDraft.trim() ? 'var(--track2)' : 'linear-gradient(135deg,#f59e0b,#f97316)', cursor: isCaseGenerating || !caseBriefDraft.trim() ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                          {isCaseGenerating ? (<><Loader2 className="w-4 h-4 animate-spin" /> กำลังแตกเป็นซีรีส์...</>) : (<><Sparkles className="w-4 h-4" /> แตกเป็นมินิซีรีส์เลย</>)}
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--sub2)' }}>ได้ชุดคลิปของเคสนี้แทรกในแผน แล้วค่อยกดเจนสคริปต์ทีละตอน</span>
                      </div>
                    </div>
                  )}
                  {showCustomClipBox && (
                    <div style={{ border: '1px solid var(--cv)', borderRadius: 12, padding: 14, background: 'rgba(124,92,255,.06)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}><Pencil className="w-4 h-4" style={{ color: 'var(--cv)' }} /> เล่าเหตุการณ์ที่เจอมา</div>
                        <button onClick={() => setShowCustomClipBox(false)} style={{ border: 'none', background: 'transparent', color: 'var(--sub2)', cursor: 'pointer', fontFamily: 'inherit' }}><X className="w-4 h-4" /></button>
                      </div>
                      <textarea value={customStoryDraft} onChange={(e) => setCustomStoryDraft(e.target.value)} rows={4} placeholder={'เล่าเหตุการณ์จริงที่อยากทำเป็นคลิป เช่น\n"วันนี้มีลูกค้าทักมาถามราคาทำเว็บ พอบอกราคาไปก็เงียบเลย แอบเฟลเหมือนกัน แต่ก็ได้เรียนรู้ว่า..."\n\nแนบรูปได้ด้วย (สูงสุด 3) เช่น "นี่คือเว็บที่ผมทำเสร็จแล้ว ช่วยคิดคอนเทนต์แนวรีวิวให้หน่อย"'} style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.55, resize: 'vertical' }} />
                      {customAttach.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          {customAttach.map((im, i) => (
                            <div key={i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--bd2)' }}>
                              <img src={`data:${im.mime};base64,${im.b64}`} alt={`แนบ ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <button onClick={() => setCustomAttach((prev) => prev.filter((_, idx) => idx !== i))} title="เอารูปออก" style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.65)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <input ref={customFileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={async (e) => {
                        const files = Array.from(e.target.files || []).slice(0, 3 - customAttach.length);
                        e.target.value = '';
                        for (const f of files) {
                          try { const b64 = await fileToResizedB64(f); setCustomAttach((prev) => prev.length < 3 ? [...prev, { b64, mime: 'image/jpeg' }] : prev); }
                          catch (err) { console.error('[customAttach]', err); }
                        }
                      }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                        <button onClick={handleGenCustomClip} disabled={isCustomGenerating || (!customStoryDraft.trim() && customAttach.length === 0)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, padding: '9px 15px', borderRadius: 10, border: 'none', color: '#fff', background: isCustomGenerating || (!customStoryDraft.trim() && customAttach.length === 0) ? 'var(--track2)' : 'linear-gradient(135deg,var(--g1),var(--g2))', cursor: isCustomGenerating || (!customStoryDraft.trim() && customAttach.length === 0) ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                          {isCustomGenerating ? (<><Loader2 className="w-4 h-4 animate-spin" /> กำลังเจนสคริปต์...</>) : (<><Sparkles className="w-4 h-4" /> เจนเป็นสคริปต์เลย</>)}
                        </button>
                        <button onClick={() => customFileRef.current?.click()} disabled={customAttach.length >= 3} title="แนบรูป (ผลงาน/สินค้า/หน้างาน) ให้ AI ดูประกอบ" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '9px 13px', borderRadius: 10, border: '1px solid var(--bd2)', background: 'transparent', color: customAttach.length >= 3 ? 'var(--sub2)' : 'var(--soft)', cursor: customAttach.length >= 3 ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                          <Paperclip className="w-4 h-4" /> แนบรูป{customAttach.length ? ` (${customAttach.length}/3)` : ''}
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--sub2)' }}>AI จะใช้เรื่อง+รูปที่แนบเป็นแกน 100% ไม่แต่งเพิ่ม · แทรกต่อจากวันล่าสุดที่เจนไว้</span>
                      </div>
                    </div>
                  )}
                </div>
                {(() => {
                  const plan = buildDayPlan();
                  if (plan.length === 0) {
                    return (
                      <div style={{ background: 'rgba(124,92,255,.08)', border: '1px dashed var(--bd2)', borderRadius: 12, padding: 20, textAlign: 'center', color: 'var(--sub)', fontSize: 13, lineHeight: 1.6 }}>
                        ต้องมีหัวข้อ <strong style={{ color: 'var(--cv)' }}>Story</strong> และ <strong style={{ color: 'var(--cv)' }}>Authority</strong> อย่างน้อยอย่างละ 1 ชุดก่อน<br />กดปุ่ม <strong>"เจนหัวข้อ"</strong> ด้านบนเพื่อเจนหัวข้อให้ครบ แล้วกลับมาดูแผนนี้
                      </div>
                    );
                  }
                  const sellDays = plan.filter((d) => d.sell).length;
                  return (
                    <>
                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14, fontSize: 11.5, color: 'var(--sub2)' }}>
                        <span>📅 รวม <strong style={{ color: 'var(--ink2)' }}>{plan.length} วัน</strong></span>
                        <span>🎬 คอนเทนต์หลัก <strong style={{ color: 'var(--ink2)' }}>{plan.length} คลิป</strong></span>
                        <span>🛒 คลิปขายเสริม (เลือกลงได้) <strong style={{ color: 'var(--ink2)' }}>{sellDays} วัน</strong></span>
                      </div>
                      {(() => {
                        const cleanTopic = (t: string) => t.replace(/^คลิป\s*\d+\s*[:：]\s*/, '');
                        const renderDayCard = (d: typeof plan[number]) => {
                          const MainIcon = modeIcons[d.main.mode];
                          const mainDone = isScriptDone(d.main.topic, d.main.mode);
                          const mainBusy = (isGenerating || isAiGenerating) && selectedTopic === d.main.topic;
                          return (
                            <div key={d.day} style={{ border: '1px solid var(--bd)', borderRadius: 12, padding: 12, background: 'var(--panel2)' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                <div style={{ flex: 'none', width: 42, textAlign: 'center' }}>
                                  <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'var(--sub2)', letterSpacing: '.08em' }}>วันที่</div>
                                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cv)', lineHeight: 1.1 }}>{d.day}</div>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                                    <MainIcon className="w-3.5 h-3.5" style={{ flex: 'none', color: 'var(--cv)' }} />
                                    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--sub2)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{MODE_LABEL[d.main.mode]}</span>
                                  </div>
                                  <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500, lineHeight: 1.45, marginBottom: 8 }}>{cleanTopic(d.main.topic)}</div>
                                  <button onClick={() => handlePlanGenerate(d.main.topic, d.main.mode)} disabled={mainBusy} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 8, cursor: mainBusy ? 'default' : 'pointer', fontFamily: 'inherit', border: mainDone ? '1px solid var(--bd2)' : 'none', color: mainDone ? 'var(--soft)' : '#fff', background: mainDone ? 'transparent' : 'linear-gradient(135deg,var(--g1),var(--g2))' }}>
                                    {mainBusy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> กำลังสร้าง...</>) : mainDone ? (<><CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34d399' }} /> ดูสคริปต์</>) : (<><Clapperboard className="w-3.5 h-3.5" /> สร้างสคริปต์</>)}
                                  </button>
                                </div>
                              </div>
                              {d.sell && (() => {
                                const sellDone = isScriptDone(d.sell!.topic, 'softsell');
                                const sellBusy = (isGenerating || isAiGenerating) && selectedTopic === d.sell!.topic;
                                return (
                                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--bd2)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <div style={{ flex: 'none', width: 42, textAlign: 'center', fontSize: 9.5, fontWeight: 700, color: '#f59e0b', lineHeight: 1.2 }}>+ คลิป<br />เสริม</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                                        <ShoppingBag className="w-3.5 h-3.5" style={{ flex: 'none', color: '#f59e0b' }} />
                                        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '.04em' }}>Soft-sell · ขาย (ไม่บังคับ)</span>
                                      </div>
                                      <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500, lineHeight: 1.45, marginBottom: 8 }}>{cleanTopic(d.sell!.topic)}</div>
                                      <button onClick={() => handlePlanGenerate(d.sell!.topic, 'softsell')} disabled={sellBusy} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 8, cursor: sellBusy ? 'default' : 'pointer', fontFamily: 'inherit', border: sellDone ? '1px solid var(--bd2)' : 'none', color: sellDone ? 'var(--soft)' : '#fff', background: sellDone ? 'transparent' : 'linear-gradient(135deg,#f59e0b,#f97316)' }}>
                                        {sellBusy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> กำลังสร้าง...</>) : sellDone ? (<><CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34d399' }} /> ดูสคริปต์</>) : (<><Clapperboard className="w-3.5 h-3.5" /> สร้างสคริปต์</>)}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        };
                        // การ์ดคลิปเล่าเรื่องเอง (แทรกหลังวันที่ afterDay)
                        const renderCustomCard = (c: CustomClip) => (
                          <div key={c.id} style={{ border: '1px solid rgba(52,211,153,.45)', borderRadius: 12, padding: 12, background: 'rgba(52,211,153,.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                              <div style={{ flex: 'none', width: 42, textAlign: 'center', paddingTop: 2 }}>
                                <Pencil className="w-4 h-4" style={{ color: '#34d399', display: 'inline-block' }} />
                                <div style={{ fontSize: 8.5, fontWeight: 700, color: '#34d399', lineHeight: 1.3, marginTop: 2 }}>คลิป<br />แทรก</div>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#34d399', textTransform: 'uppercase', letterSpacing: '.04em' }}>🖊️ เรื่องของคุณ · เล่าเอง</span>
                                </div>
                                <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500, lineHeight: 1.45, marginBottom: 8 }}>{c.title}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <button onClick={() => handleViewCustomClip(c)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', border: '1px solid var(--bd2)', color: 'var(--soft)', background: 'transparent' }}>
                                    <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34d399' }} /> ดูสคริปต์
                                  </button>
                                  <button onClick={() => handleDeleteCustomClip(c.id)} title="ลบคลิปนี้" style={{ border: 'none', background: 'transparent', color: 'var(--sub2)', cursor: 'pointer', fontFamily: 'inherit', padding: 6, display: 'flex' }}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                        // การ์ดเคสลูกค้า (มินิซีรีส์) — แทรกหลังวันที่ afterDay
                        const renderCaseCard = (cs: CustomCase) => {
                          const doneCnt = cs.clips.filter((c) => c.script).length;
                          return (
                            <div key={cs.id} style={{ border: '1px solid rgba(245,158,11,.5)', borderRadius: 12, padding: 12, background: 'rgba(245,158,11,.05)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                <Briefcase className="w-4 h-4" style={{ flex: 'none', color: '#f59e0b' }} />
                                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '.04em', flex: 'none' }}>เคสลูกค้า · มินิซีรีส์</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{cs.name}</span>
                                <span style={{ fontSize: 11, color: doneCnt === cs.clips.length ? '#34d399' : 'var(--sub2)', flex: 'none' }}>{doneCnt}/{cs.clips.length} ตอน</span>
                                <button onClick={() => handleDeleteCase(cs.id)} title="ลบเคสนี้" style={{ flex: 'none', border: 'none', background: 'transparent', color: 'var(--sub2)', cursor: 'pointer', fontFamily: 'inherit', padding: 4, display: 'flex' }}><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                {cs.clips.map((cl, i) => {
                                  const busy = caseClipBusy === `${cs.id}_${i}`;
                                  return (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 9px', borderRadius: 9, background: 'var(--panel2)', border: '1px solid var(--bd)' }}>
                                      <span style={{ flex: 'none', fontFamily: "'IBM Plex Mono',monospace", fontSize: 10.5, fontWeight: 700, color: '#f59e0b' }}>EP{i + 1}</span>
                                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--ink)', lineHeight: 1.4 }}>{cl.title}</span>
                                      <button onClick={() => handleCaseClip(cs, i)} disabled={busy} style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, padding: '6px 10px', borderRadius: 8, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', border: cl.script ? '1px solid var(--bd2)' : 'none', color: cl.script ? 'var(--soft)' : '#fff', background: cl.script ? 'transparent' : 'linear-gradient(135deg,#f59e0b,#f97316)' }}>
                                        {busy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> กำลังสร้าง...</>) : cl.script ? (<><CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34d399' }} /> ดูสคริปต์</>) : (<><Clapperboard className="w-3.5 h-3.5" /> สร้างสคริปต์</>)}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        };
                        // แบ่งเป็นสัปดาห์ละ 7 วัน (พับ/เปิดได้)
                        const weeks: (typeof plan)[] = [];
                        for (let i = 0; i < plan.length; i += 7) weeks.push(plan.slice(i, i + 7));
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                            {weeks.map((wk, wi) => {
                              const wnum = wi + 1;
                              const open = !!openWeeks[wnum];
                              const doneCnt = wk.filter((d) => isScriptDone(d.main.topic, d.main.mode)).length;
                              const sellCnt = wk.filter((d) => d.sell).length;
                              return (
                                <div key={wnum} style={{ border: '1px solid var(--bd)', borderRadius: 12, overflow: 'hidden' }}>
                                  <button onClick={() => setOpenWeeks((p) => ({ ...p, [wnum]: !p[wnum] }))} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 14px', background: open ? 'var(--panel2)' : 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                                      <span style={{ flex: 'none', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', color: 'var(--sub)', fontSize: 11 }}>▶</span>
                                      <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap' }}>สัปดาห์ที่ {wnum}</span>
                                      <span style={{ fontSize: 11.5, color: 'var(--sub2)', whiteSpace: 'nowrap' }}>วันที่ {wk[0].day}–{wk[wk.length - 1].day}</span>
                                    </div>
                                    <span style={{ fontSize: 11, color: doneCnt === wk.length ? '#34d399' : 'var(--sub2)', flex: 'none', whiteSpace: 'nowrap' }}>ทำแล้ว {doneCnt}/{wk.length}{sellCnt ? ` · +ขาย ${sellCnt}` : ''}</span>
                                  </button>
                                  {open && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: 12, borderTop: '1px solid var(--bd)' }}>
                                      {wi === 0 && customClips.filter((c) => c.afterDay === 0).map(renderCustomCard)}
                                      {wi === 0 && customCases.filter((c) => c.afterDay === 0).map(renderCaseCard)}
                                      {wk.flatMap((d) => [renderDayCard(d), ...customClips.filter((c) => c.afterDay === d.day).map(renderCustomCard), ...customCases.filter((c) => c.afterDay === d.day).map(renderCaseCard)])}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Panel: แดชบอร์ดผลลัพธ์ (Progress Tracker) */}
            {mainView === 'results' && (
              <div style={{ background: 'var(--panel)', border: '1px solid var(--bd)', borderRadius: 18, padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
                  <TrendingUp className="w-5 h-5" style={{ color: 'var(--cv)' }} />
                  <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>แดชบอร์ดผลลัพธ์</h2>
                </div>
                <p style={{ margin: '0 0 16px 30px', fontSize: 12.5, color: 'var(--sub)', lineHeight: 1.5 }}>บันทึกผลจริงเป็นระยะ (ผู้ติดตาม/ยอดขาย/ลูกค้า/วิว/คลิป) แล้วดูความคืบหน้าเทียบเป้า · สรุปรายสัปดาห์ · จบ 60 วัน · รายไตรมาส</p>

                {/* ฟอร์มเช็คอิน / ตั้งค่าเริ่มต้น */}
                {checkinDraft && (
                  <div style={{ border: '1px solid rgba(124,92,255,.35)', background: 'var(--panel2)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 3 }}>{progressTracker ? '📊 เช็คอินผลวันนี้' : '🚀 เริ่มติดตามผล'}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--sub)', marginBottom: 12 }}>{progressTracker ? 'กรอกตัวเลขล่าสุด ณ ตอนนี้ (เว้นว่างได้ถ้ายังไม่มี)' : 'กรอกตัวเลขตอนนี้เป็น "จุดเริ่มต้น" (วันแรก) — ครั้งต่อไปค่อยมาเช็คความคืบหน้า'}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
                      {PROGRESS_METRICS.map((m) => (
                        <div key={m.key}>
                          <label style={{ fontSize: 11.5, color: 'var(--sub2)', display: 'block', marginBottom: 4 }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: m.color, marginRight: 5 }}></span>{m.label} ({m.unit})</label>
                          <input inputMode="numeric" value={checkinDraft[m.key] || ''} onChange={(e) => setCheckinDraft({ ...checkinDraft, [m.key]: e.target.value.replace(/[^\d.]/g, '') })} placeholder="0" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 9, padding: '9px 11px', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14 }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                      <button onClick={submitCheckin} style={{ flex: 1, background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontWeight: 700, padding: '11px', borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5 }}>บันทึก</button>
                      <button onClick={() => setCheckinDraft(null)} style={{ border: '1px solid var(--bd2)', background: 'transparent', color: 'var(--soft)', fontSize: 13, padding: '11px 18px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit' }}>ยกเลิก</button>
                    </div>
                  </div>
                )}

                {(() => {
                  const pt = progressTracker;
                  if (!pt || pt.checkins.length === 0) {
                    return checkinDraft ? null : (
                      <div style={{ background: 'rgba(124,92,255,.08)', border: '1px dashed var(--bd2)', borderRadius: 12, padding: 24, textAlign: 'center' }}>
                        <TrendingUp className="w-8 h-8" style={{ color: 'var(--cv)', margin: '0 auto 10px' }} />
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>ยังไม่เริ่มติดตามผล</div>
                        <div style={{ fontSize: 12.5, color: 'var(--sub)', marginBottom: 16, lineHeight: 1.6 }}>บันทึกตัวเลขวันนี้เป็นจุดเริ่มต้น แล้วมาเช็คความคืบหน้าทุกสัปดาห์<br />ระบบจะสรุปให้เห็นว่าโตขึ้นแค่ไหน เทียบเป้าหมายของคุณ</div>
                        <button onClick={openCheckinForm} style={{ background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontWeight: 700, padding: '12px 24px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}><Rocket className="w-4 h-4" /> เริ่มติดตามผล</button>
                      </div>
                    );
                  }
                  const cks = pt.checkins;
                  const first = cks[0], last = cks[cks.length - 1];
                  const daysElapsed = Math.floor((Date.now() - new Date(pt.startDate).getTime()) / 86400000) + 1;
                  const daysLeft = Math.max(0, 60 - daysElapsed);
                  const chartColor = PROGRESS_METRICS.find((m) => m.key === chartMetric)?.color || '#8b6dff';
                  const chartData = cks.map((c) => c[chartMetric]).map((v, i) => ({ i, v })).filter((d) => d.v !== undefined) as { i: number; v: number }[];
                  return (
                    <>
                      {/* แถบวัน + ปุ่มเช็คอิน */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                        <div style={{ fontSize: 12.5, color: 'var(--sub)' }}>📅 วันที่ <strong style={{ color: 'var(--ink2)' }}>{daysElapsed}</strong> ของแผน · {daysLeft > 0 ? <>เหลืออีก <strong style={{ color: 'var(--ink2)' }}>{daysLeft}</strong> วัน</> : <strong style={{ color: '#34d399' }}>ครบ 60 วันแล้ว 🎉</strong>} · เช็คอินแล้ว {cks.length} ครั้ง</div>
                        {!checkinDraft && <button onClick={openCheckinForm} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontWeight: 700, fontSize: 12.5, padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>＋ เช็คอินผลใหม่</button>}
                      </div>

                      {/* การ์ดตัวชี้วัด */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 18 }}>
                        {PROGRESS_METRICS.map((m) => {
                          const cur = metricCurrent(m, cks), st = metricStart(m, cks);
                          const delta = cur !== undefined && st !== undefined ? cur - st : undefined;
                          const tgt = pt.target[m.key];
                          const pct = tgt && cur !== undefined ? Math.min(100, (cur / tgt) * 100) : null;
                          const active = chartMetric === m.key;
                          return (
                            <button key={m.key} onClick={() => setChartMetric(m.key)} style={{ textAlign: 'left', border: active ? `1px solid ${m.color}` : '1px solid var(--bd)', background: 'var(--panel2)', borderRadius: 12, padding: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: m.color }}></span><span style={{ fontSize: 11.5, color: 'var(--sub2)', fontWeight: 600 }}>{m.label}</span></div>
                              <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--ink)', lineHeight: 1.1 }}>{fmtNum(cur)}</div>
                              {delta !== undefined && delta !== 0 && <div style={{ fontSize: 11.5, fontWeight: 700, color: delta > 0 ? '#34d399' : '#f87171', marginTop: 2 }}>{delta > 0 ? '▲' : '▼'} {fmtNum(Math.abs(delta))} จากเริ่มต้น</div>}
                              {pct !== null && <div style={{ marginTop: 7 }}><div style={{ height: 5, borderRadius: 99, background: 'var(--track2)', overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: '100%', background: m.color, borderRadius: 99 }}></div></div><div style={{ fontSize: 10, color: 'var(--sub2)', marginTop: 3 }}>{Math.round(pct)}% ของเป้า {fmtNum(tgt)}</div></div>}
                            </button>
                          );
                        })}
                      </div>

                      {/* กราฟการเติบโต (ตัวชี้วัดที่เลือก) */}
                      <div style={{ border: '1px solid var(--bd)', borderRadius: 12, padding: '14px 14px 8px', marginBottom: 18 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: chartColor }}></span>กราฟ{PROGRESS_METRICS.find((m) => m.key === chartMetric)?.label} <span style={{ color: 'var(--sub2)', fontWeight: 400 }}>(แตะการ์ดด้านบนเพื่อสลับ)</span></div>
                        {chartData.length < 2 ? (
                          <div style={{ fontSize: 12, color: 'var(--sub2)', padding: '18px 0', textAlign: 'center' }}>เช็คอินอย่างน้อย 2 ครั้งเพื่อดูกราฟการเติบโต</div>
                        ) : (() => {
                          const W = 100, H = 42, pad = 3;
                          const vals = chartData.map((d) => d.v);
                          const min = Math.min(...vals), max = Math.max(...vals);
                          const n = chartData.length;
                          const xx = (idx: number) => (n <= 1 ? W / 2 : pad + (idx / (n - 1)) * (W - 2 * pad));
                          const yy = (v: number) => (H - pad) - ((v - min) / (max - min || 1)) * (H - 2 * pad);
                          const linePts = chartData.map((d, idx) => `${xx(idx).toFixed(2)},${yy(d.v).toFixed(2)}`).join(' ');
                          const areaPts = `${xx(0).toFixed(2)},${(H - pad).toFixed(2)} ${linePts} ${xx(n - 1).toFixed(2)},${(H - pad).toFixed(2)}`;
                          const gid = `cg_${chartMetric}`;
                          const startDateStr = new Date(cks[chartData[0].i].date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
                          return (
                            <div>
                              <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 128, display: 'block' }}>
                                <defs>
                                  <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={chartColor} stopOpacity="0.32" />
                                    <stop offset="100%" stopColor={chartColor} stopOpacity="0" />
                                  </linearGradient>
                                </defs>
                                {[0.33, 0.66].map((f) => <line key={f} x1="0" x2={W} y1={(pad + f * (H - 2 * pad)).toFixed(2)} y2={(pad + f * (H - 2 * pad)).toFixed(2)} style={{ stroke: 'var(--bd)' }} strokeWidth={1} vectorEffect="non-scaling-stroke" />)}
                                <polygon points={areaPts} fill={`url(#${gid})`} />
                                <polyline points={linePts} fill="none" stroke={chartColor} strokeWidth={2.5} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
                              </svg>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--sub2)', marginTop: 6 }}>
                                <span>{startDateStr} · {fmtNum(chartData[0].v)}</span>
                                <span style={{ color: chartColor, fontWeight: 700 }}>ล่าสุด {fmtNum(chartData[chartData.length - 1].v)}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* สรุปจบ 60 วัน / ถึงตอนนี้ */}
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>📈 สรุปการเติบโต (จากเริ่มต้น → ปัจจุบัน)</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 18 }}>
                        {PROGRESS_METRICS.map((m) => {
                          const st = metricStart(m, cks), cur = metricCurrent(m, cks);
                          if (st === undefined && cur === undefined) return null;
                          const delta = cur !== undefined && st !== undefined ? cur - st : undefined;
                          return (
                            <div key={m.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12.5, padding: '8px 12px', background: 'var(--panel2)', borderRadius: 9 }}>
                              <span style={{ color: 'var(--sub)' }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: m.color, marginRight: 6 }}></span>{m.label}</span>
                              <span style={{ color: 'var(--ink2)', fontWeight: 600 }}>{fmtNum(st)} → {fmtNum(cur)} {delta !== undefined && <span style={{ color: delta >= 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>({delta >= 0 ? '+' : ''}{fmtNum(delta)})</span>}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* สรุปรายไตรมาส (Q1-Q4) */}
                      {(() => {
                        const groups: Record<string, ProgressCheckin[]> = {};
                        cks.forEach((c) => { const k = quarterKey(c.date); (groups[k] = groups[k] || []).push(c); });
                        const keys = Object.keys(groups).sort();
                        if (keys.length === 0) return null;
                        return (
                          <>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>🗓️ สรุปรายไตรมาส</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 }}>
                              {keys.map((k) => {
                                const g = groups[k];
                                const [yr, q] = k.split('-');
                                return (
                                  <div key={k} style={{ border: '1px solid var(--bd)', borderRadius: 10, padding: 12 }}>
                                    <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cv)', marginBottom: 7 }}>{q} · ปี {yr} <span style={{ color: 'var(--sub2)', fontWeight: 400 }}>({g.length} เช็คอิน)</span></div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                                      {PROGRESS_METRICS.map((m) => {
                                        const a = metricStart(m, g), b = metricCurrent(m, g);
                                        if (a === undefined && b === undefined) return null;
                                        const d = a !== undefined && b !== undefined ? b - a : undefined;
                                        return <span key={m.key} style={{ fontSize: 11.5, color: 'var(--sub)' }}>{m.label}: <strong style={{ color: 'var(--ink2)' }}>{fmtNum(b)}</strong>{d !== undefined && d !== 0 && <span style={{ color: d > 0 ? '#34d399' : '#f87171', fontWeight: 700 }}> ({d > 0 ? '+' : ''}{fmtNum(d)})</span>}</span>;
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}

                      {/* ประวัติเช็คอิน */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>📋 ประวัติเช็คอิน ({cks.length})</div>
                        {isAdmin && <button onClick={deleteLastCheckin} style={{ background: 'none', border: 'none', color: '#fca5a5', fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit' }}>🗑 ลบล่าสุด</button>}
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead><tr style={{ color: 'var(--sub2)', textAlign: 'right' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>วันที่</th>
                            {PROGRESS_METRICS.map((m) => <th key={m.key} style={{ padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>{m.label}</th>)}
                          </tr></thead>
                          <tbody>
                            {cks.slice().reverse().map((c, ri) => (
                              <tr key={ri} style={{ borderTop: '1px solid var(--bd)', color: 'var(--ink2)', textAlign: 'right' }}>
                                <td style={{ textAlign: 'left', padding: '7px 8px', whiteSpace: 'nowrap', color: 'var(--sub)' }}>{new Date(c.date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}</td>
                                {PROGRESS_METRICS.map((m) => <td key={m.key} style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}>{fmtNum(c[m.key])}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* 💬 Panel: แชท AI (แอดมิน) — คุยเล่น + สั่งเจนภาพในแชท */}
            {mainView === 'chat' && isAdmin && (
              <div style={{ background: 'var(--panel)', border: '1px solid var(--bd)', borderRadius: 18, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--bd)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <MessageSquare className="w-5 h-5" style={{ color: 'var(--cv)' }} />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>แชทกับ AI</div>
                      <div style={{ fontSize: 11, color: 'var(--sub2)' }}>คุยเล่นได้เลย · หรือสั่ง "สร้างภาพ..." ภาพจะเด้งในแชท (~฿1.35/ภาพ){character?.imageB64 ? ' · มีตัวละครพร้อมใช้ ✓' : ''}</div>
                    </div>
                  </div>
                  {chatMsgs.length > 0 && (
                    <button onClick={() => { if (window.confirm('ล้างบทสนทนาทั้งหมด?')) setChatMsgs([]); }} style={{ border: '1px solid var(--bd2)', background: 'transparent', color: 'var(--sub2)', fontSize: 11.5, padding: '6px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>ล้างแชท</button>
                  )}
                </div>
                {/* messages */}
                <div style={{ height: '55vh', minHeight: 320, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {chatMsgs.length === 0 && (
                    <div style={{ margin: 'auto', textAlign: 'center', color: 'var(--sub2)' }}>
                      <MessageSquare className="w-8 h-8" style={{ margin: '0 auto 10px', color: 'var(--cv)', opacity: 0.6 }} />
                      <div style={{ fontSize: 13.5, color: 'var(--soft)', marginBottom: 4 }}>เริ่มคุยได้เลยครับ</div>
                      <div style={{ fontSize: 12, lineHeight: 1.7 }}>ลองพิมพ์: "สร้างภาพตัวละครกำลังซ่อมก๊อกน้ำ"<br />หรือคุยเรื่องไอเดียคอนเทนต์ก็ได้</div>
                    </div>
                  )}
                  {chatMsgs.map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                      {m.imageB64 ? (
                        <div style={{ maxWidth: 260, borderRadius: 14, overflow: 'hidden', border: '1px solid var(--bd2)', background: 'var(--panel2)' }}>
                          <img src={`data:${m.mime || 'image/png'};base64,${m.imageB64}`} alt={m.role === 'user' ? 'รูปที่แนบ' : 'ภาพที่เจน'} style={{ width: '100%', display: 'block' }} />
                          {m.text && <div style={{ fontSize: 12.5, color: 'var(--ink2)', padding: '8px 10px 0', lineHeight: 1.5 }}>{m.text}</div>}
                          {m.role === 'ai'
                            ? <a href={`data:${m.mime || 'image/png'};base64,${m.imageB64}`} download={`chat-image-${i}.png`} style={{ display: 'block', textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: 'var(--cv)', padding: '8px', textDecoration: 'none' }}>⬇ ดาวน์โหลดภาพ</a>
                            : <div style={{ fontSize: 10.5, color: 'var(--sub2)', textAlign: 'center', padding: '6px' }}>📎 รูปต้นแบบที่แนบ</div>}
                        </div>
                      ) : (
                        <div style={{ maxWidth: '78%', padding: '10px 14px', borderRadius: 14, fontSize: 13.5, lineHeight: 1.65, whiteSpace: 'pre-wrap', ...(m.role === 'user' ? { background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', borderBottomRightRadius: 4 } : { background: 'var(--panel2)', border: '1px solid var(--bd)', color: 'var(--ink2)', borderBottomLeftRadius: 4 }) }}>{m.text}</div>
                      )}
                    </div>
                  ))}
                  {isChatSending && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{ padding: '10px 14px', borderRadius: 14, background: 'var(--panel2)', border: '1px solid var(--bd)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--g1)' }} />
                        <span style={{ fontSize: 12.5, color: 'var(--sub)' }}>กำลังคิด...</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                {/* input */}
                {chatAttach && (
                  <div style={{ padding: '10px 12px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ position: 'relative', width: 56, height: 56, borderRadius: 10, overflow: 'hidden', border: '2px solid var(--cv)' }}>
                      <img src={`data:${chatAttach.mime};base64,${chatAttach.b64}`} alt="รูปแนบ" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button onClick={() => setChatAttach(null)} style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.65)', color: '#fff', fontSize: 11, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>✕</button>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--cv)', lineHeight: 1.5 }}>📎 แนบรูปต้นแบบแล้ว — พิมพ์สั่งว่าอยากได้ภาพแบบไหนจากรูปนี้ แล้วกดส่ง</div>
                  </div>
                )}
                <div style={{ padding: 12, borderTop: chatAttach ? 'none' : '1px solid var(--bd)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <label title="แนบรูปต้นแบบให้ AI เจนภาพต่อยอด" style={{ flex: 'none', width: 44, height: 44, borderRadius: 12, border: '1px dashed var(--bd2)', background: 'var(--field)', color: 'var(--sub)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18 }}>
                    📎
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (f) { try { const b64 = await fileToResizedB64(f); setChatAttach({ b64, mime: 'image/jpeg' }); } catch { alert('อ่านรูปไม่สำเร็จ ลองรูปอื่น'); } }
                      e.target.value = '';
                    }} />
                  </label>
                  <textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                    placeholder={chatAttach ? 'สั่งจากรูปแนบ — เช่น "เปลี่ยนพื้นหลังเป็นทะเล" / "ทำเป็นการ์ตูน" (Enter = ส่ง)' : 'พิมพ์คุย หรือสั่ง "สร้างภาพ..." · กด 📎 แนบรูปต้นแบบได้ (Enter = ส่ง)'}
                    rows={1}
                    style={{ flex: 1, boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 12, padding: '11px 14px', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, resize: 'none', minHeight: 44, maxHeight: 120 }}
                  />
                  <button onClick={handleChatSend} disabled={isChatSending || (!chatInput.trim() && !chatAttach)} style={{ flex: 'none', width: 44, height: 44, borderRadius: 12, border: 'none', cursor: isChatSending || (!chatInput.trim() && !chatAttach) ? 'not-allowed' : 'pointer', background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isChatSending || (!chatInput.trim() && !chatAttach) ? 0.5 : 1 }}>
                    {isChatSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {/* Panel 1 — เจนหัวข้อ */}
            <div style={{ display: mainView === 'topics' ? undefined : 'none', background: 'var(--panel)', border: '1px solid var(--bd)', borderRadius: 18, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
                <div style={{ width: 24, height: 24, borderRadius: 7, background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>เจนหัวข้อคอนเทนต์</h2>
              </div>
              <p style={{ margin: '0 0 16px 34px', fontSize: 12.5, color: 'var(--sub)' }}>สลับได้ 3 สาย — Authority (สอน) · Story (เล่าเรื่องจริง) · Soft-sell (แนะนำแบบไม่ขาย)</p>

              {/* Tabs */}
              <div className="cd-tabs">
                {(['authority', 'story', 'softsell'] as ContentMode[]).map((k) => {
                  const Icon = modeIcons[k];
                  const active = activeTab === k;
                  const count = topicSetsByMode[k].length;
                  return (
                    <button key={k} onClick={() => setActiveTab(k)} style={{ flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, border: active ? '1px solid transparent' : '1px solid var(--bd2)', background: active ? 'linear-gradient(135deg,var(--g1),var(--g2))' : 'var(--panel2)', boxShadow: active ? '0 8px 22px -10px rgba(124,92,255,.7)' : 'none' }}>
                      <Icon className="w-5 h-5" style={{ flex: 'none', color: active ? '#fff' : 'var(--cv)' }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: active ? 700 : 600, color: active ? '#fff' : 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{MODE_LABEL[k]}</div>
                        <div style={{ fontSize: 11, color: active ? 'rgba(255,255,255,.82)' : 'var(--sub2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{MODE_SUB[k]}{count > 0 ? ` · ${count}` : ''}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {topicError && (
                <div style={{ marginTop: 12, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 10, padding: 10, color: '#fca5a5', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle className="w-4 h-4" style={{ flex: 'none' }} />{topicError}
                </div>
              )}

              {topicList.length === 0 ? (
                <button onClick={() => handleGenerateTopics(activeTab)} disabled={isTopicGenerating || !creatorIdentity} style={{ width: '100%', marginTop: 14, background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontWeight: 700, padding: '13px', borderRadius: 12, border: 'none', cursor: isTopicGenerating || !creatorIdentity ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 14, opacity: isTopicGenerating || !creatorIdentity ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {isTopicGenerating ? (<><Loader2 className="w-5 h-5 animate-spin" /> กำลังเจน 30 หัวข้อ...</>) : (<><Sparkles className="w-5 h-5" /> เจน 30 หัวข้อ ({MODE_LABEL[activeTab]})</>)}
                </button>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '18px 0 11px', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink2)' }}>หัวข้อของคุณ <span style={{ color: 'var(--sub2)', fontWeight: 400 }}>({topicList.length}) · {MODE_LABEL[activeTab]}</span></span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {isAdmin && currentSet && (
                        <button onClick={() => handleDeleteSet(activeTab, currentSet.id)} disabled={isTopicGenerating} style={{ background: 'none', border: 'none', color: '#fca5a5', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>🗑 ลบชุดนี้</button>
                      )}
                      <button onClick={() => handleGenerateTopics(activeTab)} disabled={isTopicGenerating} style={{ display: 'flex', alignItems: 'center', gap: 7, border: '1px solid rgba(124,92,255,.35)', background: 'rgba(124,92,255,.1)', color: 'var(--cv)', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, background: 'linear-gradient(135deg,var(--g1),var(--g2))', transform: 'rotate(45deg)' }}></span>{isTopicGenerating ? 'กำลังเจน...' : 'สร้างหัวข้อชุดใหม่'}
                      </button>
                    </div>
                  </div>
                  {sets.length > 1 && (
                    <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: 'var(--sub2)' }}>📚 ประวัติชุด:</span>
                      <select value={activeSetIdx[activeTab]} onChange={(e) => setActiveSetIdx((idx) => ({ ...idx, [activeTab]: Number(e.target.value) }))} style={{ background: 'var(--field)', border: '1px solid var(--bd2)', color: 'var(--ink2)', fontSize: 12, borderRadius: 8, padding: '4px 8px', fontFamily: 'inherit' }}>
                        {sets.map((s, i) => (<option key={s.id} value={i}>ชุดที่ {i + 1} · {new Date(s.createdAt).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })} · ทำแล้ว {s.scripts.length}/{s.topics.length}</option>))}
                      </select>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 460, overflowY: 'auto', paddingRight: 4 }}>
                    {topicList.map((t, i) => {
                      const done = topicScripts.some((s) => s.topic === t);
                      return (
                        <div key={i} className="cd-topic" style={{ padding: '13px 15px', border: `1px solid ${done ? 'rgba(52,211,153,.3)' : 'var(--bd)'}`, borderRadius: 12, background: done ? 'rgba(52,211,153,.08)' : 'var(--panel2)' }}>
                          <div style={{ width: 27, height: 27, borderRadius: 8, background: done ? 'rgba(52,211,153,.16)' : 'rgba(124,92,255,.14)', color: done ? '#34d399' : 'var(--cv)', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{clipDisplayOffset + i + 1}</div>
                          <span style={{ flex: 1, fontSize: 13.5, color: 'var(--ink2)', lineHeight: 1.4 }}>{t.replace(/^คลิป\s*\d+\s*[:：]\s*/, '')}</span>
                          <button className="cd-gen" onClick={() => handleUseTopic(t)} disabled={isGenerating || isAiGenerating} style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 7, background: done ? '#3b82f6' : 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', border: 'none', padding: '9px 14px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: isGenerating || isAiGenerating ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: isGenerating || isAiGenerating ? 0.5 : 1 }}>
                            {done ? <EyeIcon className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}{done ? 'ดูสคริปต์' : 'สร้างสคริปต์'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ===== Popup สคริปต์ที่ AI สร้างให้ ===== */}
      {showScriptModal && (
        <div onClick={() => setShowScriptModal(false)} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'var(--overlay)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '24px 14px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 720, background: 'var(--modal)', border: '1px solid var(--bd2)', borderRadius: 20, boxShadow: '0 40px 90px -30px rgba(0,0,0,.7)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 18px', borderBottom: '1px solid var(--bd)', gap: 10, flex: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                <span style={{ flex: 'none', display: 'inline-block', width: 9, height: 9, background: 'linear-gradient(135deg,var(--g1),var(--g2))', transform: 'rotate(45deg)' }}></span>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>สคริปต์ที่ AI สร้างให้</span>
              </div>
              <div style={{ display: 'flex', gap: 7, flex: 'none' }}>
                {aiResult && (
                  <>
                    <button onClick={handleSavePdf} disabled={savingPdf} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(124,92,255,.1)', border: '1px solid rgba(124,92,255,.3)', color: 'var(--cv)', fontSize: 12, fontWeight: 500, padding: '7px 11px', borderRadius: 8, cursor: savingPdf ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: savingPdf ? 0.6 : 1 }}>
                      {savingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                    </button>
                    <button onClick={() => { navigator.clipboard.writeText(aiResult); }} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(124,92,255,.1)', border: '1px solid rgba(124,92,255,.3)', color: 'var(--cv)', fontSize: 12, fontWeight: 500, padding: '7px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
                      <Copy className="w-3.5 h-3.5" /> คัดลอก
                    </button>
                  </>
                )}
                <button onClick={() => setShowScriptModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, border: '1px solid var(--bd2)', background: 'transparent', color: 'var(--soft)', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}><X className="w-4 h-4" /></button>
              </div>
            </div>
            {/* Body */}
            <div style={{ overflowY: 'auto', padding: 18 }}>
              {(isGenerating || isAiGenerating) && (
                <div style={{ padding: '44px 24px', textAlign: 'center' }}>
                  <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--g1)', margin: '0 auto', display: 'block' }} />
                  <div style={{ marginTop: 14, fontSize: 13.5, color: 'var(--soft)' }}>AI กำลังร่างสคริปต์จากตัวตนของคุณ…</div>
                </div>
              )}
              {aiError && !isGenerating && !isAiGenerating && (
                <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 10, padding: 12, color: '#fca5a5', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle className="w-4 h-4" style={{ flex: 'none' }} />{aiError}
                </div>
              )}
              {aiResult && !isGenerating && !isAiGenerating && (
                <div ref={scriptContentRef} style={{ display: 'flex', flexDirection: 'column', gap: 11, background: 'var(--modal)' }}>
                  {cdParseScript(aiResult).map((sec, i) => (
                    <div key={i} style={{ border: '1px solid var(--bd)', borderRadius: 12, padding: 14, background: 'var(--panel2)' }}>
                      {sec.title && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cv)', marginBottom: sec.body ? 8 : 0 }}>{sec.title}</div>}
                      {sec.body && <pre style={{ margin: 0, color: 'var(--ink2)', fontSize: 13, whiteSpace: 'pre-wrap', fontFamily: "'IBM Plex Sans Thai', system-ui, sans-serif", lineHeight: 1.6 }}>{sec.body}</pre>}
                    </div>
                  ))}
                </div>
              )}

              {/* 🖼️ ภาพประกอบฉาก (แอดมิน) — เจนภาพตัวละครหน้าเดิมทุกฉาก + prompt สำหรับ Google Flow */}
              {isAdmin && aiResult && !isGenerating && !isAiGenerating && (
                <div style={{ marginTop: 16, borderTop: '1px solid var(--bd)', paddingTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>🖼️ ภาพประกอบฉาก <span style={{ fontSize: 11, color: 'var(--sub2)', fontWeight: 400 }}>(สำหรับทำวิดีโอใน Google Flow · ~฿1.35/ภาพ)</span></div>
                    <button onClick={handleGenSceneImages} disabled={isSceneGenRunning} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontWeight: 700, fontSize: 12.5, padding: '9px 14px', borderRadius: 9, border: 'none', cursor: isSceneGenRunning ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: isSceneGenRunning ? 0.6 : 1 }}>
                      {isSceneGenRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {isSceneGenRunning
                        ? (sceneImages.length ? `กำลังเจนภาพ ${sceneImages.filter((s) => s.status === 'done' || s.status === 'error').length}/${sceneImages.length}...` : 'กำลังอ่านฉากจากสคริปต์...')
                        : sceneImages.length ? 'เจนใหม่ทั้งชุด' : 'เจนภาพทุกฉาก'}
                    </button>
                  </div>
                  {!character?.imageB64 && (
                    <div style={{ fontSize: 12, color: '#fbbf24', background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', borderRadius: 9, padding: '9px 12px', marginBottom: 10 }}>⚠️ ยังไม่ได้ตั้ง "ตัวละคร" ของโปรเจกต์ — กดปุ่มตัวละคร (รูปคน) ข้างแถบโปรเจกต์ก่อน เพื่อให้ทุกภาพเป็นหน้าเดิม</div>
                  )}
                  {sceneImages.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 12 }}>
                      {sceneImages.map((sc, i) => (
                        <div key={i} style={{ border: '1px solid var(--bd)', borderRadius: 12, overflow: 'hidden', background: 'var(--panel2)' }}>
                          <div style={{ aspectRatio: '9/16', background: 'var(--field)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            {sc.status === 'done' && sc.imageB64 && <img src={`data:${sc.mime || 'image/png'};base64,${sc.imageB64}`} alt={sc.scene} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                            {sc.status === 'generating' && <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--g1)' }} />}
                            {sc.status === 'pending' && <span style={{ fontSize: 11, color: 'var(--sub2)' }}>รอคิว...</span>}
                            {sc.status === 'error' && (
                              <button onClick={() => handleRetryScene(i)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid rgba(239,68,68,.4)', background: 'rgba(239,68,68,.1)', color: '#fca5a5', fontSize: 11.5, padding: '7px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}><RefreshCw className="w-3.5 h-3.5" /> พลาด — เจนซ้ำ</button>
                            )}
                          </div>
                          <div style={{ padding: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sc.scene}</div>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                              {sc.status === 'done' && sc.imageB64 && (
                                <a href={`data:${sc.mime || 'image/png'};base64,${sc.imageB64}`} download={`scene-${i + 1}.png`} style={{ flex: 1, textAlign: 'center', border: '1px solid rgba(124,92,255,.35)', background: 'rgba(124,92,255,.1)', color: 'var(--cv)', fontSize: 11.5, fontWeight: 600, padding: '7px 8px', borderRadius: 8, textDecoration: 'none', fontFamily: 'inherit' }}>⬇ ดาวน์โหลด</a>
                              )}
                              <button onClick={() => { navigator.clipboard.writeText(sc.motionPrompt); }} title="คัดลอก prompt เคลื่อนไหวไปวางใน Google Flow" style={{ flex: 1, border: '1px solid var(--bd2)', background: 'transparent', color: 'var(--soft)', fontSize: 11.5, fontWeight: 600, padding: '7px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>📋 Flow prompt</button>
                            </div>
                            <div style={{ fontSize: 10.5, color: 'var(--sub2)', lineHeight: 1.5, maxHeight: 52, overflow: 'hidden' }}>{sc.motionPrompt}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== Wizard Modal — ตั้งค่าตัวตน (ดีไซน์ใหม่) ===== */}
      {currentStep >= 1 && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'var(--overlay)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '28px 14px' }}>
          <div style={{ width: '100%', maxWidth: 720, background: 'var(--modal)', border: '1px solid var(--bd2)', borderRadius: 20, boxShadow: '0 40px 90px -30px rgba(0,0,0,.7)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid var(--bd)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 11, background: 'linear-gradient(135deg,var(--g1),var(--g2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><User className="w-5 h-5" style={{ color: '#fff' }} /></div>
                <div><div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>ตั้งค่าตัวตน</div><div style={{ fontSize: 12, color: 'var(--sub2)' }}>Step {currentStep}/4</div></div>
              </div>
              {creatorIdentity && (
                <button onClick={() => setCurrentStep(0)} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--bd2)', background: 'transparent', color: 'var(--soft)', fontSize: 13, padding: '8px 13px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit' }}><X className="w-4 h-4" /> ปิด</button>
              )}
            </div>
            {/* Step indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '16px 22px', borderBottom: '1px solid var(--bd)' }}>
              {['ข้อมูลพื้นฐาน', 'เป้าหมาย', 'สินค้า/บริการ', 'บันทึก'].map((label, idx) => {
                const n = idx + 1; const active = currentStep === n; const done = currentStep > n; const on = active || done;
                return (
                  <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flex: 'none', background: on ? 'linear-gradient(135deg,var(--g1),var(--g2))' : 'var(--bd)', color: on ? '#fff' : 'var(--sub2)' }}>{done ? <CheckCircle2 className="w-4 h-4" /> : n}</div>
                    <div className="hidden sm:block" style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: on ? 'var(--ink2)' : 'var(--sub2)', fontWeight: active ? 600 : 400 }}>{label}</div>
                    {n < 4 && <div style={{ flex: 1, height: 1, minWidth: 8, background: done ? 'linear-gradient(90deg,var(--g1),var(--g2))' : 'var(--bd2)' }} />}
                  </div>
                );
              })}
            </div>
            {/* Body */}
            <div style={{ padding: 22 }}>
              {currentStep === 1 && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>ประเภทธุรกิจของคุณ <span style={{ color: '#ff5c9d' }}>*</span></div>
                  <div style={{ fontSize: 12, color: 'var(--sub2)', marginBottom: 13 }}>แตะเลือกประเภท แล้วกรอกชื่อธุรกิจ</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {BUSINESS_CATEGORIES.map((cat) => { const Icon = cat.icon; const sel = whatIDo.trim().startsWith(cat.label); return (
                      <button key={cat.label} type="button" onClick={() => setWhatIDo(cat.label + ' — ')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 99, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', border: sel ? '1px solid transparent' : '1px solid var(--bd2)', background: sel ? 'linear-gradient(135deg,var(--g1),var(--g2))' : 'transparent', color: sel ? '#fff' : 'var(--soft)' }}><Icon className="w-4 h-4" />{cat.label}</button>
                    ); })}
                  </div>
                  <textarea value={whatIDo} onChange={(e) => setWhatIDo(e.target.value)} placeholder="กรอกชื่อธุรกิจ/สิ่งที่คุณทำ — เช่น ร้านกาแฟ Bloom / คลินิกความงาม / ตัวแทนประกัน / สอนพิเศษออนไลน์ / ร้านเล็บ" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 12, padding: '12px 14px', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, minHeight: 52, resize: 'vertical', marginBottom: 20 }} />
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>กลุ่มเป้าหมายของคุณคือใคร? <span style={{ color: '#ff5c9d' }}>*</span></div>
                  <div style={{ fontSize: 12, color: 'var(--sub2)', marginBottom: 9 }}>คนที่คุณอยากให้เข้ามาดู/เป็นลูกค้า — คอนเทนต์จะถูกออกแบบมาดึงกลุ่มนี้</div>
                  <textarea value={myAudience} onChange={(e) => setMyAudience(e.target.value)} placeholder="ใครคือคนที่คุณอยากให้เข้ามาดู/เป็นลูกค้า — เช่น ผู้หญิงวัยทำงานรักสุขภาพ / พ่อแม่มือใหม่ / เจ้าของธุรกิจ / คนอยากเริ่มลงทุน / วัยรุ่นสายแฟชั่น" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 12, padding: '12px 14px', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, minHeight: 74, resize: 'vertical', lineHeight: 1.5, marginBottom: 20 }} />
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>จุดแข็งที่ไม่เหมือนใครของคุณคืออะไร? <span style={{ color: '#ff5c9d' }}>*</span></div>
                  <div style={{ fontSize: 12, color: 'var(--sub2)', marginBottom: 9 }}>อะไรที่ทำให้คุณต่างจากคู่แข่ง — ใช้เป็นมุมของคอนเทนต์</div>
                  <textarea value={myExpertise} onChange={(e) => setMyExpertise(e.target.value)} placeholder="อะไรที่ทำให้คุณต่างจากคู่แข่ง — เช่น ประสบการณ์ 10 ปี / ใช้วัตถุดิบพรีเมียม / ราคาถูกที่สุด / บริการถึงบ้าน / มีเทคนิคเฉพาะตัว" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 12, padding: '12px 14px', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, minHeight: 90, resize: 'vertical', lineHeight: 1.5 }} />
                </>
              )}
              {currentStep === 2 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 3 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>เป้าหมายของคุณคือ <span style={{ color: '#ff5c9d' }}>*</span></div>
                    <span style={{ fontSize: 12, color: 'var(--sub2)' }}>เลือกแล้ว {myGoals.length}/2</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--sub2)', marginBottom: 13 }}>แตะเลือก 1-2 อัน — ระบบจะแปลงเป็นแนวคอนเทนต์ให้เอง</div>
                  <div className="cd-goals" style={{ marginBottom: 20 }}>
                    {CONTENT_GOALS.map((g) => { const Icon = g.icon; const sel = myGoals.includes(g.key); return (
                      <div key={g.key} onClick={() => setMyGoals((prev) => prev.includes(g.key) ? prev.filter((k) => k !== g.key) : (prev.length < 2 ? [...prev, g.key] : prev))} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 15px', borderRadius: 14, cursor: 'pointer', fontSize: 14, color: 'var(--ink2)', border: sel ? '1px solid var(--g1)' : '1px solid var(--bd2)', background: sel ? 'rgba(124,92,255,.14)' : 'var(--field)' }}>
                        <Icon className="w-5 h-5" style={{ flex: 'none', color: sel ? 'var(--cv)' : 'var(--sub2)' }} />
                        <span style={{ flex: 1 }}>{g.label}</span>
                        {sel && <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--cv)', flex: 'none' }} />}
                      </div>
                    ); })}
                  </div>
                  <div style={{ border: '1px solid rgba(124,92,255,.3)', background: 'linear-gradient(135deg,rgba(124,92,255,.1),rgba(255,92,157,.06))', borderRadius: 14, padding: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 7 }}><Rocket className="w-4 h-4" style={{ color: 'var(--cv)' }} /> ความท้าทาย 60 วัน <span style={{ fontSize: 11, color: 'var(--sub2)', fontWeight: 400 }}>(ไม่บังคับ)</span></div>
                    <div style={{ fontSize: 12, color: 'var(--sub)', marginBottom: 10 }}>เลือกประเภทความท้าทาย แล้วกรอกเป้าหมายของคุณ — ระบบจะปั้นคอนเทนต์ให้เหมาะและดึงคนติดตามที่ตรงกลุ่ม</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 11 }}>
                      {CHALLENGE_PRESETS.map((c) => {
                        const Icon = c.icon;
                        const prefix = c.tmpl.split('[')[0];
                        // เลือกจาก state จริงก่อน (ลบข้อความแล้วยังเลือกอยู่) — ข้อมูลเก่าที่ไม่มี state ค่อยเดาจากข้อความ
                        const sel = challengeType ? challengeType === c.label : myChallenge.startsWith(prefix);
                        return (
                          <button key={c.label} type="button" onClick={() => {
                            if (sel) { setChallengeType(''); return; } // แตะซ้ำ = ยกเลิกการเลือก (ข้อความในช่องคงเดิม)
                            setChallengeType(c.label);
                            // เติมเทมเพลตเฉพาะตอนช่องว่างหรือยังเป็นเทมเพลตเดิม — ไม่ทับข้อความที่ผู้ใช้แก้เอง
                            const isTemplate = !myChallenge.trim() || CHALLENGE_PRESETS.some((p) => myChallenge === p.tmpl);
                            if (isTemplate) setMyChallenge(c.tmpl);
                          }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 99, cursor: 'pointer', fontFamily: 'inherit', border: sel ? '1px solid transparent' : '1px solid var(--bd2)', color: sel ? '#fff' : 'var(--soft)', background: sel ? 'linear-gradient(135deg,var(--g1),var(--g2))' : 'var(--field)' }}>
                            <Icon className="w-3.5 h-3.5" style={{ flex: 'none' }} /> {c.label}
                          </button>
                        );
                      })}
                    </div>
                    <textarea value={myChallenge} onChange={(e) => setMyChallenge(e.target.value)} placeholder="กรอกเป้าหมายให้ชัด — เช่น เพิ่มผู้ติดตามให้ได้ 10,000 คน ใน 60 วัน / ทำยอดขายให้ถึง 500,000 บาท / ขายแฟรนไชส์ให้ได้ 5 สาขา" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 11, padding: '11px 13px', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, minHeight: 56, resize: 'vertical' }} />
                    <div style={{ fontSize: 11, color: 'var(--sub2)', marginTop: 7 }}>💡 แตะเลือกด้านบนเพื่อเติมเทมเพลต แล้วแก้ตัวเลข/รายละเอียดในช่องได้เลย</div>

                    {/* โหมดลุยชาเลนจ์จริง */}
                    <button type="button" onClick={() => setChallengeMode((v) => !v)} style={{ width: '100%', marginTop: 12, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 11, padding: 13, borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', border: challengeMode ? '1px solid transparent' : '1px solid var(--bd2)', background: challengeMode ? 'linear-gradient(135deg,rgba(124,92,255,.22),rgba(255,92,157,.14))' : 'var(--field)' }}>
                      <div style={{ flex: 'none', width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: challengeMode ? 'linear-gradient(135deg,var(--g1),var(--g2))' : 'var(--sbg)' }}>
                        <Flame className="w-5 h-5" style={{ color: challengeMode ? '#fff' : 'var(--cv)' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>โหมดลุยชาเลนจ์จริง {challengeMode && <span style={{ color: '#34d399', fontSize: 12 }}>· เปิดอยู่</span>}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--sub)', lineHeight: 1.45, marginTop: 2 }}>สคริปต์จะกลายเป็น "การลุยทำภารกิจจริง" ให้คนลุ้นตาม — คลิปแรกประกาศชาเลนจ์แบบตื่นเต้น ("มาดูกันว่าจะทำได้จริงไหม!") ไม่ใช่หัวข้อแข็ง ๆ</div>
                      </div>
                      <div style={{ flex: 'none', width: 42, height: 24, borderRadius: 99, padding: 3, background: challengeMode ? 'linear-gradient(135deg,var(--g1),var(--g2))' : 'var(--track2)', transition: 'background .2s' }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', transform: challengeMode ? 'translateX(18px)' : 'translateX(0)', transition: 'transform .2s' }}></div>
                      </div>
                    </button>
                  </div>
                </>
              )}
              {currentStep === 3 && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>สินค้าที่ฉันมี <span style={{ fontSize: 12, color: 'var(--sub2)', fontWeight: 400 }}>(ไม่บังคับ — ถ้าจะแทรก Soft Sell)</span></div>
                  <div style={{ fontSize: 12, color: 'var(--sub2)', marginBottom: 11 }}>ใส่แพ็คเกจ/ราคา/โปรฯ ไว้ ระบบจะแทรกให้เนียนๆ เฉพาะสาย Soft-sell</div>
                  <textarea value={myProduct} onChange={(e) => setMyProduct(e.target.value)} placeholder="สินค้า/บริการที่ขาย พร้อมราคา/แพ็คเกจ — เช่น&#10;คอร์สเรียนออนไลน์ 1,990 บาท&#10;ครีมบำรุงผิว 590 บาท&#10;แพ็คเกจบริการรายเดือน 2,900 บาท" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 12, padding: 14, color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, minHeight: 200, resize: 'vertical', lineHeight: 1.7 }} />
                </>
              )}
              {currentStep === 4 && (
                <>
                  <div style={{ border: '1px solid var(--bd2)', borderRadius: 14, padding: 18, background: 'var(--panel2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}><span style={{ display: 'inline-block', width: 9, height: 9, background: 'linear-gradient(135deg,var(--g1),var(--g2))', transform: 'rotate(45deg)' }}></span><span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>ตัวตนของคุณ</span></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                      {[['ธุรกิจ', whatIDo], ['กลุ่มเป้าหมาย', myAudience], ['จุดแข็ง', myExpertise], ['เป้าหมาย', goalLabels.join(' · ')], ['ท้าทาย 60 วัน', myChallenge], ['สินค้า', myProduct]].map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', gap: 10 }}><span style={{ fontSize: 12.5, color: 'var(--sub2)', width: 96, flex: 'none' }}>{k}</span><span style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{v || '—'}</span></div>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'flex-start', border: '1px solid rgba(124,92,255,.3)', background: 'rgba(124,92,255,.08)', borderRadius: 12, padding: '13px 15px' }}><span style={{ display: 'inline-block', width: 9, height: 9, marginTop: 4, background: 'linear-gradient(135deg,var(--g1),var(--g2))', transform: 'rotate(45deg)', flex: 'none' }}></span><span style={{ fontSize: 12.5, color: 'var(--soft)', lineHeight: 1.5 }}>กด "บันทึกตัวตน" แล้วระบบจะเจนหัวข้อทั้ง 3 สายให้อัตโนมัติ — เลือกหัวข้อกดสร้างสคริปต์ได้เลย</span></div>
                </>
              )}
            </div>
            {/* Footer */}
            <div style={{ display: 'flex', gap: 10, padding: '18px 22px', borderTop: '1px solid var(--bd)' }}>
              {currentStep > 1 && (
                <button onClick={() => setCurrentStep(currentStep - 1)} style={{ flex: 'none', border: '1px solid var(--bd2)', background: 'transparent', color: 'var(--soft)', fontSize: 14, fontWeight: 500, padding: '13px 22px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit' }}>ย้อนกลับ</button>
              )}
              {currentStep < 4 ? (
                <button onClick={() => {
                  if (currentStep === 1 && (!whatIDo.trim() || !myAudience.trim() || !myExpertise.trim())) { alert('กรุณากรอกข้อมูลให้ครบทั้ง 3 ช่อง'); return; }
                  if (currentStep === 2) { if (myGoals.length === 0) { alert('กรุณาเลือกเป้าหมายอย่างน้อย 1 อัน'); return; } setMyProof(deriveProofFromGoals(myGoals)); }
                  setCurrentStep(currentStep + 1);
                }} style={{ flex: 1, border: 'none', background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontSize: 14, fontWeight: 600, padding: 13, borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {['ถัดไป: เป้าหมาย', 'ถัดไป: สินค้า/บริการ', 'ถัดไป: บันทึก'][currentStep - 1]} <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={handleSaveIdentity} disabled={isSaving} style={{ flex: 1, border: 'none', background: 'linear-gradient(135deg,var(--g1),var(--g2))', color: '#fff', fontSize: 14, fontWeight: 600, padding: 13, borderRadius: 12, cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: isSaving ? 0.6 : 1 }}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} บันทึกตัวตน
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentGenerator;
